import React, { useState, useEffect, useRef } from 'react';
import { Leaf, User, Gift, Activity, Star, Menu, MapPin, Trophy, Settings, LogOut, Mail, Lock, Users, BarChart3, Plus, Edit2, Trash2, X, Trash, TreePine, Palette, Footprints, Bike, Tent, CheckCircle, XCircle, Clock, UserCheck, Camera, Globe, Phone, Target, ClipboardList, Download, AlertTriangle, Search, AlertCircle, Heart, Share2, Smartphone, Award, ChevronLeft, Lock as LockIcon, Crown } from 'lucide-react';

// IMPORTAR FIREBASE
import { auth, registerUser, loginUser, logoutUser, resendVerificationEmail, resetPassword, getUserData, getNews, getOffers, getActivities, redeemOffer, completeActivity, getAllUsers, updateUser, deleteUser, addNews, updateNews, deleteNews, addOffer, updateOffer, deleteOffer, addActivity, updateActivity, deleteActivity, getAdminStats, getActivityCategories, createActivityRegistration, getAllRegistrations, getRegistrationsByActivity, getUserRegistrations, approveRegistration, rejectRegistration, cancelRegistration, getPendingRegistrationsCount, getUserActivityHistory, uploadActivityPhoto, submitActivityPhoto, approveActivityPhoto, rejectActivityPhoto, activatePremium, deactivatePremium, getRewardCatalog, addReward, updateReward, deleteReward, getChallenges, addChallenge, updateChallenge, deleteChallenge, enrollInChallenge, getUserChallengeRegistrations, getAllChallengeRegistrations, submitChallengeTaskPhoto, approveChallengeTaskPhoto, rejectChallengeTaskPhoto, claimChallengeRewards, getUserRewards, markPremiumCancellation, getLeaderboard, updateRegistrationChecklist, getRedemptions, updateRedemption, syncLeaderboardEntry, rebuildLeaderboard, deleteMyAccount, getAchievements, addAchievement, updateAchievement, deleteAchievement, seedAchievements, startActivity, endActivity, setActivityEndDate, unarchiveActivity, getAppSettings, updateAppSettings } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';

import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { reseedActivities } from './seedActivities';
import { seedRandomUsers, removeSeededUsers } from './seedUsers';
import { wipeAllData } from './resetData';

// ============================================
// GEOLOCALIZAÇÃO — GPS do browser (grátis, sem API). Falha graciosamente (resolve null).
// ============================================
const getCurrentLocation = (timeoutMs = 8000) => new Promise((resolve) => {
  if (typeof navigator === 'undefined' || !navigator.geolocation) { resolve(null); return; }
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      capturedAt: new Date().toISOString()
    }),
    () => resolve(null),
    { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60000 }
  );
});

// Código único e legível de cada atividade. Usa o código guardado se existir;
// caso contrário deriva-o do id do documento (único e estável, sem migração).
const activityCode = (a) => {
  if (!a) return '—';
  if (a.code) return a.code;
  if (!a.id) return '—';
  return `GS-${String(a.id).slice(-5).toUpperCase()}`;
};

// ============================================
// META PESSOAL (mensal) — escolhida pelo utilizador
// ============================================
const GOAL_TYPES = [
  { id: 'monthly_count', label: 'Fazer X atividades este mês', needsTarget: true },
  { id: 'weekly_one', label: 'Pelo menos 1 atividade por semana', needsTarget: false },
  { id: 'all_categories', label: 'Pelo menos 1 atividade de cada tipo', needsTarget: false },
];

const monthStart = () => {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), 1);
};

// Índice da semana dentro do mês (0, 1, 2…), com as semanas a começar à segunda
const weekIndexInMonth = (date) => {
  const d = new Date(date);
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7; // 0 = segunda
  return Math.floor((d.getDate() + offset - 1) / 7);
};

const weeksInCurrentMonth = () => {
  const n = new Date();
  const first = new Date(n.getFullYear(), n.getMonth(), 1);
  const lastDay = new Date(n.getFullYear(), n.getMonth() + 1, 0).getDate();
  const offset = (first.getDay() + 6) % 7;
  return Math.ceil((lastDay + offset) / 7);
};

// Calcula o progresso da meta a partir do histórico de atividades concluídas
const computeGoal = (user, history, activities, categories) => {
  const start = monthStart();
  const thisMonth = (history || []).filter((h) => h.completedAt && new Date(h.completedAt) >= start);
  const type = user?.goalType || 'monthly_count';
  const target = Math.max(1, parseInt(user?.goalTarget) || 4);

  if (type === 'weekly_one') {
    const weeks = weeksInCurrentMonth();
    const doneWeeks = new Set(thisMonth.map((h) => weekIndexInMonth(h.completedAt)));
    return {
      type,
      done: doneWeeks.size,
      total: weeks,
      title: 'Pelo menos 1 atividade por semana',
      detail: `${doneWeeks.size} de ${weeks} semanas cumpridas`,
    };
  }

  if (type === 'all_categories') {
    const cats = new Set(
      thisMonth
        .map((h) => (activities || []).find((a) => a.id === h.activityId)?.categoryId)
        .filter(Boolean)
    );
    const total = (categories || []).length || 6;
    return {
      type,
      done: Math.min(cats.size, total),
      total,
      title: 'Uma atividade de cada tipo',
      detail: `${cats.size} de ${total} categorias`,
      coveredCategories: cats,
    };
  }

  return {
    type: 'monthly_count',
    done: thisMonth.length,
    total: target,
    title: `${target} atividades este mês`,
    detail: `${thisMonth.length} de ${target} concluídas`,
  };
};

const goalMessage = (pct) => {
  if (pct >= 100) return 'Meta alcançada. Excelente!';
  if (pct >= 80) return 'Estamos quase a terminar!';
  if (pct >= 50) return 'Mais um pouco de esforço!';
  if (pct > 0) return 'Está num bom caminho!';
  return 'Vamos começar este mês!';
};

// ============================================
// VERSÃO E HISTÓRICO DE ALTERAÇÕES
// Ao lançar uma nova versão: atualizar APP_VERSION, acrescentar a entrada no topo
// do CHANGELOG, e atualizar package.json + CACHE_NAME no service-worker.js.
// ============================================
const APP_VERSION = '1.10.1';

const CHANGELOG = [
  { v: '1.10.1', items: [
    'Progresso da meta pessoal passou a aparecer na página de Novidades, por baixo das estatísticas.',
  ]},
  { v: '1.10.0', items: [
    'Eco-pontos de prémio ao cumprir a meta mensal, com o valor definido pelo administrador.',
    'Prémio atribuído automaticamente quando a atividade é dada como concluída, uma vez por mês.',
  ]},
  { v: '1.9.0', items: [
    'Meta pessoal mensal, escolhida pelo próprio utilizador.',
    'Três tipos de meta: X atividades no mês, uma atividade por semana, ou uma de cada tipo.',
    'Barra de progresso na página de atividades e página dedicada em Mais → Meta pessoal.',
  ]},
  { v: '1.8.0', items: [
    'Ferramentas de desenvolvedor desbloqueáveis com 5 toques na versão (1 minuto).',
    'Página de histórico de versões, acessível com 10 toques na versão.',
  ]},
  { v: '1.7.1', items: [
    'Corrigido: a app instalada não recebia atualizações — o HTML da raiz continuava em cache.',
    'Cabeçalhos de cache corrigidos para todas as páginas HTML.',
  ]},
  { v: '1.7.0', items: [
    'Segurança: regras do Firestore reescritas — deixou de ser possível a um utilizador tornar-se administrador ou aumentar os próprios pontos.',
    'Ranking protegido contra manipulação: os valores têm de coincidir com o perfil real.',
    'Política de Privacidade atualizada com a recolha de localização (GPS), fundamento legal e direito de reclamação à CNPD.',
    'Nova página de Definições com dados da conta e informação de privacidade.',
    'Apagar conta (RGPD): elimina perfil, pontos, histórico, inscrições e recompensas.',
    'Botão de Suporte passou a abrir o email; deixou de estar inativo.',
    'Nova ferramenta de administração para limpar todos os dados de teste.',
  ]},
  { v: '1.6.0', items: [
    'A aba "Inscrições" passou a "A meu cargo", com o que é da responsabilidade de cada admin.',
    'Dois separadores: inscrições por aprovar e atividades, filtrados por responsável.',
  ]},
  { v: '1.5.1', items: [
    'Pesquisa de atividades por nome, código ou zona (utilizador e admin).',
    'Cada atividade passou a ter um código único (GS-XXXXX).',
    'Rodapé passou a aparecer também no painel de administração.',
  ]},
  { v: '1.5.0', items: [
    'Ciclo de vida das atividades: começar, decorrer, terminar e arquivar.',
    'Ao começar, as inscrições fecham automaticamente.',
    'Campo "admin responsável" na criação de atividades.',
    'Detalhe da atividade a decorrer: participantes, quem concluiu, datas de início e fim.',
    'Atividades arquivadas saem da lista e ficam no histórico.',
  ]},
  { v: '1.4.4', items: [
    'Imagem das ofertas passou a ser importada por ficheiro em vez de URL.',
  ]},
  { v: '1.4.3', items: [
    'Corrigidos os cabeçalhos de cache que impediam a app de se atualizar.',
  ]},
  { v: '1.4.2', items: [
    'Gravações muito mais rápidas: o formulário fecha logo e os dados atualizam em segundo plano.',
    'Carregamentos passaram a ser feitos em paralelo.',
  ]},
  { v: '1.4.1', items: [
    'Corrigido: erro ao guardar no telemóvel — o service worker corrompia os pedidos de escrita.',
    'Mensagens de erro passaram a indicar a causa real.',
  ]},
  { v: '1.4.0', items: [
    'Ranking ordenável por total de atividades ou por cada categoria.',
    'Contagem de atividades por categoria.',
    'Ranking deixou de usar pontos como critério.',
  ]},
  { v: '1.3.0', items: [
    'Gestão completa de conquistas pelo administrador (criar, editar, ocultar, apagar).',
    'Critérios configuráveis: atividades, trilhos, Eco-pontos ou Premium.',
  ]},
  { v: '1.2.0', items: [
    'GPS na submissão de fotografias, com localização visível ao administrador.',
    'Ranking de utilizadores e página de conquistas.',
    'Atividades com zona, coordenadas, tarefas e mapas de percurso.',
    'Trilhos certificados e atribuição a parceiros.',
    'Novo fluxo de resgate de ofertas com manual, termos e painel de resgates.',
    'Pontos renomeados para Eco-pontos.',
  ]},
  { v: '1.1.38', items: [
    'Transição 3D entre páginas.',
    'Gestão de administradores, com proteção contra ficar sem nenhum.',
  ]},
  { v: '1.1.0', items: [
    'Migração dos pagamentos de PayPal para Stripe.',
    'Cancelamento da subscrição feito pelo próprio utilizador, mantendo o acesso até ao fim do período pago.',
  ]},
  { v: '1.0.0', items: [
    'Versão inicial: atividades, inscrições, pontos, ofertas, desafios Premium e painel de administração.',
  ]},
];

// ============================================
// FERRAMENTAS DE DESENVOLVEDOR
// Normalmente bloqueadas. Desbloqueiam-se por 1 minuto ao tocar 5x na versão
// (no rodapé) e voltam a bloquear sozinhas.
// ============================================
const DEV_UNLOCK_MS = 60 * 1000;
let devUnlockUntil = 0;
const devListeners = new Set();

const unlockDevTools = () => {
  devUnlockUntil = Date.now() + DEV_UNLOCK_MS;
  devListeners.forEach((fn) => fn());
};

// Hook: devolve se as ferramentas estão desbloqueadas e os segundos restantes
const useDevUnlock = () => {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    devListeners.add(fn);
    const timer = setInterval(fn, 1000); // acompanha a contagem e o auto-bloqueio
    return () => { devListeners.delete(fn); clearInterval(timer); };
  }, []);
  const remaining = Math.max(0, Math.ceil((devUnlockUntil - Date.now()) / 1000));
  return { unlocked: remaining > 0, remaining };
};

// ============================================
// CONQUISTAS — definidas por dados (geridas pelo admin no Firestore).
// Critério = métrica + valor mínimo. Avaliadas no cliente a partir das stats.
// ============================================
const ACHIEVEMENT_METRICS = [
  { id: 'activities', label: 'Atividades concluídas' },
  { id: 'trails',     label: 'Trilhos concluídos' },
  { id: 'points',     label: 'Eco-pontos' },
  { id: 'premium',    label: 'Ser membro Premium' },
];

const metricLabel = (m) => (ACHIEVEMENT_METRICS.find((x) => x.id === m)?.label || m);

// Verdadeiro se o utilizador cumpre o critério da conquista
const isAchievementUnlocked = (a, u) => {
  if (!a || !u) return false;
  if (a.metric === 'premium') return !!u.isPremium;
  const value = Number(u?.[a.metric] || 0);
  return value >= Number(a.threshold || 0);
};

// Usadas quando ainda não há conquistas na base de dados (o admin pode importá-las)
const DEFAULT_ACHIEVEMENTS = [
  { emoji: '🌱', name: 'Primeiro Passo', description: 'Completa a tua 1ª atividade', metric: 'activities', threshold: 1,    order: 1 },
  { emoji: '🧭', name: 'Explorador',     description: 'Completa 5 atividades',        metric: 'activities', threshold: 5,    order: 2 },
  { emoji: '⛰️', name: 'Aventureiro',    description: 'Completa 15 atividades',       metric: 'activities', threshold: 15,   order: 3 },
  { emoji: '🥾', name: 'Caminhante',     description: 'Completa 3 trilhos',           metric: 'trails',     threshold: 3,    order: 4 },
  { emoji: '🚵', name: 'Trilheiro',      description: 'Completa 10 trilhos',          metric: 'trails',     threshold: 10,   order: 5 },
  { emoji: '⭐', name: 'Colecionador',    description: 'Alcança 100 Eco-pontos',       metric: 'points',     threshold: 100,  order: 6 },
  { emoji: '🌟', name: 'Eco-Herói',      description: 'Alcança 500 Eco-pontos',       metric: 'points',     threshold: 500,  order: 7 },
  { emoji: '💚', name: 'Lenda Verde',    description: 'Alcança 1000 Eco-pontos',      metric: 'points',     threshold: 1000, order: 8 },
  { emoji: '👑', name: 'Membro Premium', description: 'Torna-te membro Premium',      metric: 'premium',    threshold: 0,    order: 9 },
];

// ============================================
// ROTAS / MAPAS
// ============================================
// Converte texto "lat, lng" (uma linha por ponto) num array de pontos, e vice-versa.
const parseRoute = (text) => (text || '')
  .split('\n')
  .map((line) => {
    const parts = line.split(',');
    const lat = parseFloat(parts[0]);
    const lng = parseFloat(parts[1]);
    return (!isNaN(lat) && !isNaN(lng)) ? { lat, lng } : null;
  })
  .filter(Boolean);

const routeToText = (route) => (route || []).map((p) => `${p.lat}, ${p.lng}`).join('\n');

// Mapa Leaflet que desenha a rota (polyline) entre os pontos. Grátis (OpenStreetMap).
const TrailMap = ({ route = [], height = 190 }) => {
  const containerRef = useRef(null);
  useEffect(() => {
    const L = window.L;
    if (!L || !containerRef.current) return;
    const pts = (route || []).filter((p) => p && typeof p.lat === 'number' && typeof p.lng === 'number');
    if (pts.length === 0) return;
    const map = L.map(containerRef.current, { scrollWheelZoom: false });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    }).addTo(map);
    const latlngs = pts.map((p) => [p.lat, p.lng]);
    if (latlngs.length >= 2) {
      const line = L.polyline(latlngs, { color: '#714c62', weight: 4 }).addTo(map);
      L.marker(latlngs[0]).addTo(map).bindPopup('Início');
      L.marker(latlngs[latlngs.length - 1]).addTo(map).bindPopup('Fim');
      map.fitBounds(line.getBounds(), { padding: [24, 24] });
    } else {
      L.marker(latlngs[0]).addTo(map);
      map.setView(latlngs[0], 15);
    }
    const t = setTimeout(() => map.invalidateSize(), 120);
    return () => { clearTimeout(t); map.remove(); };
  }, [route]);

  const hasRoute = (route || []).some((p) => p && typeof p.lat === 'number' && typeof p.lng === 'number');
  if (!hasRoute) return null;
  return <div ref={containerRef} style={{ height, borderRadius: 16, overflow: 'hidden', zIndex: 0 }} className="w-full my-2" />;
};

// 6 CATEGORIAS BASE
const BASE_CATEGORIES = [
  { id: 'apanhar-lixo', name: 'Apanhar Lixo', icon: '🗑️', color: 'bg-sky-500', iconComponent: Trash, image: 'https://images.unsplash.com/photo-1618477461853-cf6ed80faba5?w=400&h=300&fit=crop' },
  { id: 'plantar-arvores', name: 'Plantar Árvores', icon: '🌳', color: 'bg-green-500', iconComponent: TreePine, image: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=400&h=300&fit=crop' },
  { id: 'arte-sustentavel', name: 'Arte Sustentável', icon: '🎨', color: 'bg-purple-500', iconComponent: Palette, image: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=400&h=300&fit=crop' },
  { id: 'trilhos-pe', name: 'Trilhos a Pé', icon: '🚶', color: 'bg-orange-500', iconComponent: Footprints, image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=400&h=300&fit=crop' },
  { id: 'trilhos-bicicleta', name: 'Trilhos de Bicicleta', icon: '🚴', color: 'bg-yellow-500', iconComponent: Bike, image: 'https://images.unsplash.com/photo-1544191696-102dbdaeeaa0?w=400&h=300&fit=crop' },
  { id: 'acampar', name: 'Acampar', icon: '⛺', color: 'bg-red-500', iconComponent: Tent, image: 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=400&h=300&fit=crop' }
];

// ============================================
// FOOTER COMPONENT (MOVIDO PARA FORA)
// ============================================
const Footer = ({ onNavigate, isAdmin = false }) => {
  const [taps, setTaps] = useState(0);
  const tapTimer = useRef(null);

  // 5 toques → ferramentas de desenvolvedor por 1 minuto; 10 toques → changelog
  // Exclusivo de administradores: para os restantes utilizadores não faz nada.
  const handleVersionTap = () => {
    if (!isAdmin) return;
    const n = taps + 1;
    setTaps(n);
    clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => setTaps(0), 1500); // reinicia se parar de tocar

    if (n === 5) {
      unlockDevTools();
      alert('🔓 Ferramentas de desenvolvedor ativas durante 1 minuto.');
    }
    if (n >= 10) {
      setTaps(0);
      clearTimeout(tapTimer.current);
      if (onNavigate) onNavigate('changelog');
    }
  };

  useEffect(() => () => clearTimeout(tapTimer.current), []);

  return (
  <footer className="bg-[#111828] text-white mt-8 pb-28">
    <div className="max-w-lg mx-auto px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <img src="/icons/greenstep-logo.png" alt="GreenStep" className="w-10 h-10 rounded-full object-cover" />
        <div>
          <p className="font-extrabold">GreenStep</p>
          <p className="text-xs text-[#b3a5ad]">Seja um turista sustentável</p>
        </div>
      </div>
      <div className="space-y-2 text-sm text-[#b3a5ad] mb-8">
        <a href="mailto:greenstep@gmail.com" className="flex items-center gap-2 hover:text-white transition">
          <Mail className="w-4 h-4" /> greenstep@gmail.com
        </a>
        <p className="flex items-center gap-2"><Phone className="w-4 h-4" /> +351 ... ... ...</p>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-[#d9c7d2] mb-6">
        <button onClick={() => onNavigate && onNavigate('termos')} className="hover:text-white transition">Termos e Condições</button>
        <button onClick={() => onNavigate && onNavigate('privacidade')} className="hover:text-white transition">Privacidade</button>
        <a href="mailto:greenstep@gmail.com?subject=Pedido%20de%20suporte%20GreenStep" className="hover:text-white transition">Suporte</a>
      </div>
      <div className="border-t border-white/10 pt-5 text-xs text-[#8e7f88]">
        <p>© 2026 GreenStep. Todos os direitos reservados.</p>
        <p className="mt-1">
          Desenvolvido por <a href="https://vitorteixeira-vinein.netlify.app" target="_blank" rel="noopener noreferrer" className="text-[#d9c7d2] hover:text-white underline">Vinein</a>
          {' · '}
          <span onClick={handleVersionTap} className={isAdmin ? 'cursor-pointer select-none' : ''} title="Versão">
            Versão {APP_VERSION}
          </span>
          {isAdmin && taps >= 3 && taps < 10 && <span className="ml-1 opacity-60">({taps})</span>}
        </p>
      </div>
    </div>
  </footer>
  );
};

// ============================================
// DADOS DE PAÍSES E COMPONENTE PHONE INPUT
// ============================================

const TOP_COUNTRIES = [
  { code: 'PT', name: 'Portugal',    dial: '+351', digits: 9,  flag: '🇵🇹' },
  { code: 'FR', name: 'França',      dial: '+33',  digits: 9,  flag: '🇫🇷' },
  { code: 'ES', name: 'Espanha',     dial: '+34',  digits: 9,  flag: '🇪🇸' },
  { code: 'GB', name: 'Reino Unido', dial: '+44',  digits: 10, flag: '🇬🇧' },
  { code: 'DE', name: 'Alemanha',    dial: '+49',  digits: 10, flag: '🇩🇪' },
];

const ALL_COUNTRIES = [
  { code: 'ZA', name: 'África do Sul',      dial: '+27',  digits: 9  },
  { code: 'AF', name: 'Afeganistão',        dial: '+93',  digits: 9  },
  { code: 'AL', name: 'Albânia',            dial: '+355', digits: 8  },
  { code: 'DZ', name: 'Argélia',            dial: '+213', digits: 9  },
  { code: 'AD', name: 'Andorra',            dial: '+376', digits: 6  },
  { code: 'AO', name: 'Angola',             dial: '+244', digits: 9  },
  { code: 'SA', name: 'Arábia Saudita',     dial: '+966', digits: 9  },
  { code: 'AR', name: 'Argentina',          dial: '+54',  digits: 10 },
  { code: 'AM', name: 'Arménia',            dial: '+374', digits: 8  },
  { code: 'AU', name: 'Austrália',          dial: '+61',  digits: 9  },
  { code: 'AT', name: 'Áustria',            dial: '+43',  digits: 10 },
  { code: 'AZ', name: 'Azerbaijão',         dial: '+994', digits: 9  },
  { code: 'BD', name: 'Bangladesh',         dial: '+880', digits: 10 },
  { code: 'BE', name: 'Bélgica',            dial: '+32',  digits: 9  },
  { code: 'BO', name: 'Bolívia',            dial: '+591', digits: 8  },
  { code: 'BA', name: 'Bósnia-Herzegovina', dial: '+387', digits: 8  },
  { code: 'BR', name: 'Brasil',             dial: '+55',  digits: 11 },
  { code: 'BG', name: 'Bulgária',           dial: '+359', digits: 9  },
  { code: 'CA', name: 'Canadá',             dial: '+1',   digits: 10 },
  { code: 'KH', name: 'Camboja',            dial: '+855', digits: 9  },
  { code: 'CM', name: 'Camarões',           dial: '+237', digits: 9  },
  { code: 'KZ', name: 'Cazaquistão',        dial: '+7',   digits: 10 },
  { code: 'CL', name: 'Chile',              dial: '+56',  digits: 9  },
  { code: 'CN', name: 'China',              dial: '+86',  digits: 11 },
  { code: 'CO', name: 'Colômbia',           dial: '+57',  digits: 10 },
  { code: 'KR', name: 'Coreia do Sul',      dial: '+82',  digits: 10 },
  { code: 'CR', name: 'Costa Rica',         dial: '+506', digits: 8  },
  { code: 'HR', name: 'Croácia',            dial: '+385', digits: 9  },
  { code: 'CU', name: 'Cuba',               dial: '+53',  digits: 8  },
  { code: 'DK', name: 'Dinamarca',          dial: '+45',  digits: 8  },
  { code: 'EG', name: 'Egito',              dial: '+20',  digits: 10 },
  { code: 'AE', name: 'Emirados Árabes',    dial: '+971', digits: 9  },
  { code: 'EC', name: 'Equador',            dial: '+593', digits: 9  },
  { code: 'SK', name: 'Eslováquia',         dial: '+421', digits: 9  },
  { code: 'SI', name: 'Eslovénia',          dial: '+386', digits: 8  },
  { code: 'US', name: 'Estados Unidos',     dial: '+1',   digits: 10 },
  { code: 'ET', name: 'Etiópia',            dial: '+251', digits: 9  },
  { code: 'PH', name: 'Filipinas',          dial: '+63',  digits: 10 },
  { code: 'FI', name: 'Finlândia',          dial: '+358', digits: 9  },
  { code: 'GH', name: 'Gana',               dial: '+233', digits: 9  },
  { code: 'GR', name: 'Grécia',             dial: '+30',  digits: 10 },
  { code: 'HU', name: 'Hungria',            dial: '+36',  digits: 9  },
  { code: 'IN', name: 'Índia',              dial: '+91',  digits: 10 },
  { code: 'ID', name: 'Indonésia',          dial: '+62',  digits: 10 },
  { code: 'IR', name: 'Irão',               dial: '+98',  digits: 10 },
  { code: 'IQ', name: 'Iraque',             dial: '+964', digits: 10 },
  { code: 'IE', name: 'Irlanda',            dial: '+353', digits: 9  },
  { code: 'IL', name: 'Israel',             dial: '+972', digits: 9  },
  { code: 'IT', name: 'Itália',             dial: '+39',  digits: 10 },
  { code: 'JP', name: 'Japão',              dial: '+81',  digits: 10 },
  { code: 'JO', name: 'Jordânia',           dial: '+962', digits: 9  },
  { code: 'KE', name: 'Quénia',             dial: '+254', digits: 9  },
  { code: 'KW', name: 'Kuwait',             dial: '+965', digits: 8  },
  { code: 'LB', name: 'Líbano',             dial: '+961', digits: 8  },
  { code: 'LY', name: 'Líbia',              dial: '+218', digits: 9  },
  { code: 'LT', name: 'Lituânia',           dial: '+370', digits: 8  },
  { code: 'LU', name: 'Luxemburgo',         dial: '+352', digits: 9  },
  { code: 'MY', name: 'Malásia',            dial: '+60',  digits: 9  },
  { code: 'MA', name: 'Marrocos',           dial: '+212', digits: 9  },
  { code: 'MX', name: 'México',             dial: '+52',  digits: 10 },
  { code: 'MZ', name: 'Moçambique',         dial: '+258', digits: 9  },
  { code: 'NG', name: 'Nigéria',            dial: '+234', digits: 10 },
  { code: 'NO', name: 'Noruega',            dial: '+47',  digits: 8  },
  { code: 'NZ', name: 'Nova Zelândia',      dial: '+64',  digits: 9  },
  { code: 'PK', name: 'Paquistão',          dial: '+92',  digits: 10 },
  { code: 'PA', name: 'Panamá',             dial: '+507', digits: 8  },
  { code: 'PY', name: 'Paraguai',           dial: '+595', digits: 9  },
  { code: 'PE', name: 'Peru',               dial: '+51',  digits: 9  },
  { code: 'PL', name: 'Polónia',            dial: '+48',  digits: 9  },
  { code: 'PR', name: 'Porto Rico',         dial: '+1',   digits: 10 },
  { code: 'QA', name: 'Qatar',              dial: '+974', digits: 8  },
  { code: 'CZ', name: 'República Checa',    dial: '+420', digits: 9  },
  { code: 'RO', name: 'Roménia',            dial: '+40',  digits: 9  },
  { code: 'RU', name: 'Rússia',             dial: '+7',   digits: 10 },
  { code: 'SN', name: 'Senegal',            dial: '+221', digits: 9  },
  { code: 'RS', name: 'Sérvia',             dial: '+381', digits: 9  },
  { code: 'SG', name: 'Singapura',          dial: '+65',  digits: 8  },
  { code: 'SY', name: 'Síria',              dial: '+963', digits: 9  },
  { code: 'SE', name: 'Suécia',             dial: '+46',  digits: 9  },
  { code: 'CH', name: 'Suíça',              dial: '+41',  digits: 9  },
  { code: 'TH', name: 'Tailândia',          dial: '+66',  digits: 9  },
  { code: 'TW', name: 'Taiwan',             dial: '+886', digits: 9  },
  { code: 'TZ', name: 'Tanzânia',           dial: '+255', digits: 9  },
  { code: 'TN', name: 'Tunísia',            dial: '+216', digits: 8  },
  { code: 'TR', name: 'Turquia',            dial: '+90',  digits: 10 },
  { code: 'UA', name: 'Ucrânia',            dial: '+380', digits: 9  },
  { code: 'UG', name: 'Uganda',             dial: '+256', digits: 9  },
  { code: 'UY', name: 'Uruguai',            dial: '+598', digits: 8  },
  { code: 'VE', name: 'Venezuela',          dial: '+58',  digits: 7  },
  { code: 'VN', name: 'Vietname',           dial: '+84',  digits: 9  },
  { code: 'YE', name: 'Iémen',              dial: '+967', digits: 9  },
  { code: 'ZM', name: 'Zâmbia',             dial: '+260', digits: 9  },
  { code: 'ZW', name: 'Zimbábue',           dial: '+263', digits: 9  },
].sort((a, b) => a.name.localeCompare(b.name, 'pt'));

const PhoneInput = ({ value, onChange, id }) => {
  const [selectedCountry, setSelectedCountry] = React.useState(TOP_COUNTRIES[0]);
  const [number, setNumber] = React.useState('');
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    onChange(number ? `${selectedCountry.dial} ${number}` : '');
  }, [selectedCountry, number]);

  const digits = number.replace(/\D/g, '').length;
  const required = selectedCountry.digits;
  let phoneError = null;
  if (number.length > 0) {
    if (digits < required) phoneError = `Faltam ${required - digits} algarismo${required - digits !== 1 ? 's' : ''}`;
    else if (digits > required) phoneError = `${digits - required} algarismo${digits - required !== 1 ? 's' : ''} a mais`;
  }

  const handleNumberChange = (e) => {
    const val = e.target.value.replace(/[^\d\s\-]/g, '');
    setNumber(val);
  };

  return (
    <div className="relative">
      <div className={`flex rounded-2xl overflow-hidden border ${phoneError ? 'border-red-400' : 'border-gray-300'} focus-within:ring-2 focus-within:ring-[#714c62]`}>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1 px-3 py-3 bg-gray-50 border-r border-gray-300 hover:bg-gray-100 transition min-w-[90px] flex-shrink-0"
        >
          <span className="text-lg leading-none">{selectedCountry.flag || '🌍'}</span>
          <span className="text-sm font-medium text-gray-700">{selectedCountry.dial}</span>
          <span className="text-gray-400 text-xs ml-auto">▾</span>
        </button>
        <input
          type="tel"
          id={id}
          value={number}
          onChange={handleNumberChange}
          className="flex-1 px-3 py-3 outline-none text-gray-800 bg-white"
          placeholder={`${required} algarismos`}
          autoComplete="tel-national"
          maxLength={required + 4}
        />
      </div>
      {phoneError && (
        <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {phoneError}</p>
      )}
      {number.length > 0 && !phoneError && (
        <p className="text-xs text-[#714c62] mt-1 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Número válido</p>
      )}
      {open && (
        <div className="absolute top-full left-0 right-0 z-50 bg-white rounded-2xl border border-[#e8e2e6] shadow-xl max-h-72 overflow-y-auto">
          <div className="px-3 py-2 bg-sky-50 border-b border-sky-200 sticky top-0">
            <p className="text-xs font-bold text-[#714c62] uppercase tracking-wide flex items-center gap-1"><Star className="w-3 h-3" /> Mais usados</p>
          </div>
          {TOP_COUNTRIES.map(country => (
            <button
              key={country.code}
              type="button"
              onClick={() => { setSelectedCountry(country); setOpen(false); setNumber(''); }}
              className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-sky-50 transition text-left ${selectedCountry.code === country.code ? 'bg-sky-100' : ''}`}
            >
              <span className="text-xl w-7 text-center">{country.flag}</span>
              <span className="flex-1 text-sm text-gray-800">{country.name}</span>
              <span className="text-sm font-semibold text-gray-500">{country.dial}</span>
            </button>
          ))}
          <div className="px-3 py-2 bg-gray-50 border-t border-b border-gray-200 sticky top-10">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Todos os países</p>
          </div>
          {ALL_COUNTRIES.map(country => (
            <button
              key={country.code}
              type="button"
              onClick={() => { setSelectedCountry(country); setOpen(false); setNumber(''); }}
              className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition text-left ${selectedCountry.code === country.code ? 'bg-gray-100' : ''}`}
            >
              <span className="w-7 text-center text-sm text-gray-400">{country.code}</span>
              <span className="flex-1 text-sm text-gray-800">{country.name}</span>
              <span className="text-sm font-semibold text-gray-500">{country.dial}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


const GreenStepApp = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('novidades');
  const [userData, setUserData] = useState(null);
  const [news, setNews] = useState([]);
  const [offers, setOffers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [authMode, setAuthMode] = useState('login');
  const [loading, setLoading] = useState(false);

  // Registration states
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [userRegistrations, setUserRegistrations] = useState([]);

  // Photo upload states
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [selectedRegistrationForPhoto, setSelectedRegistrationForPhoto] = useState(null);

  // Admin states
  const [isAdmin, setIsAdmin] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [allRegistrations, setAllRegistrations] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);

  // Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Premium states
  const [challenges, setChallenges] = useState([]);
  const [rewardCatalog, setRewardCatalog] = useState([]);
  const [userChallengeRegistrations, setUserChallengeRegistrations] = useState([]);
  const [userRewardsData, setUserRewardsData] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [showChallengeDetail, setShowChallengeDetail] = useState(false);
  const [showRewardsPage, setShowRewardsPage] = useState(false);
  const [showRankingPage, setShowRankingPage] = useState(false);
  const [showAchievementsPage, setShowAchievementsPage] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState(null);
  const [rankingSort, setRankingSort] = useState('all'); // 'all' ou id de categoria
  const [activitySearch, setActivitySearch] = useState('');
  const [showSettingsPage, setShowSettingsPage] = useState(false);
  const [showGoalPage, setShowGoalPage] = useState(false);
  const [myActivityHistory, setMyActivityHistory] = useState([]);
  const [appSettings, setAppSettings] = useState({});
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [detailActivity, setDetailActivity] = useState(null);
  const [redeemOfferData, setRedeemOfferData] = useState(null);
  const [redeemTermsAccepted, setRedeemTermsAccepted] = useState(false);
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redemptions, setRedemptions] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [showProfileEditPage, setShowProfileEditPage] = useState(false);
  const [profileEditName, setProfileEditName] = useState('');
  const [profileEditPhotoFile, setProfileEditPhotoFile] = useState(null);
  const [profileEditPhotoPreview, setProfileEditPhotoPreview] = useState(null);
  const [profileEditSaving, setProfileEditSaving] = useState(false);
  const [allChallengeRegistrations, setAllChallengeRegistrations] = useState([]);

  // PWA Install state
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  // PWA Update listener
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.update();
        });
      });

      navigator.serviceWorker.register('/service-worker.js').then((registration) => {
        setInterval(() => {
          registration.update();
        }, 10000);

        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
          setShowUpdatePrompt(true);
        }

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              setWaitingWorker(newWorker);
              setShowUpdatePrompt(true);
            }
          });
        });
      }).catch((error) => {
        console.error('❌ Erro ao registrar Service Worker:', error);
      });

      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
          refreshing = true;
          window.location.reload();
        }
      });
    }
  }, []);

  // PWA Install listener
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = window.navigator.standalone === true;

    if (isStandalone || isIOSStandalone) {
      setShowInstallButton(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Listener de autenticação
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && user.emailVerified) {
        setCurrentUser(user);
      } else if (user && !user.emailVerified) {
        setCurrentUser(null);
        setUserData(null);
        setIsAdmin(false);
      } else {
        setCurrentUser(null);
        setUserData(null);
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Retorno do checkout Stripe: o Payment Link redireciona para ?stripe_session={CHECKOUT_SESSION_ID}
  const [pendingStripeSession, setPendingStripeSession] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('stripe_session');
    if (sessionId) {
      setPendingStripeSession(sessionId);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!currentUser || !pendingStripeSession) return;
    const activate = async () => {
      try {
        await activatePremium(currentUser.uid, pendingStripeSession);
        alert('Premium ativado com sucesso! Bem-vindo ao GreenStep Premium!');
        await loadData();
      } catch (error) {
        console.error('Erro ao ativar Premium:', error);
        alert('Pagamento recebido, mas houve um erro a ativar o Premium. Contacte o suporte.');
      } finally {
        setPendingStripeSession(null);
      }
    };
    activate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, pendingStripeSession]);

  const handleStripeCheckout = () => {
    const link = process.env.REACT_APP_STRIPE_PAYMENT_LINK;
    if (!link) {
      alert('Pagamento indisponível de momento. Contacte o suporte.');
      return;
    }
    const url = new URL(link);
    url.searchParams.set('client_reference_id', currentUser.uid);
    if (currentUser.email) url.searchParams.set('prefilled_email', currentUser.email);
    window.location.href = url.toString();
  };

  const loadData = async () => {
    try {
      let user = await getUserData(currentUser.uid);

      // Subscrição cancelada e período pago terminado → expirar Premium
      if (user?.isPremium && user?.cancelAtPeriodEnd && user?.premiumUntil && new Date(user.premiumUntil) <= new Date()) {
        await deactivatePremium(currentUser.uid);
        user = await getUserData(currentUser.uid);
      }

      setUserData(user);
      setIsAdmin(user?.isAdmin || false);

      // Manter a entrada pública do ranking atualizada (só dados não sensíveis)
      if (user && !user.isAdmin) {
        try { await syncLeaderboardEntry(currentUser.uid, user); } catch (e) { console.error('Sync ranking falhou:', e); }
      }

      // Em paralelo — muito mais rápido do que uma consulta de cada vez
      const [newsData, offersData, activitiesData, categoriesData, achievementsData, settingsData] = await Promise.all([
        getNews(),
        getOffers(),
        getActivities(),
        getActivityCategories(),
        getAchievements().catch((e) => { console.error('Erro ao carregar conquistas:', e); return []; }),
        getAppSettings().catch(() => ({})),
      ]);
      setAppSettings(settingsData || {});

      setNews(newsData);
      setOffers(offersData);
      setActivities(activitiesData);
      setCategories(categoriesData.length > 0 ? categoriesData : BASE_CATEGORIES);
      // Se ainda não houver conquistas na BD, usa as predefinidas (o admin pode importá-las)
      setAchievements(achievementsData.length > 0 ? achievementsData : DEFAULT_ACHIEVEMENTS.map((a, i) => ({ ...a, id: `default-${i}`, active: true })));

      // Carregar inscrições do utilizador (tudo em paralelo)
      if (!user?.isAdmin) {
        const [userRegs, challengesData, userChallengeRegs, rewards, myHistory] = await Promise.all([
          getUserRegistrations(currentUser.uid),
          getChallenges(),
          user?.isPremium ? getUserChallengeRegistrations(currentUser.uid) : Promise.resolve(null),
          user?.isPremium ? getUserRewards(currentUser.uid) : Promise.resolve(null),
          getUserActivityHistory(currentUser.uid).catch(() => []),
        ]);
        setUserRegistrations(userRegs);
        setMyActivityHistory(myHistory || []);
        setChallenges(challengesData.filter(c => c.active));
        if (userChallengeRegs) setUserChallengeRegistrations(userChallengeRegs);
        if (rewards) setUserRewardsData(rewards);
      }

      if (user?.isAdmin) {
        const [users, stats, registrations, pending, challengesData, rewardsData, challengeRegs, redemptionsData] = await Promise.all([
          getAllUsers(),
          getAdminStats(),
          getAllRegistrations(),
          getPendingRegistrationsCount(),
          getChallenges(),
          getRewardCatalog(),
          getAllChallengeRegistrations(),
          getRedemptions(),
        ]);

        setAllUsers(users);
        setAdminStats(stats);
        setAllRegistrations(registrations);
        setPendingCount(pending);
        setChallenges(challengesData);
        setRewardCatalog(rewardsData);
        setAllChallengeRegistrations(challengeRegs);
        setRedemptions(redemptionsData);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const [showVerificationScreen, setShowVerificationScreen] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [showLegalPage, setShowLegalPage] = useState(null);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResendVerification = async () => {
    try {
      await resendVerificationEmail();
      setResendCooldown(60);
      alert('Email de verificação reenviado! Verifica a tua caixa de entrada.');
    } catch (error) {
      if (error.code === 'auth/too-many-requests') {
        alert('Demasiados pedidos. Aguarda uns minutos antes de tentar novamente.');
      } else {
        alert('Erro ao reenviar email. Tenta novamente.');
      }
    }
  };

  const handleCheckVerification = async () => {
    try {
      await auth.currentUser?.reload();
      if (auth.currentUser?.emailVerified) {
        setCurrentUser(auth.currentUser);
        setShowVerificationScreen(false);
      } else {
        alert('Email ainda não verificado. Verifica a tua caixa de entrada e clica no link.');
      }
    } catch (error) {
      alert('Erro ao verificar. Tenta novamente.');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      alert('Insere o teu email.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(forgotEmail);
      alert('Email de redefinição enviado! Verifica a tua caixa de entrada (e spam).');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        alert('Não existe nenhuma conta com este email.');
      } else if (error.code === 'auth/invalid-email') {
        alert('Email inválido.');
      } else {
        alert('Erro ao enviar email. Tenta novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (authMode === 'login') {
        const user = await loginUser(email, password);
        if (!user.emailVerified) {
          setVerificationEmail(email);
          setShowVerificationScreen(true);
          setLoading(false);
          return;
        }
      } else {
        if (password !== confirmPassword) {
          alert('As passwords não coincidem!');
          setLoading(false);
          return;
        }
        if (password.length < 6) {
          alert('A password deve ter pelo menos 6 caracteres!');
          setLoading(false);
          return;
        }
        await registerUser(email, password, name);
        setVerificationEmail(email);
        setShowVerificationScreen(true);
        setLoading(false);
        return;
      }
      resetForm();
    } catch (error) {
      console.error('Erro na autenticação:', error);
      let errorMessage = 'Erro na autenticação!';

      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Este email já está registado!';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Email inválido!';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password muito fraca!';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'Utilizador não encontrado!';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Password incorreta!';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Credenciais inválidas!';
      }

      alert(errorMessage);
    }

    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      setCurrentUser(null);
      setUserData(null);
      setIsAdmin(false);
      setCurrentPage('novidades');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  // Próxima data de renovação mensal a partir da data de subscrição
  const nextRenewalDate = (premiumSince) => {
    const d = premiumSince ? new Date(premiumSince) : new Date();
    const now = new Date();
    while (d <= now) d.setMonth(d.getMonth() + 1);
    return d;
  };

  const handleCancelPremium = async () => {
    const portal = process.env.REACT_APP_STRIPE_PORTAL_LINK;
    if (!portal) {
      alert('A gestão da subscrição está indisponível de momento. Contacte o suporte.');
      return;
    }

    const until = nextRenewalDate(userData?.premiumSince);
    const untilLabel = until.toLocaleDateString('pt-PT');

    if (!window.confirm(`Vais ser levado ao portal seguro da Stripe para cancelar a subscrição:\n\n1. Introduz o teu email e o código de confirmação que vais receber.\n2. Cancela a subscrição no portal.\n\nManténs o Premium até ao fim do período já pago (${untilLabel}) e não voltas a ser cobrado.`)) return;

    const email = userData?.email || currentUser?.email || '';
    window.open(`${portal}${portal.includes('?') ? '&' : '?'}prefilled_email=${encodeURIComponent(email)}`, '_blank');

    if (!window.confirm('Já cancelaste a subscrição no portal da Stripe?\n\nClica OK apenas depois de concluíres o cancelamento no portal.')) return;

    try {
      await markPremiumCancellation(currentUser.uid, until.toISOString());
      alert(`Subscrição cancelada. Continuas Premium até ${untilLabel}.`);
      await loadData();
    } catch (error) {
      console.error('Erro ao registar cancelamento:', error);
      alert('Erro ao registar o cancelamento na app. Tenta novamente.');
    }
  };

  // Abre o modal de resgate (instruções + termos) em vez de resgatar logo
  const handleRedeemOffer = (offer) => {
    if (!currentUser || (userData?.points || 0) < (offer?.points || 0)) return;
    setRedeemTermsAccepted(false);
    setRedeemOfferData(offer);
  };

  const handleConfirmRedeem = async () => {
    const offer = redeemOfferData;
    if (!offer) return;
    setRedeemLoading(true);
    try {
      await redeemOffer(currentUser.uid, offer, { name: userData?.name, email: userData?.email || currentUser?.email });
      setRedeemOfferData(null);
      await loadData();
      alert('🎉 Pedido de resgate enviado!\n\nA nossa equipa vai processar e enviar-te os dados da recompensa por email.');
    } catch (error) {
      console.error('Erro ao resgatar:', error);
      alert('Erro ao resgatar. Tenta novamente.');
    } finally {
      setRedeemLoading(false);
    }
  };

  const handleOpenRegistrationModal = (activity) => {
    // Atividade já começou ou terminou → inscrições encerradas
    if (activity.archived) {
      alert('❌ Esta atividade já terminou.');
      return;
    }
    if (activity.ongoing || activity.registrationsClosed) {
      alert('❌ As inscrições estão encerradas — esta atividade já começou.');
      return;
    }

    // Verificar vagas disponíveis
    if (activity.availableSlots === undefined || activity.availableSlots <= 0) {
      alert('❌ Não há vagas disponíveis para esta atividade!');
      return;
    }

    // CORREÇÃO: Verificar se já tem inscrição (qualquer status)
    const existingReg = userRegistrations.find(
      reg => reg.activityId === activity.id
    );

    if (existingReg) {
      if (existingReg.status === 'pending') {
        alert('⏳ Já tem uma inscrição pendente para esta atividade!');
      } else if (existingReg.status === 'approved') {
        alert('✅ Já está inscrito nesta atividade!');
      } else if (existingReg.status === 'rejected') {
        // Permitir nova inscrição se foi rejeitada
        setSelectedActivity(activity);
        setShowRegistrationModal(true);
      }
      return;
    }

    // Se não houver inscrição existente, abrir modal
    setSelectedActivity(activity);
    setShowRegistrationModal(true);
  };

  const handleSubmitRegistration = async (formData) => {
    try {
      setLoading(true);

      const registrationData = {
        phone: formData.phone.trim(),
        age: formData.age.toString().trim(),
        emergencyContact: formData.emergencyContact.trim(),
        specialNeeds: formData.specialNeeds?.trim() || ''
      };

      console.log('📝 Enviando inscrição:', registrationData);

      await createActivityRegistration(
        selectedActivity.id,
        currentUser.uid,
        userData,
        registrationData
      );

      alert('✅ Pedido de inscrição enviado com sucesso!');
      setShowRegistrationModal(false);
      await loadData();
    } catch (error) {
      console.error('❌ Erro completo:', error);
      alert(`❌ Erro ao enviar inscrição: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRegistration = async (registration) => {
    // ✅ VERIFICAR SE JÁ FOI CONCLUÍDA
    if (registration.completed === true) {
      alert('❌ Não é possível cancelar uma atividade já concluída!');
      return;
    }

    if (!window.confirm('❌ Tem certeza que deseja cancelar esta inscrição?')) return;
    try {
      console.log('🔍 Dados da inscrição:', registration);
      setLoading(true);

      await cancelRegistration(registration.id, registration.activityId);

      console.log('✅ Cancelamento bem-sucedido!');
      alert('✅ Inscrição cancelada com sucesso!');

      // Recarregar dados
      await loadData();
    } catch (error) {
      console.error('❌ Erro completo ao cancelar:', error);

      // Mensagem de erro mais detalhada
      let errorMessage = '❌ Erro ao cancelar inscrição!';

      if (error.code === 'permission-denied') {
        errorMessage = '❌ Sem permissão para cancelar. Verifique as regras do Firestore.';
      } else if (error.message) {
        errorMessage = `❌ Erro: ${error.message}`;
      }

      alert(errorMessage);

      // Mesmo com erro, recarregar para ver o estado atual
      await loadData();
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPhotoUploadModal = (activity, registration) => {
    setSelectedActivity(activity);
    setSelectedRegistrationForPhoto(registration);
    setShowPhotoUploadModal(true);
  };

  const handleToggleTask = async (registration, taskId) => {
    if (!registration) return;
    const current = registration.completedTasks || [];
    const next = current.includes(taskId) ? current.filter((t) => t !== taskId) : [...current, taskId];
    try {
      await updateRegistrationChecklist(registration.id, next);
      await loadData();
    } catch (e) {
      console.error('Erro ao atualizar tarefa:', e);
      alert('Erro ao atualizar a tarefa.');
    }
  };

  const handleSubmitPhoto = async (imageFile) => {
    try {
      setLoading(true);

      // Upload para ImgBB
      const photoUrl = await uploadActivityPhoto(imageFile);

      // Captura da localização GPS (opcional — segue mesmo que o utilizador recuse)
      const location = await getCurrentLocation();

      // Guardar URL + localização no Firebase
      await submitActivityPhoto(selectedRegistrationForPhoto.id, photoUrl, location);

      alert(location
        ? '✅ Foto e localização enviadas! Aguarde aprovação do admin.'
        : '✅ Foto enviada! (localização não disponível) Aguarde aprovação do admin.');
      setShowPhotoUploadModal(false);
      await loadData();
    } catch (error) {
      console.error('Erro ao enviar foto:', error);
      alert('❌ Erro ao enviar foto!');
    } finally {
      setLoading(false);
    }
  };

  const handleInstallPWA = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setShowInstallButton(false);
      }
    } catch (error) {
      console.error('❌ Erro na instalação:', error);
    }

    setDeferredPrompt(null);
  };

  const handleUpdate = () => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      setShowUpdatePrompt(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setName('');
    setConfirmPassword('');
  };

  // ============================================
  // ADMIN FUNCTIONS
  // ============================================

  const openModal = (type, mode, item = null) => {
    setModalType(type);
    setModalMode(mode);
    setEditingItem(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleDeleteUser = async (userId) => {
    const userToDelete = allUsers.find(u => u.id === userId);
    if (userToDelete?.isAdmin) {
      alert('⚠️ Não é possível eliminar contas de administrador!');
      return;
    }

    if (!window.confirm('Tem certeza que deseja apagar este utilizador?')) return;
    try {
      await deleteUser(userId);
      alert('Utilizador apagado!');
      await loadData();
    } catch (error) {
      alert('Erro ao apagar utilizador.');
    }
  };

  const handleDeleteNews = async (newsId) => {
    if (!window.confirm('Apagar esta notícia?')) return;
    try {
      await deleteNews(newsId);
      alert('Notícia apagada!');
      await loadData();
    } catch (error) {
      alert('Erro ao apagar notícia.');
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('Apagar esta oferta?')) return;
    try {
      await deleteOffer(offerId);
      alert('Oferta apagada!');
      await loadData();
    } catch (error) {
      alert('Erro ao apagar oferta.');
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('Apagar esta atividade?')) return;
    try {
      await deleteActivity(activityId);
      alert('Atividade apagada!');
      await loadData();
    } catch (error) {
      alert('Erro ao apagar atividade.');
    }
  };

  // ============================================
  // PREMIUM / CHALLENGE FUNCTIONS
  // ============================================

  const handleEnrollChallenge = async (challenge) => {
    if (userData.points < challenge.pointsCost) {
      alert('Pontos insuficientes para se inscrever neste desafio!');
      return;
    }
    if (!window.confirm(`Inscrever-se no desafio "${challenge.name}" por ${challenge.pointsCost} pontos?`)) return;
    try {
      setLoading(true);
      await enrollInChallenge(currentUser.uid, userData.name, challenge);
      alert('Inscrição realizada com sucesso!');
      await loadData();
    } catch (error) {
      alert(error.message || 'Erro ao inscrever.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitChallengePhoto = async (registrationId, taskId, imageFile) => {
    try {
      setLoading(true);
      const photoUrl = await uploadActivityPhoto(imageFile);
      const location = await getCurrentLocation();
      await submitChallengeTaskPhoto(registrationId, taskId, photoUrl, location);
      alert('Foto enviada! Aguarde validação do admin.');
      await loadData();
    } catch (error) {
      alert('Erro ao enviar foto.');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimRewards = async (registrationId, challenge) => {
    try {
      setLoading(true);
      await claimChallengeRewards(registrationId, currentUser.uid, challenge);
      alert('Prémios recebidos com sucesso!');
      await loadData();
    } catch (error) {
      alert('Erro ao receber prémios.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReward = async (rewardId) => {
    if (!window.confirm('Apagar esta recompensa?')) return;
    try {
      await deleteReward(rewardId);
      alert('Recompensa apagada!');
      await loadData();
    } catch (error) {
      alert('Erro ao apagar recompensa.');
    }
  };

  const handleDeleteChallenge = async (challengeId) => {
    if (!window.confirm('Apagar este desafio?')) return;
    try {
      await deleteChallenge(challengeId);
      alert('Desafio apagado!');
      await loadData();
    } catch (error) {
      alert('Erro ao apagar desafio.');
    }
  };

  const handleSaveChallenge = async (challengeData, challengeId) => {
    try {
      if (challengeId) {
        await updateChallenge(challengeId, challengeData);
      } else {
        await addChallenge(challengeData);
      }
      alert('Desafio guardado com sucesso!');
      // Atualiza em segundo plano para o modal fechar já
      loadData().catch((e) => console.error('Erro ao atualizar dados:', e));
    } catch (error) {
      console.error('Erro ao guardar desafio:', error);
      alert('Erro ao guardar desafio.\n\n' + (error?.code ? `${error.code}\n` : '') + (error?.message || error));
      throw error;
    }
  };

  const handleApproveChallengePhoto = async (registrationId, taskId, taskPoints) => {
    if (!window.confirm('Aprovar esta foto e concluir a tarefa?')) return;
    try {
      await approveChallengeTaskPhoto(registrationId, taskId, taskPoints);
      alert('Foto aprovada! Tarefa concluída.');
      await loadData();
    } catch (error) {
      alert('Erro ao aprovar foto.');
    }
  };

  const handleRejectChallengePhoto = async (registrationId, taskId) => {
    if (!window.confirm('Rejeitar esta foto?')) return;
    try {
      await rejectChallengeTaskPhoto(registrationId, taskId);
      alert('Foto rejeitada.');
      await loadData();
    } catch (error) {
      alert('Erro ao rejeitar foto.');
    }
  };

  const RegistrationModal = ({
    show,
    activity,
    onClose,
    onSubmit,
    loading
  }) => {
    const [formData, setFormData] = useState({
      phone: '',
      age: '',
      emergencyContact: '',
      specialNeeds: ''
    });

    useEffect(() => {
      if (!show) {
        setFormData({ phone: '', age: '', emergencyContact: '', specialNeeds: '' });
      }
    }, [show]);

    if (!show || !activity) return null;

    const handleSubmit = () => {
      if (!formData.phone || !formData.age || !formData.emergencyContact) {
        alert('⚠️ Por favor, preencha todos os campos obrigatórios!');
        return;
      }
      onSubmit(formData);
    };

    return (
      <div className="fixed inset-0 bg-[#111828]/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-[28px] max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-extrabold text-[#111828]">Inscrição na atividade</h2>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#f7f3f5] flex items-center justify-center text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-5 p-4 bg-[#f7f3f5] rounded-2xl">
            <h3 className="font-bold text-[#111828] mb-2">{activity.name}</h3>
            <div className="space-y-1 text-sm text-gray-600">
              {activity.distance && activity.distance.trim() !== '' && (
                <p className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#714c62]" /> {activity.distance}</p>
              )}
              <p className="flex items-center gap-1.5"><Activity className="w-4 h-4 text-[#714c62]" /> {activity.difficulty}</p>
              <p className="flex items-center gap-1.5"><Leaf className="w-4 h-4 text-[#3d7a52]" /> +{activity.points} Eco-pontos</p>
              {activity.availableSlots !== undefined && (
                <p className="font-bold text-[#714c62] flex items-center gap-1.5">
                  <Users className="w-4 h-4" /> {activity.availableSlots} vagas disponíveis
                </p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">
                Telefone <span className="text-red-500">*</span>
              </label>
              <PhoneInput
                id="phone"
                value={formData.phone}
                onChange={(val) => setFormData(prev => ({ ...prev, phone: val }))}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">
                Idade <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.age}
                onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                className="w-full px-4 py-3 rounded-2xl border border-[#e8e2e6] focus:ring-2 focus:ring-[#714c62] focus:outline-none"
                placeholder="18"
                min="1"
                max="120"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">
                Contato de Emergência <span className="text-red-500">*</span>
              </label>
              <PhoneInput
                id="emergencyContact"
                value={formData.emergencyContact}
                onChange={(val) => setFormData(prev => ({ ...prev, emergencyContact: val }))}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">
                Necessidades Especiais (opcional)
              </label>
              <textarea
                value={formData.specialNeeds}
                onChange={(e) => setFormData(prev => ({ ...prev, specialNeeds: e.target.value }))}
                className="w-full px-4 py-3 rounded-2xl border border-[#e8e2e6] focus:ring-2 focus:ring-[#714c62] focus:outline-none h-24 resize-none"
                placeholder="Alergias, restrições alimentares, mobilidade reduzida..."
              />
            </div>
          </div>

          <div className="flex gap-2.5 mt-6">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 text-sm bg-[#f7f3f5] text-[#111828] font-bold rounded-2xl hover:bg-[#efe9ec] transition"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.phone || !formData.age || !formData.emergencyContact}
              className="flex-[1.4] px-4 py-3 text-sm bg-[#714c62] text-white font-bold rounded-2xl shadow-[0_5px_14px_rgba(113,76,98,0.35)] hover:bg-[#5c3d50] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'A enviar...' : 'Confirmar Inscrição'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const PhotoUploadModal = ({ show, activity, registration, onClose, onSubmit, loading }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
      if (!show) {
        setSelectedFile(null);
        setPreview(null);
      }
    }, [show]);

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        if (!file.type.startsWith('image/')) {
          alert('⚠️ Por favor, selecione apenas ficheiros de imagem!');
          return;
        }
        if (file.size > 10 * 1024 * 1024) {
          alert('⚠️ A imagem é muito grande! Máximo 10MB.');
          return;
        }
        setSelectedFile(file);
        setPreview(URL.createObjectURL(file));
      }
    };

    const handleSubmit = () => {
      if (!selectedFile) {
        alert('⚠️ Por favor, selecione uma foto!');
        return;
      }
      onSubmit(selectedFile);
    };

    if (!show || !activity) return null;

    return (
      <div className="fixed inset-0 bg-[#111828]/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-[28px] max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-extrabold text-[#111828] flex items-center gap-2">
              <Camera className="w-6 h-6 text-[#714c62]" />
              Enviar foto
            </h2>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#f7f3f5] flex items-center justify-center text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-5 p-4 bg-[#f7f3f5] rounded-2xl">
            <h3 className="font-bold text-[#111828] mb-1">{activity.name}</h3>
            <p className="text-sm text-gray-600">
              Envie uma foto que comprove a sua participação nesta atividade.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">
              Selecionar Foto <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="w-full px-4 py-2.5 rounded-2xl border border-[#e8e2e6] focus:ring-2 focus:ring-[#714c62] focus:outline-none"
              disabled={loading}
            />
            <p className="text-xs text-gray-400 mt-1">
              Formatos aceites: JPG, PNG, WEBP (máx 10MB)
            </p>
          </div>

          {preview && (
            <div className="mb-4">
              <p className="text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">Pré-visualização:</p>
              <img
                src={preview}
                alt="Preview"
                className="w-full h-64 object-cover rounded-2xl"
              />
            </div>
          )}

          <div className="flex gap-2.5">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 text-sm bg-[#f7f3f5] text-[#111828] font-bold rounded-2xl hover:bg-[#efe9ec] transition"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !selectedFile}
              className="flex-[1.4] px-4 py-3 text-sm bg-[#714c62] text-white font-bold rounded-2xl shadow-[0_5px_14px_rgba(113,76,98,0.35)] hover:bg-[#5c3d50] transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>A enviar...</>
              ) : (
                <>
                  <Camera className="w-4 h-4" />
                  Enviar Foto
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleFooterNavigate = (page) => {
    setShowLegalPage(page);
    window.scrollTo(0, 0);
  };

  // Página de Termos e Condições
  const TermosPage = () => (
    <div className="min-h-screen bg-[#f7f3f5]">
      <div className="bg-white/90 backdrop-blur shadow-[0_2px_10px_rgba(17,24,40,0.06)] px-4 py-4 flex items-center gap-3 sticky top-0 z-50">
        <button onClick={() => setShowLegalPage(null)} className="text-gray-600 hover:text-gray-800">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold text-[#111828]">Termos e Condições</h1>
      </div>
      <div className="px-4 py-6 max-w-3xl mx-auto">
        <p className="text-sm text-gray-500 mb-6">Última atualização: 8 de março de 2026</p>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">1. Aceitação dos Termos</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            Ao utilizar a aplicação GreenStep, o utilizador concorda com os presentes Termos e Condições. Se não concordar com alguma das condições, deverá cessar a utilização da aplicação.
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">2. Descrição do Serviço</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            O GreenStep é uma plataforma de turismo sustentável que permite aos utilizadores participar em atividades ecológicas, acumular pontos e trocar por recompensas. A aplicação inclui funcionalidades gratuitas e premium.
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">3. Registo e Conta</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            O utilizador deve fornecer informações verdadeiras e manter os seus dados de acesso seguros. Cada pessoa pode ter apenas uma conta. O GreenStep reserva-se o direito de suspender contas que violem estes termos.
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">4. Sistema de Pontos e Recompensas</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            Os pontos são atribuídos pela participação em atividades aprovadas pela administração. Os pontos não têm valor monetário e não podem ser transferidos entre utilizadores. As recompensas estão sujeitas a disponibilidade.
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">5. Subscrição Premium</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            A subscrição Premium é um serviço pago mensal processado via Stripe. O utilizador pode cancelar a qualquer momento através do portal da Stripe, mantendo o acesso Premium até ao fim do período já pago. Não são efetuados reembolsos de períodos já faturados.
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">6. Conteúdo do Utilizador</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            Ao submeter fotografias e outros conteúdos, o utilizador garante que possui os direitos sobre os mesmos e concede ao GreenStep licença para os utilizar no contexto da plataforma.
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">7. Limitação de Responsabilidade</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            O GreenStep não se responsabiliza por danos diretos ou indiretos resultantes da utilização da aplicação. As atividades ao ar livre são realizadas por conta e risco do utilizador.
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">8. Alterações aos Termos</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            O GreenStep pode alterar estes termos a qualquer momento. As alterações serão comunicadas através da aplicação. A continuação do uso após as alterações constitui aceitação dos novos termos.
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-8">
          <h2 className="text-lg font-bold text-[#111828] mb-3">9. Contacto</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            Para questões relacionadas com estes termos, contacte-nos através de greenstep@gmail.com.
          </p>
        </div>
      </div>
    </div>
  );

  // Página de histórico de versões (10 toques na versão, no rodapé)
  const ChangelogPage = () => (
    <div className="min-h-screen bg-[#f7f3f5]">
      <div className="bg-white/90 backdrop-blur shadow-[0_2px_10px_rgba(17,24,40,0.06)] px-4 py-4 flex items-center gap-3 sticky top-0 z-50">
        <button onClick={() => setShowLegalPage(null)} className="text-gray-600 hover:text-gray-800">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold text-[#111828]">Histórico de versões</h1>
      </div>
      <div className="px-4 py-6 max-w-3xl mx-auto pb-16">
        <div className="bg-[#111828] rounded-3xl p-5 mb-5 text-white shadow-[0_10px_26px_rgba(17,24,40,0.28)]">
          <p className="text-sm text-[#b3a5ad]">Versão instalada</p>
          <p className="text-3xl font-extrabold">{APP_VERSION}</p>
          <p className="text-xs text-[#8e7f88] mt-1">{CHANGELOG.length} versões publicadas</p>
        </div>

        <div className="space-y-3">
          {CHANGELOG.map((entry, i) => (
            <div key={entry.v} className={`bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-5 ${i === 0 ? 'ring-2 ring-[#714c62]' : ''}`}>
              <div className="flex items-center gap-2 mb-2.5">
                <h2 className="text-lg font-extrabold text-[#111828]">v{entry.v}</h2>
                {i === 0 && <span className="text-[10px] font-bold text-white bg-[#714c62] px-2 py-0.5 rounded-full">atual</span>}
              </div>
              <ul className="space-y-1.5">
                {entry.items.map((it, j) => (
                  <li key={j} className="text-sm text-gray-600 leading-relaxed flex gap-2">
                    <span className="text-[#714c62] mt-1.5 flex-shrink-0 w-1 h-1 rounded-full bg-[#714c62]" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Página de Política de Privacidade
  const PrivacidadePage = () => (
    <div className="min-h-screen bg-[#f7f3f5]">
      <div className="bg-white/90 backdrop-blur shadow-[0_2px_10px_rgba(17,24,40,0.06)] px-4 py-4 flex items-center gap-3 sticky top-0 z-50">
        <button onClick={() => setShowLegalPage(null)} className="text-gray-600 hover:text-gray-800">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold text-[#111828]">Política de Privacidade</h1>
      </div>
      <div className="px-4 py-6 max-w-3xl mx-auto">
        <p className="text-sm text-gray-500 mb-6">Última atualização: 8 de março de 2026</p>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">1. Dados Recolhidos</h2>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            Recolhemos os seguintes dados pessoais:
          </p>
          <ul className="text-gray-600 text-sm leading-relaxed list-disc pl-5 space-y-1.5">
            <li><strong>Dados de conta:</strong> nome, endereço de email e fotografia de perfil (se a adicionar).</li>
            <li><strong>Dados de inscrição em atividades:</strong> idade, contacto telefónico, contacto de emergência e, quando indicadas voluntariamente, necessidades especiais.</li>
            <li><strong>Fotografias</strong> submetidas como prova de conclusão de atividades e tarefas.</li>
            <li><strong>Dados de localização (GPS):</strong> quando submete uma fotografia, a aplicação solicita a sua localização para associar as coordenadas ao registo da atividade, permitindo validar que esta foi realizada no local. <strong>A recolha é opcional</strong> — pode recusar a permissão no seu dispositivo e continuar a utilizar a aplicação normalmente; a fotografia é submetida sem localização.</li>
            <li><strong>Dados de utilização:</strong> pontos acumulados, atividades e trilhos concluídos, conquistas e histórico de resgates.</li>
          </ul>
          <p className="text-gray-600 text-sm leading-relaxed mt-3">
            No caso de subscrições Premium, os dados de pagamento (cartão) são recolhidos e processados diretamente pela Stripe, não sendo em momento algum acedidos ou armazenados por nós.
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">1.1. Fundamento Legal</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            O tratamento dos seus dados assenta na <strong>execução do contrato</strong> (prestação do serviço da aplicação e gestão da conta), no seu <strong>consentimento</strong> (recolha de localização e submissão de fotografias, que pode retirar a qualquer momento nas definições do dispositivo) e no <strong>cumprimento de obrigações legais</strong> (nomeadamente fiscais, no caso das subscrições).
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">2. Finalidade do Tratamento</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            Os dados são utilizados para: gestão da conta do utilizador, atribuição de pontos e recompensas, validação de atividades (incluindo a verificação, através da localização e das fotografias, de que a atividade foi realizada), gestão das inscrições e do contacto de emergência durante as atividades, apresentação do ranking público, processamento de resgates de ofertas, comunicações relacionadas com o serviço e melhoria da experiência do utilizador.
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">3. Armazenamento e Segurança</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            Os dados são armazenados em servidores seguros do Google Firebase, localizados na União Europeia. Utilizamos encriptação e medidas de segurança adequadas para proteger as informações pessoais.
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">4. Partilha de Dados</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            Não vendemos nem partilhamos dados pessoais com terceiros para fins de marketing. Os dados podem ser partilhados com: Google Firebase (alojamento), Stripe (pagamentos Premium) e ImgBB (alojamento de imagens).
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">5. Direitos do Utilizador</h2>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            De acordo com o RGPD, o utilizador tem direito a: aceder aos seus dados pessoais, retificar dados incorretos, solicitar a eliminação dos dados, limitar ou opor-se ao tratamento, retirar o consentimento a qualquer momento e à portabilidade dos dados.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            <strong>Eliminação da conta:</strong> pode apagar a sua conta e todos os dados pessoais associados diretamente na aplicação, em <strong>Mais → Definições → Apagar a minha conta</strong>. A eliminação é imediata e irreversível.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Para exercer os restantes direitos, contacte greenstep@gmail.com. Tem ainda o direito de apresentar reclamação junto da Comissão Nacional de Proteção de Dados (CNPD).
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">6. Cookies e Tecnologias</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            A aplicação utiliza armazenamento local (localStorage) para manter a sessão do utilizador. Não utilizamos cookies de rastreamento de terceiros.
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
          <h2 className="text-lg font-bold text-[#111828] mb-3">7. Retenção de Dados</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            Os dados pessoais são mantidos enquanto a conta estiver ativa. Após eliminação da conta, os dados serão removidos num prazo de 30 dias, exceto quando a lei exigir retenção por período superior.
          </p>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-8">
          <h2 className="text-lg font-bold text-[#111828] mb-3">8. Contacto</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            Para questões de privacidade, contacte o responsável pelo tratamento de dados através de greenstep@gmail.com.
          </p>
        </div>
      </div>
    </div>
  );

  // Renderizar páginas legais
  if (showLegalPage === 'termos') return <TermosPage />;
  if (showLegalPage === 'privacidade') return <PrivacidadePage />;
  if (showLegalPage === 'changelog' && isAdmin) return <ChangelogPage />;

  // Ecrã de verificação de email
  if (showVerificationScreen) {
    return (
      <div className="min-h-screen bg-[#f7f3f5] flex items-center justify-center p-4">
        <div className="bg-white rounded-[28px] shadow-[0_24px_60px_rgba(17,24,40,0.15)] p-8 w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#714c62] rounded-full mb-6">
            <Mail className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-[#111828] mb-3">Verifica o teu email</h2>
          <p className="text-gray-600 mb-2">
            Enviámos um email de verificação para:
          </p>
          <p className="font-semibold text-[#714c62] mb-6">{verificationEmail}</p>
          <p className="text-gray-500 text-sm mb-8">
            Clica no link que recebeste no email para ativar a tua conta. Verifica também a pasta de spam.
          </p>

          <button
            onClick={handleCheckVerification}
            className="w-full bg-[#714c62] text-white py-3 font-bold rounded-2xl shadow-[0_5px_14px_rgba(113,76,98,0.35)] hover:bg-[#5c3d50] transition mb-3 flex items-center justify-center gap-2"
          >
            <CheckCircle className="w-5 h-5" />
            Já verifiquei o email
          </button>

          <button
            onClick={handleResendVerification}
            disabled={resendCooldown > 0}
            className="w-full bg-[#f7f3f5] text-[#111828] py-3 font-bold rounded-2xl hover:bg-[#efe9ec] transition mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resendCooldown > 0
              ? `Reenviar em ${resendCooldown}s`
              : 'Reenviar email de verificação'
            }
          </button>

          <button
            onClick={() => {
              logoutUser();
              setShowVerificationScreen(false);
              resetForm();
            }}
            className="w-full text-gray-500 py-2 text-sm hover:text-gray-700 transition"
          >
            Voltar ao login
          </button>
        </div>
      </div>
    );
  }

  // Ecrã de redefinição de senha
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-[#f7f3f5] flex items-center justify-center p-4">
        <div className="bg-white rounded-[28px] shadow-[0_24px_60px_rgba(17,24,40,0.15)] p-8 w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#714c62] rounded-full mb-6">
            <Lock className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-[#111828] mb-3">Redefinir senha</h2>
          <p className="text-gray-600 mb-6 text-sm">
            Insere o teu email e enviaremos um link para redefinires a tua senha.
          </p>

          <form onSubmit={handleForgotPassword}>
            <div className="mb-6 text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-2xl border border-[#e8e2e6] focus:ring-2 focus:ring-[#714c62] focus:border-transparent"
                  placeholder="o-teu@email.com"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#714c62] text-white py-3 font-bold rounded-2xl shadow-[0_5px_14px_rgba(113,76,98,0.35)] hover:bg-[#5c3d50] transition mb-3 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              {loading ? 'A enviar...' : 'Enviar email de redefinição'}
            </button>
          </form>

          <button
            onClick={() => { setShowForgotPassword(false); setForgotEmail(''); }}
            className="w-full text-gray-500 py-2 text-sm hover:text-gray-700 transition"
          >
            Voltar ao login
          </button>
        </div>
      </div>
    );
  }

  // Auth Page
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-[#f7f3f5] flex items-center justify-center p-4">
        <div className="bg-white rounded-[28px] shadow-[0_24px_60px_rgba(17,24,40,0.15)] p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/icons/greenstep-logo.png" alt="GreenStep" className="w-20 h-20 rounded-full object-cover mb-4 mx-auto" />
            <h1 className="text-3xl font-bold text-[#111828] mb-2">GreenStep</h1>
            <p className="text-gray-600">Seja um turista sustentável</p>
          </div>

          <div className="mb-6">
            <div className="flex bg-[#f1e9ee] p-1 rounded-full">
              <button
                onClick={() => setAuthMode('login')}
                className={`flex-1 py-2 font-medium rounded-full transition ${authMode === 'login' ? 'bg-white text-[#714c62] shadow' : 'text-gray-600'}`}
              >
                Entrar
              </button>
              <button
                onClick={() => setAuthMode('signup')}
                className={`flex-1 py-2 font-medium rounded-full transition ${authMode === 'signup' ? 'bg-white text-[#714c62] shadow' : 'text-gray-600'}`}
              >
                Registar
              </button>
            </div>
          </div>

          <div>
            {authMode === 'signup' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome Completo</label>
                <div className="relative">
                  <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-2xl border border-[#e8e2e6] focus:ring-2 focus:ring-[#714c62] focus:border-transparent"
                    placeholder="João Silva"
                  />
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-2xl border border-[#e8e2e6] focus:ring-2 focus:ring-[#714c62] focus:border-transparent"
                  placeholder="seu@email.com"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-2xl border border-[#e8e2e6] focus:ring-2 focus:ring-[#714c62] focus:border-transparent"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {authMode === 'login' && (
              <div className="text-right mb-4">
                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(true); setForgotEmail(email); }}
                  className="text-sm text-[#714c62] font-medium hover:underline"
                >
                  Esqueceu a senha?
                </button>
              </div>
            )}

            {authMode === 'signup' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-2xl border border-[#e8e2e6] focus:ring-2 focus:ring-[#714c62] focus:border-transparent"
                    placeholder="••••••••"
                  />
                </div>
              </div>
            )}

            <button
              onClick={handleAuth}
              disabled={loading}
              className="w-full bg-[#714c62] text-white py-3 font-bold rounded-2xl shadow-[0_5px_14px_rgba(113,76,98,0.35)] hover:bg-[#5c3d50] transition disabled:opacity-50"
            >
              {loading ? 'A processar...' : authMode === 'login' ? 'Entrar' : 'Criar Conta'}
            </button>
          </div>

          <div className="mt-6 text-center text-sm text-gray-600">
            {authMode === 'login' ? (
              <p>Ainda não tens conta? <button onClick={() => setAuthMode('signup')} className="text-[#714c62] font-medium">Regista-te</button></p>
            ) : (
              <p>Já tens conta? <button onClick={() => setAuthMode('login')} className="text-[#714c62] font-medium">Entra</button></p>
            )}
          </div>

          {/* Install PWA - Android */}
          {showInstallButton && (
            <div className="mt-6 bg-gradient-to-r from-[#714c62] to-[#5c3d50] rounded-3xl p-4 text-white text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Smartphone className="w-5 h-5" />
                <h3 className="font-bold">Instalar a App</h3>
              </div>
              <p className="text-sky-50 text-sm mb-3">Aceda mais rápido direto do ecrã inicial!</p>
              <button
                onClick={handleInstallPWA}
                className="bg-white text-[#714c62] px-6 py-2 font-semibold rounded-full hover:bg-sky-50 transition shadow-md inline-flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> Instalar Agora
              </button>
            </div>
          )}

          {/* Install PWA - iOS (Safari) */}
          {!showInstallButton && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.navigator.standalone && (
            <div className="mt-6 bg-gradient-to-r from-[#714c62] to-[#5c3d50] rounded-3xl p-4 text-white text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Smartphone className="w-5 h-5" />
                <h3 className="font-bold">Instalar a App</h3>
              </div>
              <p className="text-blue-50 text-sm mb-1">Para instalar no seu iPhone/iPad:</p>
              <div className="text-sm text-blue-50 space-y-1">
                <p>1. Toque no ícone <Share2 className="w-4 h-4 inline" /> (Partilhar)</p>
                <p>2. Selecione <strong>"Adicionar ao ecrã inicial"</strong></p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Se for ADMIN, mostrar Dashboard Admin
  if (isAdmin) {
    return <AdminDashboard
      userData={userData}
      allUsers={allUsers}
      news={news}
      offers={offers}
      activities={activities}
      categories={categories}
      adminStats={adminStats}
      allRegistrations={allRegistrations}
      pendingCount={pendingCount}
      handleLogout={handleLogout}
      openModal={openModal}
      handleDeleteUser={handleDeleteUser}
      handleDeleteNews={handleDeleteNews}
      handleDeleteOffer={handleDeleteOffer}
      handleDeleteActivity={handleDeleteActivity}
      showModal={showModal}
      closeModal={closeModal}
      modalType={modalType}
      modalMode={modalMode}
      editingItem={editingItem}
      loadData={loadData}
      challenges={challenges}
      rewardCatalog={rewardCatalog}
      allChallengeRegistrations={allChallengeRegistrations}
      redemptions={redemptions}
      achievements={achievements}
      appSettings={appSettings}
      handleFooterNavigate={handleFooterNavigate}
      handleDeleteReward={handleDeleteReward}
      handleDeleteChallenge={handleDeleteChallenge}
      handleSaveChallenge={handleSaveChallenge}
      handleApproveChallengePhoto={handleApproveChallengePhoto}
      handleRejectChallengePhoto={handleRejectChallengePhoto}
    />;
  }

  // Header Normal
  const Header = () => (
    <header className="fixed top-0 left-0 right-0 bg-[#f7f3f5]/90 backdrop-blur z-50 px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <img src="/icons/greenstep-logo.png" alt="GreenStep" className="w-10 h-10 rounded-full object-cover shadow-[0_3px_10px_rgba(17,24,40,0.12)]" />
        <span className="font-extrabold text-[#111828] text-lg">GreenStep</span>
      </div>
      <div className="flex items-center gap-2">
        {userData?.isPremium && (
          <span className="bg-[#fbf3dd] text-[#8a6d1c] text-[11px] px-2.5 py-1.5 rounded-full font-bold flex items-center gap-1">
            <Crown className="w-3 h-3" /> Premium
          </span>
        )}
        <div className="bg-white px-4 py-2 rounded-full shadow-[0_3px_10px_rgba(17,24,40,0.08)] flex items-center gap-1.5">
          <Leaf className="w-4 h-4 text-[#3d7a52]" />
          <span className="font-extrabold text-[#111828] text-sm">{userData?.points || 0} <span className="text-[#3d7a52]">Eco</span></span>
        </div>
      </div>
    </header>
  );

  // Bottom Navigation
  const BottomNav = () => {
    const navItems = [
      { id: 'novidades', icon: Leaf, label: 'Novidades' },
      { id: 'ofertas', icon: Gift, label: 'Ofertas' },
      { id: 'atividades', icon: Activity, label: 'Atividades' },
      { id: 'premium', icon: Crown, label: 'Premium' },
      { id: 'mais', icon: Menu, label: 'Mais' }
    ];

    return (
      <nav className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[28px] shadow-[0_-6px_20px_rgba(17,24,40,0.07)] z-50">
        <div className="flex justify-around items-center px-2 pt-3 pb-5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setCurrentPage(item.id);
                  setShowRewardsPage(false);
                  setShowRankingPage(false);
                  setShowAchievementsPage(false);
                  setShowSettingsPage(false);
                  setShowGoalPage(false);
                  setShowChallengeDetail(false);
                  setSelectedChallenge(null);
                }}
                className="flex items-center transition"
              >
                {isActive ? (
                  <span key="on" className="gs-pop flex items-center gap-2 bg-[#111828] text-white rounded-full px-4 py-2">
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-bold">{item.label}</span>
                  </span>
                ) : (
                  <span key="off" className="p-2 text-[#b7aeb4]"><Icon className="w-6 h-6" /></span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    );
  };

  // Pages
  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage('atividades');
  };

  const NovidadesPage = () => (
    <div className="pt-20 pb-0">
      <div className="px-4 max-w-lg mx-auto">
        <div className="relative h-56 mb-6 overflow-hidden rounded-3xl shadow-[0_10px_26px_rgba(17,24,40,0.18)]">
          <img
            src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&h=600&fit=crop"
            alt="Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111828]/70 to-[#111828]/10 flex flex-col items-start justify-end p-6 text-white">
            <h1 className="text-3xl font-extrabold mb-1">GreenStep</h1>
            <p className="text-sm text-white/85">Seja um turista sustentável</p>
          </div>
        </div>

        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#9b8b94]">Explorar</p>
        <h2 className="text-2xl font-extrabold text-[#111828] mb-4">Atividades a realizar</h2>
        <div className="mb-6 overflow-x-auto pb-2 -mx-4 px-4">
          <div className="flex gap-3 min-w-max">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryClick(cat.id)}
                className="flex flex-col items-center w-28 flex-shrink-0 group"
              >
                <div className="w-28 h-20 rounded-2xl overflow-hidden shadow-[0_3px_12px_rgba(17,24,40,0.08)] mb-2">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  />
                </div>
                <span className="text-xs text-center font-semibold leading-tight line-clamp-2 text-gray-600">
                  {cat.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 pb-28 max-w-lg mx-auto">
        <h3 className="text-lg font-extrabold text-[#111828] mb-3">Últimas Novidades</h3>
        {news.length === 0 ? (
          <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 text-center text-gray-400">
            <Leaf className="w-12 h-12 mx-auto mb-2 text-gray-200" />
            <p className="text-sm">Nenhuma novidade disponível.</p>
          </div>
        ) : (
          news.map((item) => (
            <div key={item.id} className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] mb-4 overflow-hidden">
              <img src={item.image} alt={item.title} className="w-full h-40 object-cover" />
              <div className="p-4">
                <div className="text-[11px] text-gray-400 mb-1">{item.date}</div>
                <h4 className="font-bold text-[#111828] mb-1.5">{item.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed mb-3">{item.description}</p>
                {item.points && (
                  <div className="inline-flex items-center gap-1.5 text-[#714c62] text-xs font-bold bg-[#f1e9ee] px-3 py-1.5 rounded-full">
                    <Star className="w-3.5 h-3.5 fill-[#714c62]" />
                    <span>+{item.points} pontos disponíveis</span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}

        <div className="bg-[#111828] rounded-3xl p-5 mb-4 mt-6 text-white shadow-[0_10px_26px_rgba(17,24,40,0.28)]">
          <h4 className="font-bold text-[#d9c7d2] text-sm mb-3 flex items-center gap-2"><Target className="w-4 h-4" /> As tuas estatísticas</h4>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white/10 rounded-2xl p-3 text-center">
              <div className="text-2xl font-extrabold">{userData?.activities || 0}</div>
              <div className="text-[11px] text-[#b3a5ad]">Atividades</div>
            </div>
            <div className="bg-white/10 rounded-2xl p-3 text-center">
              <div className="text-2xl font-extrabold">{userData?.trails || 0}</div>
              <div className="text-[11px] text-[#b3a5ad]">Trilhos</div>
            </div>
            <div className="bg-[#714c62] rounded-2xl p-3 text-center shadow-[0_6px_16px_rgba(113,76,98,0.4)]">
              <div className="text-2xl font-extrabold">{userData?.points || 0}</div>
              <div className="text-[11px] text-[#e6d5df]">Pontos</div>
            </div>
          </div>
        </div>

        {/* META PESSOAL */}
        {(() => {
          const goal = computeGoal(userData, myActivityHistory, activities, categories);
          const pct = goal.total > 0 ? Math.min(100, Math.round((goal.done / goal.total) * 100)) : 0;
          return (
            <div className="mb-4">
              <h4 className="font-bold text-[#111828] text-sm mb-2 flex items-center gap-2">
                <Target className="w-4 h-4 text-[#3d7a52]" /> Meta pessoal
              </h4>
              <p className="text-xs text-gray-500 mb-2">{goalMessage(pct)}</p>
              <div className="relative w-full h-7 bg-[#d9d9d9] rounded-full overflow-hidden">
                <div className="h-full bg-[#2d5f2d] rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                  style={{ width: `${Math.max(pct, 8)}%` }}>
                  <span className="text-white text-xs font-bold">{pct}%</span>
                </div>
              </div>
              <button onClick={() => { setCurrentPage('mais'); setShowGoalPage(true); }}
                className="text-[11px] text-gray-400 hover:text-[#714c62] transition mt-2 underline">
                Detalhes das metas pessoais
              </button>
            </div>
          );
        })()}

        {/* MINHAS INSCRIÇÕES */}
        {userRegistrations.length > 0 && (
          <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-4 mb-4">
            <h4 className="font-bold text-[#111828] text-sm mb-3 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-[#714c62]" /> Minhas Inscrições</h4>
            <div className="space-y-2">
              {userRegistrations.slice(0, 3).map((reg) => {
                const activity = activities.find(a => a.id === reg.activityId);
                return (
                  <div key={reg.id} className="flex items-center justify-between p-3 bg-[#f7f3f5] rounded-2xl">
                    <div>
                      <p className="text-sm font-bold text-[#111828]">{activity?.name || 'Atividade'}</p>
                      <p className="text-[11px] text-gray-400">{new Date(reg.createdAt).toLocaleDateString('pt-PT')}</p>
                    </div>
                    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${reg.status === 'pending' ? 'bg-[#fdf2e3] text-[#b45309]' :
                      reg.status === 'approved' ? 'bg-[#e6f2ea] text-[#3d7a52]' :
                        'bg-[#faeaea] text-[#8d3b3b]'
                      }`}>
                      {reg.status === 'pending' ? 'Pendente' :
                        reg.status === 'approved' ? 'Aprovado' : 'Rejeitado'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>    </div>
  );

  const OfertasPage = () => (
    <div className="pt-20 pb-0">
      <div className="px-4 pb-28 max-w-lg mx-auto">
        <div className="bg-[#111828] rounded-3xl p-6 mb-6 text-white shadow-[0_10px_26px_rgba(17,24,40,0.28)]">
          <p className="text-sm font-bold text-[#d9c7d2] mb-1">Os teus pontos</p>
          <div className="text-5xl font-extrabold mb-2">{userData?.points || 0}</div>
          <p className="text-[#b3a5ad] text-sm">Troca por recompensas incríveis!</p>
        </div>

        <h3 className="text-lg font-extrabold text-[#111828] mb-4">Recompensas Disponíveis</h3>
        <div className="grid gap-3">
          {offers.length === 0 ? (
            <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 text-center text-gray-400">
              <Gift className="w-12 h-12 mx-auto mb-2 text-gray-200" />
              <p className="text-sm">Nenhuma oferta disponível.</p>
            </div>
          ) : (
            offers.map((offer) => (
              <div key={offer.id} className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] overflow-hidden flex">
                <img src={offer.image} alt={offer.title} className="w-28 object-cover" />
                <div className="p-4 flex-1">
                  <h4 className="font-bold text-[#111828] mb-1.5">{offer.title}</h4>
                  {offer.partnerName && (
                    <p className="text-[11px] text-gray-400 mb-1.5 flex items-center gap-1">
                      <Users className="w-3 h-3" /> {offer.partnerType ? `${offer.partnerType}: ` : 'Oferta de '}{offer.partnerName}
                    </p>
                  )}
                  <div className="inline-flex items-center gap-1.5 text-[#714c62] font-bold text-xs bg-[#f1e9ee] px-3 py-1.5 rounded-full mb-3">
                    <Leaf className="w-3.5 h-3.5" />
                    <span>{offer.points} Eco-pontos</span>
                  </div>
                  <button
                    onClick={() => handleRedeemOffer(offer)}
                    className="block bg-[#714c62] text-white px-4 py-2 text-sm font-bold rounded-full shadow-[0_4px_12px_rgba(113,76,98,0.3)] hover:bg-[#5c3d50] transition disabled:opacity-50 disabled:shadow-none"
                    disabled={userData?.points < offer.points}
                  >
                    {userData?.points >= offer.points ? 'Resgatar' : 'Pontos insuficientes'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>    </div>
  );

  const AtividadesPage = () => {
    // Atividades arquivadas não aparecem ao utilizador
    const visibleActivities = activities.filter((a) => !a.archived);
    const byCategory = selectedCategory
      ? visibleActivities.filter(act => act.categoryId === selectedCategory)
      : visibleActivities;
    const term = (activitySearch || '').trim().toLowerCase();
    const filteredActivities = term
      ? byCategory.filter((a) => (a.name || '').toLowerCase().includes(term)
          || activityCode(a).toLowerCase().includes(term)
          || (a.zone || '').toLowerCase().includes(term))
      : byCategory;
    const ongoingMine = visibleActivities.filter(a => a.ongoing && userRegistrations.some(r => r.activityId === a.id && r.status === 'approved' && !r.completed));

    return (
      <div className="pt-20 pb-0">
        <div className="px-4 pb-28 max-w-lg mx-auto">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#9b8b94]">Explorar</p>
          <h2 className="text-2xl font-extrabold text-[#111828] mb-4">Atividades a realizar</h2>

          {/* Meta pessoal do mês */}
          {(() => {
            const goal = computeGoal(userData, myActivityHistory, activities, categories);
            const pct = goal.total > 0 ? Math.min(100, Math.round((goal.done / goal.total) * 100)) : 0;
            return (
              <button onClick={() => setShowGoalPage(true)} className="w-full text-left bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-4 mb-5">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <p className="text-sm font-extrabold text-[#111828] flex items-center gap-1.5">
                    <Target className="w-4 h-4 text-[#3d7a52]" /> Meta pessoal
                  </p>
                  <span className="text-[11px] font-bold text-[#714c62]">Ver →</span>
                </div>
                <p className="text-xs text-gray-500 mb-2">{goalMessage(pct)}</p>
                <div className="relative w-full h-6 bg-[#d9d9d9] rounded-full overflow-hidden">
                  <div className="h-full bg-[#2d5f2d] rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                    style={{ width: `${Math.max(pct, 8)}%` }}>
                    <span className="text-white text-[11px] font-bold">{pct}%</span>
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5">{goal.detail}</p>
              </button>
            );
          })()}

          {ongoingMine.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full bg-[#3d7a52] animate-pulse" />
                <h3 className="font-extrabold text-[#111828]">A decorrer</h3>
              </div>
              <div className="space-y-3">
                {ongoingMine.map(a => {
                  const reg = userRegistrations.find(r => r.activityId === a.id);
                  const total = a.tasks?.length || 0;
                  const done = (reg?.completedTasks || []).length;
                  return (
                    <button key={a.id} onClick={() => setDetailActivity(a)} className="w-full text-left bg-[#111828] text-white rounded-[22px] p-4 shadow-[0_8px_20px_rgba(17,24,40,0.28)] flex items-center gap-3">
                      {a.image && <img src={a.image} alt={a.name} className="w-14 h-14 rounded-2xl object-cover flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-extrabold truncate">{a.name}</p>
                        {a.zone && <p className="text-xs text-[#b3a5ad] flex items-center gap-1"><MapPin className="w-3 h-3" /> {a.zone}</p>}
                        {total > 0 && <p className="text-xs text-[#e8c76a] font-bold mt-0.5">{done}/{total} tarefas concluídas</p>}
                      </div>
                      <span className="text-xs font-bold bg-white/10 px-3 py-2 rounded-full whitespace-nowrap">Abrir</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* CATEGORIAS */}
          <div className="mb-6 overflow-x-auto pb-2 -mx-4 px-4">
            <div className="flex gap-3 min-w-max">
              <button
                onClick={() => setSelectedCategory(null)}
                className="flex flex-col items-center w-28 flex-shrink-0 group"
              >
                <div className={`w-28 h-20 rounded-2xl overflow-hidden mb-2 flex items-center justify-center ${selectedCategory === null ? 'bg-[#111828] shadow-[0_6px_16px_rgba(17,24,40,0.25)]' : 'bg-white shadow-[0_3px_12px_rgba(17,24,40,0.08)]'}`}>
                  <Globe className={`w-6 h-6 ${selectedCategory === null ? 'text-white' : 'text-[#714c62]'}`} />
                </div>
                <span className={`text-xs text-center font-semibold leading-tight ${selectedCategory === null ? 'text-[#111828]' : 'text-gray-600'}`}>
                  Todas
                </span>
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className="flex flex-col items-center w-28 flex-shrink-0 group"
                >
                  <div className={`w-28 h-20 rounded-2xl overflow-hidden mb-2 ${selectedCategory === cat.id ? 'ring-[3px] ring-[#714c62] shadow-[0_6px_16px_rgba(113,76,98,0.3)]' : 'shadow-[0_3px_12px_rgba(17,24,40,0.08)]'}`}>
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                  </div>
                  <span className={`text-xs text-center font-semibold leading-tight line-clamp-2 ${selectedCategory === cat.id ? 'text-[#714c62]' : 'text-gray-600'}`}>
                    {cat.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* PESQUISA */}
          <div className="relative mb-4">
            <Search className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={activitySearch}
              onChange={(e) => setActivitySearch(e.target.value)}
              placeholder="Pesquisar atividade, código ou zona…"
              className="w-full pl-11 pr-10 py-3 rounded-2xl border border-[#e8e2e6] bg-white text-sm shadow-[0_2px_8px_rgba(17,24,40,0.05)] focus:ring-2 focus:ring-[#714c62] focus:outline-none"
            />
            {activitySearch && (
              <button onClick={() => setActivitySearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[#f7f3f5] flex items-center justify-center text-gray-400">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* ATIVIDADES */}
          <div className="grid gap-4">
            {filteredActivities.length === 0 ? (
              <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 text-center text-gray-400">
                <Activity className="w-12 h-12 mx-auto mb-2 text-gray-200" />
                <p className="text-sm">Nenhuma atividade disponível nesta categoria.</p>
              </div>
            ) : (
              filteredActivities.map((activity) => {
                const category = categories.find(c => c.id === activity.categoryId);
                const userReg = userRegistrations.find(
                  reg => reg.activityId === activity.id && (reg.status === 'pending' || reg.status === 'approved')
                );

                return (
                  <div key={activity.id} className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] overflow-hidden">
                    <div className="relative h-44">
                      <img src={activity.image} alt={activity.name} className="w-full h-full object-cover" />
                      {category && (
                        <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 bg-white text-[#111828] text-[11px] font-bold rounded-full">
                          {category.iconComponent && <category.iconComponent className="w-3 h-3" />}
                          <span>{category.name}</span>
                        </span>
                      )}
                      <span className="absolute top-3 right-3 px-2.5 py-1 bg-[#714c62] text-white text-[11px] font-bold rounded-full">
                        +{activity.points} pts
                      </span>
                      {activity.certified && (
                        <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 bg-[#e6f2ea] text-[#3d7a52] text-[11px] font-bold rounded-full shadow-[0_2px_8px_rgba(17,24,40,0.12)]">
                          <CheckCircle className="w-3 h-3" /> Certificado
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <h4 className="font-bold text-[#111828]">{activity.name}</h4>
                        <span className="text-[10px] font-mono font-bold text-gray-500 bg-[#f2eef1] px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">{activityCode(activity)}</span>
                      </div>
                      {activity.zone && (
                        <p className="text-[11px] text-[#714c62] font-semibold mb-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {activity.zone}
                        </p>
                      )}
                      {activity.partnerName && (
                        <p className="text-[11px] text-gray-400 mb-1.5 flex items-center gap-1">
                          <Users className="w-3 h-3" /> {activity.partnerType ? `${activity.partnerType}: ` : ''}{activity.partnerName}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-3">
                        {activity.distance && activity.distance.trim() !== '' && (
                          <span className="flex items-center gap-1 bg-[#f7f3f5] px-2.5 py-1 rounded-full font-semibold">
                            <MapPin className="w-3 h-3" /> {activity.distance}
                          </span>
                        )}
                        <span className="bg-[#f7f3f5] px-2.5 py-1 rounded-full font-semibold">
                          {activity.difficulty}
                        </span>
                        {activity.availableSlots !== undefined && (
                          <span className={`px-2.5 py-1 rounded-full font-bold ${activity.availableSlots > 0 ? 'bg-[#f1e9ee] text-[#714c62]' : 'bg-[#faeaea] text-[#8d3b3b]'}`}>
                            {activity.availableSlots > 0
                              ? `${activity.availableSlots} vagas`
                              : 'Sem vagas'}
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => setDetailActivity(activity)}
                        className="w-full mb-3 px-4 py-2.5 text-sm font-bold bg-[#f1e9ee] text-[#714c62] rounded-2xl hover:bg-[#e6dae2] transition flex items-center justify-center gap-2"
                      >
                        <ClipboardList className="w-4 h-4" /> Ver detalhes e tarefas
                      </button>
                      <div className="flex items-center justify-between">
                        {userReg ? (
                          <div className="flex flex-col gap-2 w-full">
                            {userReg.status === 'pending' ? (
                              <div className="px-4 py-2.5 text-sm font-bold flex items-center gap-2 bg-[#fdf2e3] text-[#b45309] rounded-2xl">
                                <Clock className="w-4 h-4" />
                                Inscrição pendente
                              </div>
                            ) : userReg.completed ? (
                              <div className="px-4 py-2.5 text-sm font-bold flex items-center gap-2 bg-[#714c62] text-white rounded-2xl">
                                <CheckCircle className="w-4 h-4" />
                                Concluída
                              </div>
                            ) : (
                              <>
                                {activity.requiresPhoto ? (
                                  <>
                                    {!userReg.photoUrl ? (
                                      <div className="flex flex-col gap-2">
                                        <div className="px-4 py-2.5 text-sm font-bold flex items-center gap-2 bg-[#e6f2ea] text-[#3d7a52] rounded-2xl">
                                          <CheckCircle className="w-4 h-4" />
                                          Inscrito
                                        </div>
                                        <button
                                          onClick={() => handleOpenPhotoUploadModal(activity, userReg)}
                                          className="px-4 py-2.5 text-sm font-bold bg-[#714c62] text-white rounded-2xl shadow-[0_4px_12px_rgba(113,76,98,0.3)] hover:bg-[#5c3d50] transition flex items-center justify-center gap-2"
                                          disabled={loading}
                                        >
                                          <Camera className="w-4 h-4" />
                                          Enviar Foto
                                        </button>
                                        <button
                                          onClick={() => handleCancelRegistration(userReg)}
                                          className="px-4 py-2.5 text-sm font-bold bg-[#faeaea] text-[#8d3b3b] rounded-2xl hover:bg-[#f5dcdc] transition flex items-center justify-center gap-1.5"
                                          disabled={loading}
                                        >
                                          <XCircle className="w-4 h-4" />
                                          Cancelar Inscrição
                                        </button>
                                      </div>
                                    ) : userReg.photoStatus === 'pending' ? (
                                      <div className="flex flex-col gap-2">
                                        <div className="px-4 py-2.5 text-sm font-bold flex items-center gap-2 bg-[#fdf2e3] text-[#b45309] rounded-2xl">
                                          <Clock className="w-4 h-4" />
                                          Foto em análise
                                        </div>
                                        <div className="text-xs text-gray-400 px-2">
                                          Aguarde a aprovação do administrador
                                        </div>
                                      </div>
                                    ) : userReg.photoStatus === 'rejected' ? (
                                      <div className="flex flex-col gap-2">
                                        <div className="px-4 py-2.5 text-sm font-bold flex items-center gap-2 bg-[#faeaea] text-[#8d3b3b] rounded-2xl">
                                          <XCircle className="w-4 h-4" />
                                          Foto rejeitada
                                        </div>
                                        <button
                                          onClick={() => handleOpenPhotoUploadModal(activity, userReg)}
                                          className="px-4 py-2.5 text-sm font-bold bg-[#714c62] text-white rounded-2xl shadow-[0_4px_12px_rgba(113,76,98,0.3)] hover:bg-[#5c3d50] transition flex items-center justify-center gap-2"
                                          disabled={loading}
                                        >
                                          <Camera className="w-4 h-4" />
                                          Enviar Nova Foto
                                        </button>
                                        <button
                                          onClick={() => handleCancelRegistration(userReg)}
                                          className="px-4 py-2.5 text-sm font-bold bg-[#faeaea] text-[#8d3b3b] rounded-2xl hover:bg-[#f5dcdc] transition flex items-center justify-center gap-1.5"
                                          disabled={loading}
                                        >
                                          <XCircle className="w-4 h-4" />
                                          Cancelar Inscrição
                                        </button>
                                      </div>
                                    ) : null}
                                  </>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <div className="px-4 py-2.5 text-sm font-bold flex items-center gap-2 bg-[#e6f2ea] text-[#3d7a52] rounded-2xl">
                                      <CheckCircle className="w-4 h-4" />
                                      Inscrito
                                    </div>
                                    <button
                                      onClick={() => handleCancelRegistration(userReg)}
                                      className="px-4 py-2.5 text-sm font-bold bg-[#faeaea] text-[#8d3b3b] rounded-2xl hover:bg-[#f5dcdc] transition flex items-center gap-1.5"
                                      disabled={loading}
                                    >
                                      <XCircle className="w-4 h-4" />
                                      Cancelar
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => handleOpenRegistrationModal(activity)}
                            className="w-full bg-[#714c62] text-white px-4 py-3 text-sm font-bold rounded-2xl shadow-[0_5px_14px_rgba(113,76,98,0.35)] hover:bg-[#5c3d50] transition disabled:opacity-50 disabled:shadow-none"
                            disabled={activity.availableSlots === 0 || activity.ongoing || activity.registrationsClosed}
                          >
                            {(activity.ongoing || activity.registrationsClosed)
                              ? 'Inscrições encerradas'
                              : activity.availableSlots === 0 ? 'Esgotado' : 'Inscrever-me'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>      </div>
    );
  };

  // ============================================
  // CHALLENGE DETAIL VIEW (USER)
  // ============================================
  const ChallengeDetailView = () => {
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [activeTaskId, setActiveTaskId] = useState(null);
    const challenge = selectedChallenge;
    const registration = userChallengeRegistrations.find(r => r.challengeId === challenge?.id);

    if (!challenge) return null;

    const completedTasks = registration ? registration.taskProgress.filter(t => t.status === 'completed').length : 0;
    const totalTasks = challenge.tasks?.length || 0;
    const allDone = registration?.status === 'completed';

    const handlePhotoSelect = (e, taskId) => {
      const file = e.target.files[0];
      if (file) {
        if (!file.type.startsWith('image/')) { alert('Selecione uma imagem!'); return; }
        if (file.size > 10 * 1024 * 1024) { alert('Imagem muito grande! Máximo 10MB.'); return; }
        setPhotoFile(file);
        setPhotoPreview(URL.createObjectURL(file));
        setActiveTaskId(taskId);
      }
    };

    const handlePhotoSubmit = async (taskId) => {
      if (!photoFile || !registration) return;
      await handleSubmitChallengePhoto(registration.id, taskId, photoFile);
      setPhotoFile(null);
      setPhotoPreview(null);
      setActiveTaskId(null);
    };

    return (
      <div className="pt-20 pb-0">
        <div className="px-4 pb-28 max-w-lg mx-auto">
          <button onClick={() => { setShowChallengeDetail(false); setSelectedChallenge(null); }} className="flex items-center gap-2 text-gray-500 text-sm font-semibold mb-4 hover:text-[#111828]">
            <span className="w-9 h-9 rounded-full bg-white shadow-[0_2px_8px_rgba(17,24,40,0.08)] flex items-center justify-center"><ChevronLeft className="w-5 h-5 text-[#111828]" /></span>
            Voltar aos Desafios
          </button>

          {challenge.image && (
            <img src={challenge.image} alt={challenge.name} className="w-full h-44 object-cover rounded-3xl mb-4 shadow-[0_6px_18px_rgba(17,24,40,0.12)]" />
          )}

          <h2 className="text-2xl font-extrabold text-[#111828] mb-2">{challenge.name}</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-4">{challenge.description}</p>

          {registration && (
            <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-4 mb-4">
              <div className="flex justify-between text-xs font-semibold text-gray-500 mb-2">
                <span>Progresso: {completedTasks}/{totalTasks} tarefas</span>
                <span className="font-bold text-[#714c62]">{totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0}%</span>
              </div>
              <div className="w-full bg-[#f1e9ee] h-2.5 rounded-full">
                <div className="bg-[#714c62] h-2.5 rounded-full transition-all" style={{ width: `${totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0}%` }} />
              </div>
            </div>
          )}

          {!registration && (
            <button
              onClick={() => handleEnrollChallenge(challenge)}
              disabled={loading}
              className="w-full bg-[#714c62] text-white py-3.5 font-bold rounded-2xl shadow-[0_5px_14px_rgba(113,76,98,0.35)] hover:bg-[#5c3d50] transition mb-6 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Target className="w-5 h-5" /> Inscrever-se ({challenge.pointsCost} pontos)
            </button>
          )}

          {registration && (
            <div className="space-y-3 mb-6">
              <h3 className="font-extrabold text-[#111828] text-lg">Tarefas</h3>
              {(challenge.tasks || []).sort((a, b) => a.order - b.order).map(task => {
                const progress = registration.taskProgress.find(tp => tp.taskId === task.id);
                const status = progress?.status || 'locked';
                const photoStatus = progress?.photoStatus;

                return (
                  <div key={task.id} className={`bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-4 ${status === 'locked' ? 'opacity-50' : ''}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        status === 'completed' ? 'bg-[#714c62]' : status === 'available' ? 'bg-[#f1e9ee]' : 'bg-[#f7f3f5]'
                      }`}>
                        {status === 'completed' ? <CheckCircle className="w-5 h-5 text-white" /> :
                         status === 'locked' ? <LockIcon className="w-4 h-4 text-gray-400" /> :
                         <span className="text-[#714c62] font-extrabold text-sm">{task.order}</span>}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-[#111828]">{task.name}</h4>
                        <p className="text-sm text-gray-500 mb-1">{task.description}</p>
                        {task.details && <p className="text-xs text-gray-400 mb-2">{task.details}</p>}
                        <span className="text-xs text-[#714c62] font-bold">+{task.points} pontos</span>

                        {status === 'completed' && (
                          <p className="text-xs text-[#3d7a52] font-bold mt-1 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Concluída</p>
                        )}

                        {status === 'available' && photoStatus === 'pending' && (
                          <div className="mt-2 bg-[#fdf2e3] rounded-2xl p-3 text-sm text-[#b45309] font-semibold flex items-center gap-2">
                            <Clock className="w-4 h-4" /> Foto enviada — aguardar validação
                          </div>
                        )}

                        {status === 'available' && photoStatus === 'rejected' && (
                          <div className="mt-2 bg-[#faeaea] rounded-2xl p-3 text-sm text-[#8d3b3b] font-semibold flex items-center gap-2">
                            <XCircle className="w-4 h-4" /> Foto rejeitada — envie outra foto
                          </div>
                        )}

                        {status === 'available' && (photoStatus === null || photoStatus === 'rejected') && (
                          <div className="mt-3">
                            <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">Enviar foto de prova</label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handlePhotoSelect(e, task.id)}
                              className="w-full text-sm rounded-2xl border border-[#e8e2e6] p-2.5"
                            />
                            {activeTaskId === task.id && photoPreview && (
                              <div className="mt-2">
                                <img src={photoPreview} alt="Preview" className="w-full h-32 object-cover rounded-2xl mb-2" />
                                <button
                                  onClick={() => handlePhotoSubmit(task.id)}
                                  disabled={loading}
                                  className="bg-[#714c62] text-white px-4 py-2.5 text-sm font-bold rounded-2xl shadow-[0_4px_12px_rgba(113,76,98,0.3)] hover:bg-[#5c3d50] transition disabled:opacity-50 flex items-center gap-2"
                                >
                                  <Camera className="w-4 h-4" /> {loading ? 'A enviar...' : 'Enviar Foto'}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {allDone && registration && !registration.rewardsClaimed && (
            <button
              onClick={() => handleClaimRewards(registration.id, challenge)}
              className="w-full bg-[#714c62] text-white py-4 font-extrabold text-lg rounded-2xl shadow-[0_8px_20px_rgba(113,76,98,0.4)] hover:bg-[#5c3d50] transition flex items-center justify-center gap-2 mb-4"
            >
              <Award className="w-6 h-6" /> Receber Prémios!
            </button>
          )}

          {allDone && registration?.rewardsClaimed && (
            <div className="bg-[#e6f2ea] rounded-[22px] p-5 mb-4 text-center">
              <CheckCircle className="w-8 h-8 text-[#3d7a52] mx-auto mb-2" />
              <p className="text-[#3d7a52] font-bold">Desafio concluído! Prémios recebidos.</p>
              <p className="text-sm text-[#3d7a52]">Veja os seus prémios em Mais → Recompensas</p>
            </div>
          )}

          {challenge.rewards && challenge.rewards.length > 0 && (
            <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-4">
              <h3 className="font-bold text-[#111828] mb-3">Prémios deste desafio</h3>
              <div className="grid grid-cols-2 gap-3">
                {challenge.rewards.map((reward, i) => (
                  <div key={i} className="bg-[#f7f3f5] rounded-2xl p-3 text-center">
                    {reward.image && <img src={reward.image} alt={reward.name} className="w-full h-20 object-cover rounded-xl mb-2" />}
                    <p className="text-sm font-semibold text-[#111828]">{reward.name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>      </div>
    );
  };

  const PremiumPage = () => {
    if (!userData?.isPremium) {
      return (
        <div className="pt-20 pb-0">
          <div className="px-4 pb-28 max-w-lg mx-auto">
            <div className="bg-[#111828] rounded-3xl p-8 mb-6 text-white text-center shadow-[0_10px_26px_rgba(17,24,40,0.28)]">
              <Crown className="w-14 h-14 mx-auto mb-4 text-[#e8c76a]" />
              <h2 className="text-3xl font-extrabold mb-2">GreenStep Premium</h2>
              <p className="text-[#b3a5ad] mb-4">Desbloqueia funcionalidades exclusivas</p>
              <div className="text-4xl font-extrabold mb-6">4,99€<span className="text-lg font-bold text-[#b3a5ad]">/mês</span></div>
              <div className="max-w-sm mx-auto">
                <button
                  onClick={handleStripeCheckout}
                  className="w-full bg-[#e8c76a] text-[#111828] py-4 font-extrabold text-lg rounded-2xl shadow-[0_8px_20px_rgba(232,199,106,0.35)] hover:bg-[#dcb84f] transition flex items-center justify-center gap-2"
                >
                  <Crown className="w-5 h-5" /> Subscrever Premium
                </button>
                <p className="text-xs text-[#b3a5ad] mt-3 flex items-center justify-center gap-1.5">
                  <Lock className="w-3 h-3" /> Pagamento seguro processado pela Stripe
                </p>
              </div>
            </div>

            <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
              <h3 className="font-extrabold text-[#111828] mb-4">Vantagens Premium</h3>
              <ul className="space-y-3">
                {[
                  'Acesso a Desafios exclusivos',
                  'Ganhe recompensas especiais',
                  'Badge Premium no perfil',
                  'Desafios com tarefas sequenciais',
                  'Catálogo de prémios exclusivo',
                  'Sem anúncios'
                ].map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#f1e9ee] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-[#714c62]" />
                    </div>
                    <span className="text-gray-600 text-sm">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>        </div>
      );
    }

    // PREMIUM USER: show challenges
    if (showChallengeDetail && selectedChallenge) {
      return <ChallengeDetailView />;
    }

    return (
      <div className="pt-20 pb-0">
        <div className="px-4 pb-28 max-w-lg mx-auto">
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#9b8b94]">Premium</p>
          <h2 className="text-2xl font-extrabold text-[#111828] mb-5 flex items-center gap-2">Desafios <Crown className="w-6 h-6 text-[#e8c76a]" /></h2>

          {userData?.cancelAtPeriodEnd && userData?.premiumUntil && (
            <div className="bg-[#fbf3dd] rounded-2xl p-3.5 mb-4 flex gap-2 items-start">
              <Clock className="w-4 h-4 text-[#8a6d1c] mt-0.5 flex-shrink-0" />
              <p className="text-xs text-[#8a6d1c] leading-relaxed">
                Subscrição cancelada — manténs o Premium até <span className="font-bold">{new Date(userData.premiumUntil).toLocaleDateString('pt-PT')}</span>.
              </p>
            </div>
          )}

          {challenges.length === 0 ? (
            <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-8 text-center">
              <Target className="w-12 h-12 text-gray-200 mx-auto mb-4" />
              <p className="text-gray-400 text-sm">Nenhum desafio disponível de momento.</p>
              <p className="text-xs text-gray-300 mt-2">Volte em breve para novos desafios!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {challenges.map(challenge => {
                const registration = userChallengeRegistrations.find(r => r.challengeId === challenge.id);
                const completedTasks = registration ? registration.taskProgress.filter(t => t.status === 'completed').length : 0;
                const totalTasks = challenge.tasks?.length || 0;
                const progressPercent = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

                return (
                  <div
                    key={challenge.id}
                    onClick={() => { setSelectedChallenge(challenge); setShowChallengeDetail(true); }}
                    className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] overflow-hidden cursor-pointer hover:shadow-lg transition"
                  >
                    {challenge.image && (
                      <img src={challenge.image} alt={challenge.name} className="w-full h-36 object-cover" />
                    )}
                    <div className="p-4">
                      <h3 className="font-bold text-[#111828] mb-1">{challenge.name}</h3>
                      <p className="text-sm text-gray-500 mb-3 line-clamp-2">{challenge.description}</p>

                      {registration ? (
                        <div>
                          <div className="flex justify-between text-xs font-semibold text-gray-500 mb-1.5">
                            <span>{completedTasks}/{totalTasks} tarefas</span>
                            <span className="font-bold text-[#714c62]">{Math.round(progressPercent)}%</span>
                          </div>
                          <div className="w-full bg-[#f1e9ee] h-2 rounded-full">
                            <div className="bg-[#714c62] h-2 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
                          </div>
                          {registration.status === 'completed' && registration.rewardsClaimed && (
                            <p className="text-xs text-[#3d7a52] font-bold mt-2 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Concluído</p>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#714c62] font-bold">{challenge.pointsCost} pontos</span>
                          <span className="text-[11px] bg-[#f1e9ee] text-[#714c62] px-2.5 py-1 rounded-full font-bold">Novo</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>      </div>
    );
  };

  // Rewards Sub-Page
  const handleOpenProfileEdit = () => {
    setProfileEditName(userData?.name || '');
    setProfileEditPhotoFile(null);
    setProfileEditPhotoPreview(null);
    setShowProfileEditPage(true);
  };

  const handleProfilePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Por favor seleciona um ficheiro de imagem.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('A imagem é demasiado grande (máx. 5MB).');
      return;
    }
    setProfileEditPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setProfileEditPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleProfileEditSave = async () => {
    if (!currentUser) return;
    const trimmedName = profileEditName.trim();
    if (trimmedName.length < 2) {
      alert('O nome tem de ter pelo menos 2 caracteres.');
      return;
    }
    if (trimmedName.length > 40) {
      alert('O nome não pode ter mais de 40 caracteres.');
      return;
    }

    try {
      setProfileEditSaving(true);
      const updates = {};

      if (trimmedName !== (userData?.name || '')) {
        updates.name = trimmedName;
      }

      if (profileEditPhotoFile) {
        const photoUrl = await uploadActivityPhoto(profileEditPhotoFile);
        updates.photoURL = photoUrl;
      }

      if (Object.keys(updates).length === 0) {
        alert('Nada foi alterado.');
        setProfileEditSaving(false);
        return;
      }

      await updateUser(currentUser.uid, updates);
      await loadData();
      alert('✅ Perfil atualizado com sucesso!');
      setShowProfileEditPage(false);
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      alert('❌ Erro ao atualizar perfil. Tenta novamente.');
    } finally {
      setProfileEditSaving(false);
    }
  };

  const ProfileEditSubPage = () => {
    const currentPhoto = profileEditPhotoPreview || userData?.photoURL;
    return (
      <div className="pt-20 pb-0">
        <div className="px-4 pb-28 max-w-lg mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <button onClick={() => setShowProfileEditPage(false)} className="w-10 h-10 rounded-full bg-white shadow-[0_2px_8px_rgba(17,24,40,0.08)] flex items-center justify-center">
              <ChevronLeft className="w-5 h-5 text-[#111828]" />
            </button>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#9b8b94]">Conta</p>
              <h2 className="text-2xl font-extrabold text-[#111828]">Editar Perfil</h2>
            </div>
          </div>

          <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-6 mb-4">
            <div className="flex flex-col items-center mb-6">
              <div className="relative">
                {currentPhoto ? (
                  <img
                    src={currentPhoto}
                    alt="Foto de perfil"
                    className="w-28 h-28 rounded-full object-cover ring-4 ring-[#f1e9ee]"
                  />
                ) : (
                  <div className="w-28 h-28 bg-[#714c62] rounded-full flex items-center justify-center text-white text-4xl font-bold ring-4 ring-[#f1e9ee]">
                    {(profileEditName || userData?.name || 'U').charAt(0).toUpperCase()}
                  </div>
                )}
                <label
                  htmlFor="profile-photo-input"
                  className="absolute bottom-0 right-0 bg-[#714c62] text-white p-2.5 rounded-full cursor-pointer hover:bg-[#5c3d50] transition shadow-[0_4px_12px_rgba(113,76,98,0.4)]"
                >
                  <Camera className="w-4 h-4" />
                  <input
                    id="profile-photo-input"
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePhotoSelect}
                    className="hidden"
                  />
                </label>
              </div>
              <p className="text-xs text-gray-400 mt-3">Clica no ícone da câmara para mudar a foto</p>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">
                Nome público
              </label>
              <input
                type="text"
                value={profileEditName}
                onChange={(e) => setProfileEditName(e.target.value)}
                maxLength={40}
                placeholder="O teu nome"
                className="w-full rounded-2xl border border-[#e8e2e6] p-3 focus:outline-none focus:ring-2 focus:ring-[#714c62]"
              />
              <p className="text-xs text-gray-400 mt-1">{profileEditName.length}/40 caracteres</p>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">Email</label>
              <input
                type="email"
                value={userData?.email || currentUser?.email || ''}
                disabled
                className="w-full rounded-2xl border border-[#eee9ec] bg-[#f7f3f5] p-3 text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-400 mt-1">O email não pode ser alterado.</p>
            </div>

            <div className="flex gap-2.5 mt-6">
              <button
                onClick={() => setShowProfileEditPage(false)}
                disabled={profileEditSaving}
                className="flex-1 bg-[#f7f3f5] text-[#111828] py-3 font-bold rounded-2xl hover:bg-[#efe9ec] transition disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleProfileEditSave}
                disabled={profileEditSaving}
                className="flex-[1.4] bg-[#714c62] text-white py-3 font-bold rounded-2xl shadow-[0_5px_14px_rgba(113,76,98,0.35)] hover:bg-[#5c3d50] transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {profileEditSaving ? 'A guardar...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>      </div>
    );
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('⚠️ APAGAR A TUA CONTA\n\nIsto elimina permanentemente:\n• O teu perfil e dados pessoais\n• Os teus Eco-pontos e histórico\n• As tuas inscrições e recompensas\n\nEsta ação é irreversível. Continuar?')) return;
    if (!window.confirm('Tens mesmo a certeza? Não é possível recuperar a conta.')) return;
    setDeletingAccount(true);
    try {
      await deleteMyAccount();
      alert('A tua conta foi apagada. Obrigado por teres feito parte da GreenStep.');
      setCurrentUser(null);
      setUserData(null);
    } catch (error) {
      console.error('Erro ao apagar conta:', error);
      if (error?.code === 'auth/requires-recent-login') {
        alert('Por segurança, precisas de iniciar sessão novamente antes de apagar a conta.\n\nSai da conta, volta a entrar e tenta outra vez.');
      } else {
        alert('Erro ao apagar a conta: ' + (error?.message || error));
      }
    } finally {
      setDeletingAccount(false);
    }
  };

  const GoalSubPage = () => {
    const goal = computeGoal(userData, myActivityHistory, activities, categories);
    const pct = goal.total > 0 ? Math.min(100, Math.round((goal.done / goal.total) * 100)) : 0;
    const [type, setType] = useState(userData?.goalType || 'monthly_count');
    const [target, setTarget] = useState(userData?.goalTarget || 4);
    const [saving, setSaving] = useState(false);
    const monthName = new Date().toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
    const rewardPoints = parseInt(appSettings?.goalRewardPoints) || 0;
    const currentMonthKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
    const alreadyAwarded = userData?.goalAwardedMonth === currentMonthKey;

    const handleSaveGoal = async () => {
      setSaving(true);
      try {
        await updateUser(currentUser.uid, {
          goalType: type,
          goalTarget: Math.max(1, parseInt(target) || 1),
        });
        await loadData();
        alert('✅ Meta guardada!');
      } catch (e) {
        console.error('Erro ao guardar meta:', e);
        alert('❌ Erro ao guardar a meta: ' + (e?.message || e));
      } finally { setSaving(false); }
    };

    return (
      <div className="pt-20 pb-0">
        <div className="px-4 pb-28 max-w-lg mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <button onClick={() => setShowGoalPage(false)} className="w-10 h-10 rounded-full bg-white shadow-[0_2px_8px_rgba(17,24,40,0.08)] flex items-center justify-center">
              <ChevronLeft className="w-5 h-5 text-[#111828]" />
            </button>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#9b8b94]">Progresso</p>
              <h2 className="text-2xl font-extrabold text-[#111828] flex items-center gap-2">Meta pessoal <Target className="w-6 h-6 text-[#3d7a52]" /></h2>
              <p className="text-xs text-gray-400 capitalize">{monthName}</p>
            </div>
          </div>

          {/* Progresso atual */}
          <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-5 mb-4">
            <p className="text-sm font-bold text-[#111828] mb-1">{goal.title}</p>
            <p className="text-xs text-gray-500 mb-3">{goalMessage(pct)}</p>
            <div className="relative w-full h-7 bg-[#d9d9d9] rounded-full overflow-hidden">
              <div className="h-full bg-[#2d5f2d] rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                style={{ width: `${Math.max(pct, 8)}%` }}>
                <span className="text-white text-xs font-bold">{pct}%</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">{goal.detail}</p>

            {rewardPoints > 0 && (
              <div className={`mt-3 rounded-2xl p-3 text-center ${alreadyAwarded ? 'bg-[#e6f2ea]' : 'bg-[#fbf3dd]'}`}>
                {alreadyAwarded ? (
                  <p className="text-xs font-bold text-[#3d7a52] flex items-center justify-center gap-1.5">
                    <CheckCircle className="w-4 h-4" /> Já recebeste {rewardPoints} Eco-pontos por esta meta!
                  </p>
                ) : (
                  <p className="text-xs font-bold text-[#8a6d1c] flex items-center justify-center gap-1.5">
                    <Leaf className="w-4 h-4" /> Ganha {rewardPoints} Eco-pontos ao cumprir esta meta
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Detalhe por categoria, quando a meta é essa */}
          {goal.type === 'all_categories' && (
            <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-5 mb-4">
              <p className="text-sm font-bold text-[#111828] mb-3">Tipos de atividade</p>
              <div className="space-y-2">
                {categories.map((c) => {
                  const ok = goal.coveredCategories?.has(c.id);
                  return (
                    <div key={c.id} className="flex items-center gap-2.5">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${ok ? 'bg-[#3d7a52] text-white' : 'bg-[#f2eef1] text-gray-400'}`}>
                        {ok ? <CheckCircle className="w-4 h-4" /> : <LockIcon className="w-3 h-3" />}
                      </span>
                      <span className={`text-sm ${ok ? 'font-bold text-[#111828]' : 'text-gray-500'}`}>{c.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Escolher a meta */}
          <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-5">
            <p className="text-sm font-bold text-[#111828] mb-3">Definir a minha meta</p>
            <div className="space-y-2.5 mb-4">
              {GOAL_TYPES.map((g) => (
                <button key={g.id} onClick={() => setType(g.id)}
                  className={`w-full text-left px-4 py-3 rounded-2xl border text-sm font-semibold transition flex items-center gap-2.5 ${type === g.id ? 'bg-[#e6f2ea] border-[#3d7a52] text-[#2d5f2d]' : 'bg-[#f7f3f5] border-transparent text-gray-600'}`}>
                  <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${type === g.id ? 'border-[#3d7a52] bg-[#3d7a52]' : 'border-gray-300'}`} />
                  {g.label}
                </button>
              ))}
            </div>

            {type === 'monthly_count' && (
              <div className="mb-4">
                <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">Quantas atividades por mês?</label>
                <input type="number" min="1" max="60" value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-[#e8e2e6] bg-white focus:ring-2 focus:ring-[#3d7a52] focus:outline-none" />
              </div>
            )}

            <button onClick={handleSaveGoal} disabled={saving}
              className="w-full bg-[#2d5f2d] text-white py-3 font-bold rounded-2xl hover:bg-[#244d24] transition disabled:opacity-50 flex items-center justify-center gap-2">
              <Target className="w-4 h-4" /> {saving ? 'A guardar…' : 'Guardar meta'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const SettingsSubPage = () => (
    <div className="pt-20 pb-0">
      <div className="px-4 pb-28 max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => setShowSettingsPage(false)} className="w-10 h-10 rounded-full bg-white shadow-[0_2px_8px_rgba(17,24,40,0.08)] flex items-center justify-center">
            <ChevronLeft className="w-5 h-5 text-[#111828]" />
          </button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#9b8b94]">Conta</p>
            <h2 className="text-2xl font-extrabold text-[#111828]">Definições</h2>
          </div>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-5 mb-4">
          <h3 className="font-bold text-[#111828] mb-3 text-sm">A tua conta</h3>
          <div className="space-y-2 text-sm">
            <p className="flex justify-between gap-3"><span className="text-gray-500">Nome</span><span className="font-semibold text-[#111828] text-right">{userData?.name || '—'}</span></p>
            <p className="flex justify-between gap-3"><span className="text-gray-500">Email</span><span className="font-semibold text-[#111828] text-right break-all">{userData?.email || currentUser?.email}</span></p>
            <p className="flex justify-between gap-3"><span className="text-gray-500">Membro desde</span><span className="font-semibold text-[#111828]">{userData?.createdAt ? new Date(userData.createdAt).toLocaleDateString('pt-PT') : '—'}</span></p>
          </div>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-5 mb-4">
          <h3 className="font-bold text-[#111828] mb-2 text-sm">Privacidade e dados</h3>
          <p className="text-xs text-gray-500 leading-relaxed mb-3">
            Podes consultar como tratamos os teus dados nos Termos e na Política de Privacidade, no fundo da página.
            Para exercer os teus direitos de acesso, correção ou portabilidade dos dados, contacta-nos por email.
          </p>
          <a href="mailto:greenstep@gmail.com?subject=Pedido%20sobre%20dados%20pessoais"
            className="inline-flex items-center gap-2 text-sm font-bold text-[#714c62] bg-[#f1e9ee] px-4 py-2.5 rounded-2xl hover:bg-[#e6dae2] transition">
            <Mail className="w-4 h-4" /> Contactar sobre os meus dados
          </a>
        </div>

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-5 border border-[#f5dcdc]">
          <h3 className="font-bold text-[#8d3b3b] mb-2 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Zona perigosa</h3>
          <p className="text-xs text-gray-500 leading-relaxed mb-3">
            Apagar a conta elimina permanentemente o teu perfil, Eco-pontos, histórico, inscrições e recompensas.
            Esta ação não pode ser revertida.
          </p>
          <button onClick={handleDeleteAccount} disabled={deletingAccount}
            className="w-full bg-[#8d3b3b] text-white py-3 font-bold rounded-2xl hover:bg-[#7a3232] transition disabled:opacity-50 flex items-center justify-center gap-2">
            <Trash2 className="w-4 h-4" /> {deletingAccount ? 'A apagar…' : 'Apagar a minha conta'}
          </button>
        </div>
      </div>
    </div>
  );

  const RewardsSubPage = () => (
    <div className="pt-20 pb-0">
      <div className="px-4 pb-28 max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => setShowRewardsPage(false)} className="w-10 h-10 rounded-full bg-white shadow-[0_2px_8px_rgba(17,24,40,0.08)] flex items-center justify-center">
            <ChevronLeft className="w-5 h-5 text-[#111828]" />
          </button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#9b8b94]">Premium</p>
            <h2 className="text-2xl font-extrabold text-[#111828]">Minhas Recompensas</h2>
          </div>
        </div>

        {userRewardsData.length === 0 ? (
          <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-8 text-center">
            <Award className="w-12 h-12 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-400 text-sm">Nenhuma recompensa recebida ainda.</p>
            <p className="text-xs text-gray-300 mt-2">Complete desafios para ganhar recompensas!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {userRewardsData.map(reward => (
              <div key={reward.id} className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] overflow-hidden flex">
                {reward.rewardImage && (
                  <img src={reward.rewardImage} alt={reward.rewardName} className="w-24 object-cover" />
                )}
                <div className="p-4 flex-1">
                  <h3 className="font-bold text-[#111828]">{reward.rewardName}</h3>
                  <p className="text-sm text-gray-500">Desafio: {reward.challengeName}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Recebida em {new Date(reward.earnedAt).toLocaleDateString('pt-PT')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>    </div>
  );

  const openRanking = async () => {
    setShowRankingPage(true);
    setLeaderboardLoading(true);
    setLeaderboardError(null);
    try {
      const lb = await getLeaderboard(50);
      setLeaderboardData(lb);
    } catch (e) {
      console.error('Erro ao carregar ranking:', e);
      setLeaderboardData([]);
      setLeaderboardError(e?.code || e?.message || 'erro desconhecido');
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const RankingSubPage = () => {
    // 'all' = todas as atividades; caso contrário, o id da categoria
    const sortKey = rankingSort;
    const currentCat = sortKey === 'all' ? null : categories.find((c) => c.id === sortKey);
    const countFor = (u) => (sortKey === 'all' ? (u.activities || 0) : ((u.categoryCounts || {})[sortKey] || 0));
    const sorted = [...leaderboardData]
      .sort((a, b) => countFor(b) - countFor(a) || (a.name || '').localeCompare(b.name || ''))
      .slice(0, 50);

    return (
    <div className="pt-20 pb-0">
      <div className="px-4 pb-28 max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => setShowRankingPage(false)} className="w-10 h-10 rounded-full bg-white shadow-[0_2px_8px_rgba(17,24,40,0.08)] flex items-center justify-center">
            <ChevronLeft className="w-5 h-5 text-[#111828]" />
          </button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#9b8b94]">Comunidade</p>
            <h2 className="text-2xl font-extrabold text-[#111828] flex items-center gap-2">Ranking <Trophy className="w-6 h-6 text-[#e8c76a]" /></h2>
            <p className="text-xs text-gray-400">{currentCat ? currentCat.name : 'Todas as atividades'}</p>
          </div>
        </div>

        {/* Seletor de ordenação */}
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1 -mx-4 px-4">
          <button onClick={() => setRankingSort('all')}
            className={`whitespace-nowrap text-sm font-bold px-4 py-2 rounded-full flex-shrink-0 transition ${sortKey === 'all' ? 'bg-[#111828] text-white' : 'bg-white text-gray-500 shadow-[0_2px_8px_rgba(17,24,40,0.05)]'}`}>
            Todas
          </button>
          {categories.map((cat) => (
            <button key={cat.id} onClick={() => setRankingSort(cat.id)}
              className={`whitespace-nowrap text-sm font-semibold px-4 py-2 rounded-full flex-shrink-0 transition ${sortKey === cat.id ? 'bg-[#111828] text-white' : 'bg-white text-gray-500 shadow-[0_2px_8px_rgba(17,24,40,0.05)]'}`}>
              {cat.name}
            </button>
          ))}
        </div>

        {leaderboardLoading ? (
          <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-8 text-center text-gray-400 text-sm">A carregar ranking…</div>
        ) : leaderboardData.length === 0 ? (
          <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-8 text-center">
            <Trophy className="w-12 h-12 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-400 text-sm">Ainda não há classificação.</p>
            {leaderboardError && (
              <div className="mt-4 bg-[#faeaea] rounded-2xl p-3.5 text-left">
                <p className="text-[11px] font-bold text-[#8d3b3b] mb-1 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" /> Não foi possível carregar</p>
                <p className="text-[11px] text-[#8d3b3b] font-mono break-all">{leaderboardError}</p>
                {String(leaderboardError).includes('permission') && (
                  <p className="text-[11px] text-[#8d3b3b] mt-1.5">Falta autorizar a leitura da coleção <span className="font-mono">leaderboard</span> nas regras do Firestore.</p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {sorted.map((u, i) => {
              const isMe = currentUser && u.id === currentUser.uid;
              const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null;
              return (
                <div key={u.id} className={`flex items-center gap-3 rounded-[22px] p-3.5 shadow-[0_3px_12px_rgba(17,24,40,0.05)] ${isMe ? 'bg-[#111828] text-white' : 'bg-white'}`}>
                  <span className={`w-9 text-center font-extrabold ${isMe ? 'text-white' : 'text-[#714c62]'}`}>{medal || `#${i + 1}`}</span>
                  {u.photoURL ? (
                    <img src={u.photoURL} alt={u.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${isMe ? 'bg-white/15 text-white' : 'bg-[#f1e9ee] text-[#714c62]'}`}>{u.name?.charAt(0) || 'U'}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold truncate ${isMe ? 'text-white' : 'text-[#111828]'}`}>{u.name || 'Utilizador'}{isMe ? ' (tu)' : ''}</p>
                    <p className={`text-xs ${isMe ? 'text-[#b3a5ad]' : 'text-gray-400'}`}>
                      {u.level || 'Iniciante'}{currentCat ? ` · ${u.activities || 0} no total` : ''}
                    </p>
                  </div>
                  <span className={`font-extrabold text-sm whitespace-nowrap flex flex-col items-end ${isMe ? 'text-[#e8c76a]' : 'text-[#714c62]'}`}>
                    <span className="flex items-center gap-1"><Activity className="w-4 h-4" /> {countFor(u)}</span>
                    <span className={`text-[10px] font-semibold ${isMe ? 'text-[#b3a5ad]' : 'text-gray-400'}`}>atividades</span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
    );
  };

  const AchievementsSubPage = () => {
    const visibleAchievements = (achievements || []).filter((a) => a.active !== false);
    const unlockedCount = visibleAchievements.filter((a) => isAchievementUnlocked(a, userData)).length;
    return (
      <div className="pt-20 pb-0">
        <div className="px-4 pb-28 max-w-lg mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <button onClick={() => setShowAchievementsPage(false)} className="w-10 h-10 rounded-full bg-white shadow-[0_2px_8px_rgba(17,24,40,0.08)] flex items-center justify-center">
              <ChevronLeft className="w-5 h-5 text-[#111828]" />
            </button>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#9b8b94]">Perfil</p>
              <h2 className="text-2xl font-extrabold text-[#111828] flex items-center gap-2">Conquistas <Trophy className="w-6 h-6 text-[#e8c76a]" /></h2>
            </div>
          </div>

          <div className="bg-[#111828] rounded-3xl p-5 mb-4 text-white shadow-[0_10px_26px_rgba(17,24,40,0.28)]">
            <p className="text-sm text-[#b3a5ad]">Desbloqueadas</p>
            <p className="text-3xl font-extrabold">{unlockedCount} <span className="text-lg text-[#b3a5ad]">/ {visibleAchievements.length}</span></p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {visibleAchievements.map(a => {
              const unlocked = isAchievementUnlocked(a, userData);
              return (
                <div key={a.id} className={`rounded-[22px] p-4 text-center shadow-[0_3px_12px_rgba(17,24,40,0.05)] ${unlocked ? 'bg-white' : 'bg-[#f2eef1] opacity-70'}`}>
                  <div className={`text-4xl mb-2 ${unlocked ? '' : 'grayscale opacity-50'}`}>{a.emoji}</div>
                  <p className="font-bold text-sm text-[#111828]">{a.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{a.description || a.desc}</p>
                  {unlocked ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#3d7a52] bg-[#e6f2ea] px-2 py-0.5 rounded-full mt-2"><CheckCircle className="w-3 h-3" /> Desbloqueada</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-400 bg-[#eae4e8] px-2 py-0.5 rounded-full mt-2"><LockIcon className="w-3 h-3" /> Bloqueada</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const MaisPage = () => {
    if (showProfileEditPage) return <ProfileEditSubPage />;
    if (showRewardsPage) return <RewardsSubPage />;
    if (showRankingPage) return <RankingSubPage />;
    if (showAchievementsPage) return <AchievementsSubPage />;
    if (showSettingsPage) return <SettingsSubPage />;
    if (showGoalPage) return <GoalSubPage />;

    return (
    <div className="pt-20 pb-0">
      <div className="px-4 pb-28 max-w-lg mx-auto">
        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-5 mb-4">
          <div className="flex items-center gap-4">
            {userData?.photoURL ? (
              <img
                src={userData.photoURL}
                alt="Foto de perfil"
                className="w-16 h-16 rounded-full object-cover ring-4 ring-[#f1e9ee]"
              />
            ) : (
              <div className="w-16 h-16 bg-[#714c62] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {userData?.name?.charAt(0) || 'U'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-extrabold text-[#111828]">{userData?.name || 'Utilizador'}</h3>
              <p className="text-gray-500 text-xs break-all">{userData?.email || currentUser?.email}</p>
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className="px-2.5 py-1 bg-[#f1e9ee] text-[#714c62] text-[10px] font-bold rounded-full">
                  {userData?.level || 'Iniciante'}
                </span>
                {userData?.isPremium && (
                  <span className="px-2.5 py-1 bg-[#fbf3dd] text-[#8a6d1c] text-[10px] font-bold rounded-full flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Premium
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {showInstallButton && (
          <div className="bg-[#111828] rounded-3xl shadow-[0_10px_26px_rgba(17,24,40,0.28)] p-5 mb-4 text-white">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
                <Leaf className="w-8 h-8 text-[#a97f96]" />
              </div>
              <div className="flex-1">
                <h3 className="font-extrabold mb-0.5">Instalar GreenStep</h3>
                <p className="text-[#b3a5ad] text-xs mb-3">Aceda mais rápido à app direto do seu ecrã inicial!</p>
                <button
                  onClick={handleInstallPWA}
                  className="bg-white text-[#111828] px-5 py-2 text-sm font-bold rounded-full hover:bg-[#f1e9ee] transition"
                >
                  <span className="flex items-center gap-2"><Download className="w-4 h-4" /> Instalar Agora</span>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)] p-1.5 mb-4">
          {[
            { icon: User, label: 'Perfil', action: handleOpenProfileEdit },
            { icon: Target, label: 'Meta pessoal', action: () => setShowGoalPage(true) },
            { icon: BarChart3, label: 'Ranking', action: openRanking },
            { icon: Trophy, label: 'Conquistas', action: () => setShowAchievementsPage(true) },
            ...(userData?.isPremium ? [
              { icon: Award, label: 'Recompensas', action: () => setShowRewardsPage(true) },
              userData?.cancelAtPeriodEnd
                ? { icon: Clock, label: `Premium ativo até ${userData?.premiumUntil ? new Date(userData.premiumUntil).toLocaleDateString('pt-PT') : 'ao fim do período pago'}`, action: () => alert(`A tua subscrição foi cancelada e não voltas a ser cobrado.\n\nManténs o acesso Premium até ${userData?.premiumUntil ? new Date(userData.premiumUntil).toLocaleDateString('pt-PT') : 'ao fim do período já pago'}.`) }
                : { icon: XCircle, label: 'Cancelar Subscrição Premium', action: handleCancelPremium, danger: true }
            ] : []),
            { icon: Settings, label: 'Definições', action: () => setShowSettingsPage(true) },
            { icon: LogOut, label: 'Sair', action: handleLogout, danger: true }
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <button
                key={i}
                onClick={item.action}
                className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[#f7f3f5] transition text-left"
              >
                <span className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${item.danger ? 'bg-[#faeaea]' : 'bg-[#f1e9ee]'}`}>
                  <Icon className={`w-5 h-5 ${item.danger ? 'text-[#8d3b3b]' : 'text-[#714c62]'}`} />
                </span>
                <span className={`text-sm font-bold ${item.danger ? 'text-[#8d3b3b]' : 'text-[#111828]'}`}>{item.label}</span>
              </button>
            );
          })}
        </div>

      </div>    </div>
    );
  };

  const ActivityDetailModal = () => {
    const activity = detailActivity;
    if (!activity) return null;
    const reg = userRegistrations.find((r) => r.activityId === activity.id);
    const isApproved = reg && (reg.status === 'approved' || reg.completed);
    const isOngoing = !!activity.ongoing;
    const interactive = isOngoing && isApproved && !reg.completed;
    const completedTasks = reg?.completedTasks || [];
    const tasks = (activity.tasks || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    const hasCoords = activity.lat != null && activity.lng != null;

    return (
      <div className="fixed inset-0 bg-[#111828]/50 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setDetailActivity(null)}>
        <div className="bg-white rounded-t-[28px] sm:rounded-[28px] w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          {activity.image && <img src={activity.image} alt={activity.name} className="w-full h-44 object-cover" />}
          <div className="p-5">
            <div className="flex items-start justify-between gap-3 mb-1">
              <div>
                <h2 className="text-xl font-extrabold text-[#111828]">{activity.name}</h2>
                <span className="text-[11px] font-mono font-bold text-gray-500 bg-[#f2eef1] px-2 py-0.5 rounded-full inline-block mt-1">{activityCode(activity)}</span>
              </div>
              <button onClick={() => setDetailActivity(null)} className="w-9 h-9 rounded-full bg-[#f7f3f5] flex items-center justify-center text-gray-400 flex-shrink-0"><X className="w-5 h-5" /></button>
            </div>
            {activity.zone && <p className="text-sm text-[#714c62] font-semibold flex items-center gap-1 mb-2"><MapPin className="w-4 h-4" /> {activity.zone}</p>}
            {activity.description && <p className="text-sm text-gray-500 leading-relaxed mb-3">{activity.description}</p>}

            {hasCoords && (
              <div className="bg-[#f7f3f5] rounded-2xl p-3.5 mb-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-bold text-[#9b8b94] uppercase">Coordenadas exatas</p>
                  <p className="text-sm font-mono text-[#111828] break-all">{activity.lat}, {activity.lng}</p>
                </div>
                <a href={`https://www.google.com/maps/search/?api=1&query=${activity.lat},${activity.lng}`} target="_blank" rel="noopener noreferrer"
                  className="text-xs font-bold text-white bg-[#714c62] px-3 py-2 rounded-full whitespace-nowrap flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Abrir no mapa</a>
              </div>
            )}

            <div className="flex items-center justify-between mb-2">
              <h3 className="font-extrabold text-[#111828]">Tarefas {tasks.length > 0 && <span className="text-gray-400 font-bold">({completedTasks.length}/{tasks.length})</span>}</h3>
              {isOngoing && <span className="text-[10px] font-bold text-[#3d7a52] bg-[#e6f2ea] px-2.5 py-1 rounded-full">A decorrer</span>}
            </div>

            {tasks.length === 0 ? (
              <p className="text-sm text-gray-400 py-2">Esta atividade não tem tarefas.</p>
            ) : (
              <div className="space-y-3">
                {!interactive && (
                  <p className="text-xs text-gray-400 mb-1">
                    {isApproved ? 'As tarefas ficam ativas quando o admin iniciar a atividade.' : 'Inscreve-te e aguarda aprovação para poder marcar as tarefas.'}
                  </p>
                )}
                {tasks.map((task, idx) => {
                  const done = completedTasks.includes(task.id);
                  const firstPt = task.route && task.route.length > 0 ? task.route[0] : null;
                  return (
                    <div key={task.id} className={`rounded-2xl p-3.5 border ${done ? 'bg-[#e6f2ea] border-[#bfe0cb]' : 'bg-[#f7f3f5] border-transparent'}`}>
                      <div className="flex items-start gap-3">
                        <button
                          disabled={!interactive}
                          onClick={() => interactive && handleToggleTask(reg, task.id)}
                          className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${done ? 'bg-[#3d7a52] text-white' : 'bg-white border border-[#d9d0d6]'} ${interactive ? '' : 'opacity-60 cursor-not-allowed'}`}
                        >
                          {done && <CheckCircle className="w-4 h-4" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className={`font-bold text-[#111828] ${done ? 'line-through opacity-70' : ''}`}>{idx + 1}. {task.name}</p>
                          {task.description && <p className="text-sm text-gray-500">{task.description}</p>}
                          {firstPt && (
                            <>
                              <TrailMap route={task.route} />
                              <a href={`https://www.google.com/maps/search/?api=1&query=${firstPt.lat},${firstPt.lng}`} target="_blank" rel="noopener noreferrer"
                                className="text-xs font-bold text-[#714c62] inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Abrir no Google Maps</a>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const RedeemModal = () => {
    const offer = redeemOfferData;
    if (!offer) return null;
    const defaultInstructions = 'Após confirmares o resgate, os teus Eco-pontos serão descontados e o pedido é enviado à nossa equipa. Vais receber os dados da recompensa (código/voucher e instruções de utilização) no teu email, normalmente em 24–48h.';
    return (
      <div className="fixed inset-0 bg-[#111828]/50 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => !redeemLoading && setRedeemOfferData(null)}>
        <div className="bg-white rounded-t-[28px] sm:rounded-[28px] w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          {offer.image && <img src={offer.image} alt={offer.title} className="w-full h-40 object-cover" />}
          <div className="p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h2 className="text-xl font-extrabold text-[#111828]">{offer.title}</h2>
                <span className="inline-flex items-center gap-1 text-[#714c62] font-bold text-xs bg-[#f1e9ee] px-3 py-1 rounded-full mt-1"><Leaf className="w-3.5 h-3.5" /> {offer.points} Eco-pontos</span>
              </div>
              <button onClick={() => setRedeemOfferData(null)} disabled={redeemLoading} className="w-9 h-9 rounded-full bg-[#f7f3f5] flex items-center justify-center text-gray-400 flex-shrink-0"><X className="w-5 h-5" /></button>
            </div>

            <div className="bg-[#f7f3f5] rounded-2xl p-4 mb-4">
              <h3 className="text-sm font-extrabold text-[#111828] mb-1.5 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-[#714c62]" /> Manual de instruções</h3>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{offer.instructions || defaultInstructions}</p>
            </div>

            <div className="bg-[#fbf3dd] rounded-2xl p-4 mb-4">
              <h3 className="text-sm font-extrabold text-[#8a6d1c] mb-1.5">Termos e Condições</h3>
              <p className="text-xs text-[#8a6d1c] leading-relaxed">
                Ao resgatar, os Eco-pontos são descontados e não são reembolsáveis. A recompensa é pessoal e intransmissível e está sujeita à disponibilidade do parceiro. Os dados serão enviados para o email associado à tua conta. A validade e as condições de utilização são as indicadas no manual acima.
              </p>
            </div>

            <label className="flex items-start gap-2.5 cursor-pointer mb-4">
              <input type="checkbox" checked={redeemTermsAccepted} onChange={(e) => setRedeemTermsAccepted(e.target.checked)} className="w-5 h-5 accent-[#714c62] mt-0.5" />
              <span className="text-sm text-[#111828] font-semibold">Li e aceito o manual de instruções e os termos e condições.</span>
            </label>

            <div className="flex gap-2.5">
              <button onClick={() => setRedeemOfferData(null)} disabled={redeemLoading} className="flex-1 px-4 py-3 text-sm bg-[#f7f3f5] text-[#111828] font-bold rounded-2xl hover:bg-[#efe9ec] transition disabled:opacity-50">Cancelar</button>
              <button onClick={handleConfirmRedeem} disabled={!redeemTermsAccepted || redeemLoading} className="flex-[1.4] px-4 py-3 text-sm bg-[#714c62] text-white font-bold rounded-2xl shadow-[0_5px_14px_rgba(113,76,98,0.35)] hover:bg-[#5c3d50] transition disabled:opacity-50 flex items-center justify-center gap-2">
                {redeemLoading ? 'A processar…' : <><Gift className="w-4 h-4" /> Confirmar resgate</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'novidades': return <NovidadesPage />;
      case 'ofertas': return <OfertasPage />;
      case 'atividades': return <AtividadesPage />;
      case 'premium': return <PremiumPage />;
      case 'mais': return <MaisPage />;
      default: return <NovidadesPage />;
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f3f5]">
      {showUpdatePrompt && (
        <div className="fixed top-4 left-4 right-4 z-[60] bg-[#111828] text-white p-4 rounded-3xl shadow-[0_12px_30px_rgba(17,24,40,0.4)] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#714c62] rounded-full flex items-center justify-center">
              <Star className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold text-sm">Nova versão disponível!</p>
              <p className="text-xs text-[#b3a5ad]">Atualize para ver as novidades</p>
            </div>
          </div>
          <button
            onClick={handleUpdate}
            className="bg-white text-[#111828] px-4 py-2 text-sm font-bold rounded-full hover:bg-[#f1e9ee] transition"
          >
            Atualizar
          </button>
        </div>
      )}

      <Header />
      <div key={currentPage} className="gs-page">
        {renderPage()}
      </div>
      <Footer onNavigate={handleFooterNavigate} isAdmin={isAdmin} />

      <BottomNav />
      <RegistrationModal
        show={showRegistrationModal}
        activity={selectedActivity}
        onClose={() => setShowRegistrationModal(false)}
        onSubmit={handleSubmitRegistration}
        loading={loading}
      />

      <PhotoUploadModal
        show={showPhotoUploadModal}
        activity={selectedActivity}
        registration={selectedRegistrationForPhoto}
        onClose={() => setShowPhotoUploadModal(false)}
        onSubmit={handleSubmitPhoto}
        loading={loading}
      />

      {detailActivity && <ActivityDetailModal />}
      {redeemOfferData && <RedeemModal />}
    </div>
  );
};

// ============================================
// ADMIN DASHBOARD v2 (estilo 1c)
// ============================================

const AdminDashboard = ({ userData, allUsers, news, offers, activities, categories, adminStats, allRegistrations, pendingCount, handleLogout, openModal, handleDeleteUser, handleDeleteNews, handleDeleteOffer, handleDeleteActivity, showModal, closeModal, modalType, modalMode, editingItem, loadData, challenges, rewardCatalog, allChallengeRegistrations, redemptions, achievements, appSettings, handleFooterNavigate, handleDeleteReward, handleDeleteChallenge, handleSaveChallenge, handleApproveChallengePhoto, handleRejectChallengePhoto }) => {
  const [currentAdminPage, setCurrentAdminPage] = useState('dashboard');
  const [selectedActivityForRegs, setSelectedActivityForRegs] = useState(null);
  const [activityRegistrations, setActivityRegistrations] = useState([]);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [selectedChallengeForRegs, setSelectedChallengeForRegs] = useState(null);
  const [selectedActiveActivity, setSelectedActiveActivity] = useState(null);
  const [myTab, setMyTab] = useState('registrations'); // 'registrations' | 'activities'

  const handleApproveRegistration = async (registration) => {
    if (!window.confirm(`Aprovar inscrição de ${registration.userName}?`)) return;
    try {
      const adminUid = auth.currentUser?.uid;
      if (!adminUid) { alert('❌ Erro: Usuário não autenticado!'); return; }
      await approveRegistration(registration.id, adminUid, registration.activityId);
      alert('✅ Inscrição aprovada!');
      await loadData();
      if (selectedActivityForRegs) {
        const regs = await getRegistrationsByActivity(selectedActivityForRegs.id);
        setActivityRegistrations(regs);
      }
      setSelectedRegistration(null);
    } catch (error) {
      console.error('❌ Erro ao aprovar inscrição!');
      alert('❌ Erro ao aprovar inscrição!');
    }
  };

  const handleRejectRegistration = async (registration) => {
    if (!window.confirm(`Rejeitar inscrição de ${registration.userName}?`)) return;
    try {
      const adminUid = auth.currentUser?.uid;
      if (!adminUid) { alert('❌ Erro: Usuário não autenticado!'); return; }
      await rejectRegistration(registration.id, adminUid);
      alert('❌ Inscrição rejeitada!');
      await loadData();
      if (selectedActivityForRegs) {
        const regs = await getRegistrationsByActivity(selectedActivityForRegs.id);
        setActivityRegistrations(regs);
      }
      setSelectedRegistration(null);
    } catch (error) {
      console.error('❌ Erro ao rejeitar inscrição!');
      alert('❌ Erro ao rejeitar inscrição!');
    }
  };

  // ---------- Blocos de estilo partilhados (1c) ----------
  const card = 'bg-white rounded-[22px] shadow-[0_3px_12px_rgba(17,24,40,0.05)]';
  const btnPrimary = 'bg-[#714c62] text-white font-bold rounded-2xl shadow-[0_5px_14px_rgba(113,76,98,0.35)] hover:bg-[#5c3d50] transition disabled:opacity-50';
  const btnSoft = 'bg-[#f1e9ee] text-[#714c62] font-bold rounded-2xl hover:bg-[#e6dae2] transition';
  const btnGhost = 'bg-[#f7f3f5] text-[#111828] font-bold rounded-2xl hover:bg-[#efe9ec] transition';
  const btnDanger = 'bg-[#faeaea] text-[#8d3b3b] font-bold rounded-2xl hover:bg-[#f5dcdc] transition';
  const chipOn = 'bg-[#111828] text-white text-sm font-bold px-4 py-2 rounded-full whitespace-nowrap';
  const chipOff = 'bg-white text-gray-500 text-sm font-semibold px-4 py-2 rounded-full shadow-[0_2px_8px_rgba(17,24,40,0.05)] whitespace-nowrap';
  const inputCls = 'w-full px-4 py-3 rounded-2xl border border-[#e8e2e6] bg-white focus:ring-2 focus:ring-[#714c62] focus:outline-none';
  const statusPill = (status) =>
    status === 'pending' ? 'bg-[#fdf2e3] text-[#b45309]' :
    status === 'approved' ? 'bg-[#e6f2ea] text-[#3d7a52]' :
    'bg-[#faeaea] text-[#8d3b3b]';
  const statusLabel = (status) =>
    status === 'pending' ? 'Pendente' : status === 'approved' ? 'Aprovado' : 'Rejeitado';

  // Cabeçalho de página (eyebrow + título grande, sem header fixo)
  const PageHeader = ({ eyebrow, title, right, onBack }) => (
    <div className="flex items-center gap-3 mb-5">
      {onBack && (
        <button onClick={onBack} className="w-10 h-10 rounded-full bg-white shadow-[0_2px_8px_rgba(17,24,40,0.08)] flex items-center justify-center flex-shrink-0">
          <ChevronLeft className="w-5 h-5 text-[#111828]" />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#9b8b94]">{eyebrow}</p>
        <h2 className="text-2xl font-extrabold text-[#111828] leading-tight">{title}</h2>
      </div>
      {right}
    </div>
  );

  // Botão flutuante "+"
  const Fab = ({ onClick }) => (
    <button onClick={onClick}
      className="fixed right-5 bottom-24 z-40 w-14 h-14 rounded-full bg-[#714c62] text-white flex items-center justify-center shadow-[0_10px_24px_rgba(113,76,98,0.45)] hover:bg-[#5c3d50] transition">
      <Plus className="w-7 h-7" />
    </button>
  );

  // ---------- Navegação inferior: 4 itens + «Mais» ----------
  const morePageIds = ['more', 'news', 'offers', 'rewards', 'premium', 'redemptions', 'achievements', 'active', 'archived'];
  const AdminNav = () => {
    const navItems = [
      { id: 'dashboard', icon: BarChart3, label: 'Painel' },
      { id: 'mine', icon: UserCheck, label: 'A meu cargo', badge: (allRegistrations || []).filter((r) => r.status === 'pending' && (activities || []).some((a) => a.id === r.activityId && a.responsibleAdminId === auth.currentUser?.uid)).length },
      { id: 'users', icon: Users, label: 'Utilizadores' },
      { id: 'activities', icon: Activity, label: 'Atividades' },
      { id: 'more', icon: Menu, label: 'Mais' }
    ];
    return (
      <nav className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[28px] shadow-[0_-6px_20px_rgba(17,24,40,0.07)] z-50">
        <div className="flex justify-around items-center px-2 pt-3 pb-5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.id === 'more' ? morePageIds.includes(currentAdminPage) : currentAdminPage === item.id;
            return (
              <button key={item.id}
                onClick={() => { setCurrentAdminPage(item.id); setSelectedRegistration(null); setSelectedActivityForRegs(null); setSelectedChallengeForRegs(null); setSelectedActiveActivity(null); }}
                className="relative flex items-center transition">
                {isActive ? (
                  <span key="on" className="gs-pop flex items-center gap-2 bg-[#111828] text-white rounded-full px-4 py-2">
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-bold">{item.label}</span>
                  </span>
                ) : (
                  <span key="off" className="p-2 text-[#b7aeb4]"><Icon className="w-6 h-6" /></span>
                )}
                {item.badge > 0 && !isActive && (
                  <span className="absolute -top-1 -right-1 bg-[#714c62] text-white text-[10px] font-bold min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center">
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    );
  };

  // ---------- DASHBOARD ----------
  const DashboardPage = () => {
    // Barras: atividades concluídas nos últimos 6 meses
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        label: d.toLocaleDateString('pt-PT', { month: 'short' }).replace('.', ''),
        y: d.getFullYear(), m: d.getMonth(), count: 0
      });
    }
    (allRegistrations || []).forEach(r => {
      if (r.completed && r.completedAt) {
        const d = new Date(r.completedAt);
        const slot = months.find(mo => mo.y === d.getFullYear() && mo.m === d.getMonth());
        if (slot) slot.count++;
      }
    });
    const maxCount = Math.max(1, ...months.map(mo => mo.count));
    const pendingPhotos = (allRegistrations || []).filter(r => r.photoStatus === 'pending').length;

    return (
      <div className="pt-6 pb-28 px-4 max-w-lg mx-auto">
        <PageHeader eyebrow="Painel Admin" title="Visão geral"
          right={
            <div className="w-11 h-11 rounded-full bg-[#714c62] text-white font-bold flex items-center justify-center shadow-[0_6px_14px_rgba(113,76,98,0.35)]">
              {(userData?.name || 'A').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}
            </div>
          } />

        {/* Cartão escuro com gráfico */}
        <div className="bg-[#111828] rounded-3xl p-5 text-white shadow-[0_10px_26px_rgba(17,24,40,0.28)] mb-4">
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm font-bold text-[#d9c7d2]">Atividades concluídas</p>
            <span className="text-[11px] font-bold text-[#111828] bg-white px-2.5 py-1 rounded-full">últimos 6 meses</span>
          </div>
          <p className="text-4xl font-extrabold mb-3">{adminStats?.totalCompletedActivities || 0}</p>
          <div className="flex items-end gap-2 h-16">
            {months.map((mo, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className={`w-full rounded-md ${i === months.length - 1 ? 'bg-[#a97f96]' : i === months.length - 2 ? 'bg-[#714c62]' : 'bg-white/20'}`}
                  style={{ height: `${Math.max(8, (mo.count / maxCount) * 56)}px` }} />
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-1">
            {months.map((mo, i) => (
              <div key={i} className="flex-1 text-center text-[10px] text-[#8e7f88] capitalize">{mo.label}</div>
            ))}
          </div>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className={`${card} p-4`}>
            <p className="text-2xl font-extrabold text-[#111828]">{adminStats?.totalUsers || 0}</p>
            <p className="text-xs font-semibold text-gray-500">Utilizadores</p>
          </div>
          <div className={`${card} p-4`}>
            <p className="text-2xl font-extrabold text-[#714c62]">{pendingCount}</p>
            <p className="text-xs font-semibold text-gray-500">Pendentes</p>
            {pendingCount > 0 && <p className="text-[11px] font-bold text-[#b45309] mt-1">requer ação</p>}
          </div>
          <div className={`${card} p-4`}>
            <p className="text-2xl font-extrabold text-[#111828]">{adminStats?.totalActivities || 0}</p>
            <p className="text-xs font-semibold text-gray-500">Atividades</p>
          </div>
          <div className={`${card} p-4`}>
            <p className="text-2xl font-extrabold text-[#111828]">{adminStats?.premiumUsers || 0}</p>
            <p className="text-xs font-semibold text-gray-500">Premium</p>
          </div>
        </div>

        {/* Ações pendentes */}
        <div className={`${card} p-1.5`}>
          <button onClick={() => setCurrentAdminPage('registrations')} className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[#f7f3f5] transition text-left">
            <span className="w-10 h-10 rounded-full bg-[#f1e9ee] flex items-center justify-center flex-shrink-0"><UserCheck className="w-5 h-5 text-[#714c62]" /></span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-bold text-[#111828]">Inscrições</span>
              <span className="block text-xs text-gray-500">{pendingCount} por aprovar</span>
            </span>
            <span className="text-xs font-extrabold text-[#714c62]">Rever →</span>
          </button>
          <button onClick={() => setCurrentAdminPage('registrations')} className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[#f7f3f5] transition text-left">
            <span className="w-10 h-10 rounded-full bg-[#f1e9ee] flex items-center justify-center flex-shrink-0"><Camera className="w-5 h-5 text-[#714c62]" /></span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-bold text-[#111828]">Fotos de atividades</span>
              <span className="block text-xs text-gray-500">{pendingPhotos} por validar</span>
            </span>
            <span className="text-xs font-extrabold text-[#714c62]">Rever →</span>
          </button>
        </div>
      </div>
    );
  };

  // ---------- MENU «MAIS» ----------
  const MorePage = () => {
    const [seeding, setSeeding] = useState(false);
    const [seedingUsers, setSeedingUsers] = useState(false);
    const { unlocked: devUnlocked, remaining: devRemaining } = useDevUnlock();
    const TOOLS_LOCKED = !devUnlocked;
    const [goalPoints, setGoalPoints] = useState(appSettings?.goalRewardPoints ?? 0);
    const [savingSettings, setSavingSettings] = useState(false);

    const handleSaveGoalPoints = async () => {
      setSavingSettings(true);
      try {
        await updateAppSettings({ goalRewardPoints: Math.max(0, parseInt(goalPoints) || 0) });
        await loadData();
        alert('✅ Guardado! Os utilizadores passam a ganhar estes Eco-pontos ao cumprir a meta mensal.');
      } catch (e) {
        console.error('Erro ao guardar definições:', e);
        alert('❌ Erro ao guardar: ' + (e?.message || e));
      } finally { setSavingSettings(false); }
    };

    const handleSeedActivities = async () => {
      if (!window.confirm('⚠️ Isto APAGA todas as atividades atuais e recria a lista completa (com zona, coordenadas e tarefas).\n\nAs inscrições existentes deixam de estar ligadas a uma atividade. Continuar?')) return;
      setSeeding(true);
      try {
        const { deleted, added, total } = await reseedActivities();
        await loadData();
        alert(`✅ Concluído!\n\nApagadas: ${deleted}\nCriadas: ${added}\nTotal na lista: ${total}`);
      } catch (error) {
        console.error('Erro ao refazer atividades:', error);
        alert('❌ Erro ao refazer atividades: ' + (error?.message || error));
      } finally {
        setSeeding(false);
      }
    };

    const handleSeedUsers = async () => {
      if (!window.confirm('Gerar 18 utilizadores de teste com dados aleatórios (pontos, atividades, etc.)?\n\nSão só para testar o ranking. Podes removê-los depois no botão abaixo.')) return;
      setSeedingUsers(true);
      try {
        const { added } = await seedRandomUsers(18);
        await loadData();
        alert(`✅ Criados ${added} utilizadores de teste! Vê o ranking em Utilizadores ou na app.`);
      } catch (error) {
        console.error('Erro ao gerar utilizadores:', error);
        alert('❌ Erro ao gerar utilizadores: ' + (error?.message || error));
      } finally {
        setSeedingUsers(false);
      }
    };

    const handleWipeAll = async () => {
      if (!window.confirm('⚠️ APAGAR TODOS OS DADOS DE TESTE\n\nIsto apaga: atividades, inscrições, histórico, resgates, recompensas ganhas e o ranking.\nApaga também os utilizadores de teste e repõe a ZERO os pontos e atividades dos utilizadores reais.\n\nNÃO apaga: admins, notícias, ofertas, desafios, catálogo e conquistas.\n\nEsta ação é irreversível. Continuar?')) return;
      if (!window.confirm('Tens mesmo a certeza? Não há forma de recuperar estes dados.')) return;
      setSeedingUsers(true);
      try {
        const r = await wipeAllData();
        await loadData();
        const linhas = Object.entries(r.deleted).map(([k, v]) => `• ${k}: ${v}`).join('\n');
        alert(`✅ Limpeza concluída!\n\n${linhas}\n• utilizadores de teste apagados: ${r.seededRemoved}\n• utilizadores repostos a zero: ${r.usersReset}`);
      } catch (error) {
        console.error('Erro na limpeza:', error);
        alert('❌ Erro na limpeza: ' + (error?.message || error));
      } finally { setSeedingUsers(false); }
    };

    const handleRebuildLeaderboard = async () => {
      setSeedingUsers(true);
      try {
        const { synced } = await rebuildLeaderboard();
        alert(`✅ Ranking sincronizado (${synced} utilizadores).`);
      } catch (error) {
        console.error('Erro ao sincronizar ranking:', error);
        alert('❌ Erro ao sincronizar ranking: ' + (error?.message || error));
      } finally {
        setSeedingUsers(false);
      }
    };

    const handleRemoveSeededUsers = async () => {
      if (!window.confirm('Remover todos os utilizadores de teste (os gerados aleatoriamente)?')) return;
      setSeedingUsers(true);
      try {
        const { removed } = await removeSeededUsers();
        await loadData();
        alert(`✅ Removidos ${removed} utilizadores de teste.`);
      } catch (error) {
        console.error('Erro ao remover utilizadores:', error);
        alert('❌ Erro ao remover utilizadores: ' + (error?.message || error));
      } finally {
        setSeedingUsers(false);
      }
    };

    const pendingRedemptions = (redemptions || []).filter((r) => r.status !== 'sent').length;
    const tiles = [
      { id: 'news', icon: Leaf, label: 'Novidades', sub: `${news?.length || 0} publicadas` },
      { id: 'offers', icon: Gift, label: 'Ofertas', sub: `${offers?.length || 0} ativas` },
      { id: 'rewards', icon: Award, label: 'Recompensas', sub: `catálogo · ${rewardCatalog?.length || 0} itens` },
      { id: 'premium', icon: Crown, label: 'Premium', sub: `${challenges?.length || 0} desafios` },
      { id: 'redemptions', icon: Mail, label: 'Resgates', sub: pendingRedemptions > 0 ? `${pendingRedemptions} pendentes` : 'nenhum pendente' },
      { id: 'achievements', icon: Trophy, label: 'Conquistas', sub: `${achievements?.length || 0} definidas` },
      { id: 'active', icon: Activity, label: 'Atividades ativas', sub: `${(activities || []).filter((a) => a.ongoing && !a.archived).length} a decorrer` },
      { id: 'archived', icon: ClipboardList, label: 'Arquivadas', sub: `${(activities || []).filter((a) => a.archived).length} no histórico` }
    ];
    return (
      <div className="pt-6 pb-28 px-4 max-w-lg mx-auto">
        <div className={`${card} p-4 flex items-center gap-4 mb-5`}>
          <div className="w-[52px] h-[52px] rounded-full bg-[#714c62] text-white font-bold text-lg flex items-center justify-center">
            {(userData?.name || 'A').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-extrabold text-[#111828]">{userData?.name}</p>
            <p className="text-xs text-gray-500">Administrador · GreenStep</p>
          </div>
        </div>

        <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#9b8b94] mb-3">Conteúdos</p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {tiles.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setCurrentAdminPage(t.id)} className={`${card} p-4 text-left hover:shadow-lg transition`}>
                <span className="w-10 h-10 rounded-2xl bg-[#f1e9ee] flex items-center justify-center mb-2.5"><Icon className="w-5 h-5 text-[#714c62]" /></span>
                <span className="block text-sm font-bold text-[#111828]">{t.label}</span>
                <span className="block text-[11px] text-gray-500 mt-0.5">{t.sub}</span>
              </button>
            );
          })}
        </div>

        <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#9b8b94] mb-3">Metas dos utilizadores</p>
        <div className={`${card} p-5 mb-4`}>
          <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">
            Eco-pontos por meta mensal cumprida
          </label>
          <div className="flex gap-2.5">
            <input
              type="number" min="0" max="10000"
              value={goalPoints}
              onChange={(e) => setGoalPoints(e.target.value)}
              className={`${inputCls} flex-1`}
              placeholder="0"
            />
            <button onClick={handleSaveGoalPoints} disabled={savingSettings}
              className={`px-5 text-sm ${btnPrimary}`}>
              {savingSettings ? '…' : 'Guardar'}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2 leading-relaxed">
            Atribuídos automaticamente quando um utilizador cumpre a meta do mês, no momento em que a atividade é dada como concluída. Cada utilizador só recebe uma vez por mês. Coloca 0 para desativar.
          </p>
        </div>

        <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#9b8b94] mb-3 flex items-center gap-2">
          Ferramentas de Desenvolvedor
          {TOOLS_LOCKED ? (
            <span className="normal-case tracking-normal text-[10px] font-bold text-gray-400 bg-[#f2eef1] px-2 py-0.5 rounded-full flex items-center gap-1">
              <LockIcon className="w-3 h-3" /> Bloqueadas
            </span>
          ) : (
            <span className="normal-case tracking-normal text-[10px] font-bold text-[#3d7a52] bg-[#e6f2ea] px-2 py-0.5 rounded-full flex items-center gap-1">
              <Clock className="w-3 h-3" /> Ativas · {devRemaining}s
            </span>
          )}
        </p>
        <div className={`${card} p-1.5 mb-4 ${TOOLS_LOCKED ? 'opacity-30 pointer-events-none select-none' : ''}`} aria-disabled={TOOLS_LOCKED}>
          <button onClick={handleSeedActivities} disabled={TOOLS_LOCKED || seeding} className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[#f7f3f5] transition text-left disabled:opacity-50">
            <span className="w-9 h-9 rounded-full bg-[#e6f2ea] flex items-center justify-center flex-shrink-0"><Download className="w-5 h-5 text-[#3d7a52]" /></span>
            <span className="flex-1">
              <span className="block text-sm font-bold text-[#111828]">{seeding ? 'A refazer atividades…' : 'Refazer atividades (zona, coords, tarefas)'}</span>
              <span className="block text-[11px] text-gray-500">Apaga as atuais e recria a lista completa com locais e tarefas</span>
            </span>
          </button>
          <button onClick={handleSeedUsers} disabled={TOOLS_LOCKED || seedingUsers} className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[#f7f3f5] transition text-left disabled:opacity-50 border-t border-[#f2eff2]">
            <span className="w-9 h-9 rounded-full bg-[#f1e9ee] flex items-center justify-center flex-shrink-0"><Users className="w-5 h-5 text-[#714c62]" /></span>
            <span className="flex-1">
              <span className="block text-sm font-bold text-[#111828]">{seedingUsers ? 'A processar…' : 'Gerar utilizadores de teste'}</span>
              <span className="block text-[11px] text-gray-500">18 utilizadores aleatórios com pontos e atividades (testar ranking)</span>
            </span>
          </button>
          <button onClick={handleRebuildLeaderboard} disabled={TOOLS_LOCKED || seedingUsers} className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[#f7f3f5] transition text-left disabled:opacity-50 border-t border-[#f2eff2]">
            <span className="w-9 h-9 rounded-full bg-[#fbf3dd] flex items-center justify-center flex-shrink-0"><Trophy className="w-5 h-5 text-[#8a6d1c]" /></span>
            <span className="flex-1">
              <span className="block text-sm font-bold text-[#111828]">Sincronizar ranking</span>
              <span className="block text-[11px] text-gray-500">Atualiza o ranking com os dados atuais dos utilizadores</span>
            </span>
          </button>
          <button onClick={handleRemoveSeededUsers} disabled={TOOLS_LOCKED || seedingUsers} className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[#f7f3f5] transition text-left disabled:opacity-50 border-t border-[#f2eff2]">
            <span className="w-9 h-9 rounded-full bg-[#faeaea] flex items-center justify-center flex-shrink-0"><Trash2 className="w-5 h-5 text-[#8d3b3b]" /></span>
            <span className="flex-1">
              <span className="block text-sm font-bold text-[#8d3b3b]">Remover utilizadores de teste</span>
              <span className="block text-[11px] text-gray-500">Apaga apenas os utilizadores gerados aleatoriamente</span>
            </span>
          </button>
          <button onClick={handleWipeAll} disabled={TOOLS_LOCKED || seedingUsers} className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[#faeaea] transition text-left disabled:opacity-50 border-t-2 border-[#f5dcdc]">
            <span className="w-9 h-9 rounded-full bg-[#8d3b3b] flex items-center justify-center flex-shrink-0"><AlertTriangle className="w-5 h-5 text-white" /></span>
            <span className="flex-1">
              <span className="block text-sm font-extrabold text-[#8d3b3b]">Limpar TODOS os dados de teste</span>
              <span className="block text-[11px] text-gray-500">Atividades, inscrições, histórico, pontos e ranking — irreversível</span>
            </span>
          </button>
        </div>

        <div className={`${card} p-1.5`}>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-[#f7f3f5] transition text-left">
            <span className="w-9 h-9 rounded-full bg-[#f7f3f5] flex items-center justify-center flex-shrink-0"><LogOut className="w-5 h-5 text-gray-500" /></span>
            <span className="text-sm font-bold text-[#8d3b3b]">Terminar sessão</span>
          </button>
        </div>
      </div>
    );
  };

  // ---------- INSCRIÇÕES ----------
  const RegistrationsPage = ({ restrictToMine = false, hideHeader = false }) => {
    const [filterStatus, setFilterStatus] = useState('all');
    const [completing, setCompleting] = useState(null);

    const handleCompleteActivity = async (registration) => {
      const activity = activities.find(a => a.id === registration.activityId);
      if (!activity) { alert('❌ Atividade não encontrada!'); return; }
      if (!window.confirm(`Marcar como concluída?\n\nUtilizador: ${registration.userName}\nAtividade: ${activity.name}\nPontos a receber: +${activity.points}`)) return;
      setCompleting(registration.id);
      try {
        const award = await completeActivity(registration.userId, registration.activityId, activity.points, registration.id);
        alert(`✅ Atividade concluída! ${registration.userName} ganhou +${activity.points} pontos!` + (award ? `\n\n🎯 Cumpriu a meta mensal e recebeu +${award.reward} Eco-pontos extra!` : ''));
        await loadData();
        if (selectedActivityForRegs) {
          const regs = await getRegistrationsByActivity(selectedActivityForRegs.id);
          setActivityRegistrations(regs);
        }
      } catch (error) {
        console.error('❌ Erro ao completar atividade:', error);
        alert('❌ Erro ao marcar atividade como concluída!');
      }
      setCompleting(null);
    };

    const handleApprovePhoto = async (registration) => {
      const activity = activities.find(a => a.id === registration.activityId);
      if (!activity) { alert('❌ Atividade não encontrada!'); return; }
      if (!window.confirm(`Aprovar foto e concluir atividade?\n\nUtilizador: ${registration.userName}\nAtividade: ${activity.name}\nPontos a receber: +${activity.points}`)) return;
      setCompleting(registration.id);
      try {
        await approveActivityPhoto(registration.id);
        const award = await completeActivity(registration.userId, registration.activityId, activity.points, registration.id);
        alert(`✅ Foto aprovada! ${registration.userName} ganhou +${activity.points} pontos!` + (award ? `\n\n🎯 Cumpriu a meta mensal e recebeu +${award.reward} Eco-pontos extra!` : ''));
        await loadData();
        if (selectedRegistration) {
          const updatedReg = await getDoc(doc(db, 'activityRegistrations', registration.id));
          setSelectedRegistration({ id: registration.id, ...updatedReg.data() });
        }
      } catch (error) {
        console.error('❌ Erro ao aprovar foto:', error);
        alert('❌ Erro ao aprovar foto!');
      }
      setCompleting(null);
    };

    const handleRejectPhoto = async (registration) => {
      if (!window.confirm(`Rejeitar foto?\n\nO utilizador ${registration.userName} poderá enviar uma nova foto.`)) return;
      setCompleting(registration.id);
      try {
        await rejectActivityPhoto(registration.id);
        alert('❌ Foto rejeitada. O utilizador pode enviar uma nova foto.');
        await loadData();
        if (selectedRegistration) {
          const updatedReg = await getDoc(doc(db, 'activityRegistrations', registration.id));
          setSelectedRegistration({ id: registration.id, ...updatedReg.data() });
        }
      } catch (error) {
        console.error('❌ Erro ao rejeitar foto:', error);
        alert('❌ Erro ao rejeitar foto!');
      }
      setCompleting(null);
    };

    // --- detalhe de inscrição ---
    if (selectedRegistration) {
      const InfoRow = ({ icon: Icon, label, value }) => (
        <div className="flex items-start gap-3 p-3.5 bg-[#f7f3f5] rounded-2xl">
          <Icon className="w-5 h-5 text-[#9b8b94] mt-0.5 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-[#9b8b94]">{label}</p>
            <p className="font-bold text-[#111828] break-words text-sm">{value}</p>
          </div>
        </div>
      );
      return (
        <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
          <PageHeader eyebrow="Inscrições" title="Detalhes" onBack={() => setSelectedRegistration(null)} />
          <div className={`${card} p-5`}>
            <div className="space-y-2.5">
              <InfoRow icon={User} label="Nome" value={selectedRegistration.userName} />
              <InfoRow icon={Mail} label="Email" value={selectedRegistration.userEmail} />
              <InfoRow icon={Phone} label="Telefone" value={selectedRegistration.userPhone} />
              <InfoRow icon={User} label="Idade" value={`${selectedRegistration.userAge} anos`} />
              <InfoRow icon={AlertCircle} label="Contato de Emergência" value={selectedRegistration.emergencyContact} />
              {selectedRegistration.specialNeeds && (
                <InfoRow icon={Heart} label="Necessidades Especiais" value={selectedRegistration.specialNeeds} />
              )}
              <InfoRow icon={Clock} label="Data da Inscrição"
                value={new Date(selectedRegistration.createdAt).toLocaleDateString('pt-PT', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })} />
              <div className="flex items-center gap-2 p-3.5 bg-[#f7f3f5] rounded-2xl">
                <ClipboardList className="w-5 h-5 text-[#9b8b94] flex-shrink-0" />
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${statusPill(selectedRegistration.status)}`}>
                  {statusLabel(selectedRegistration.status)}
                </span>
                {selectedRegistration.completed && (
                  <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#714c62] text-white">Concluída</span>
                )}
              </div>
              {selectedRegistration.completed && selectedRegistration.completedAt && (
                <InfoRow icon={CheckCircle} label="Atividade Concluída"
                  value={new Date(selectedRegistration.completedAt).toLocaleDateString('pt-PT', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })} />
              )}
            </div>

            {selectedRegistration.status === 'pending' && (
              <div className="flex gap-2.5 mt-5">
                <button onClick={() => handleRejectRegistration(selectedRegistration)} className={`flex-1 px-4 py-3 text-sm ${btnDanger}`}>Rejeitar</button>
                <button onClick={() => handleApproveRegistration(selectedRegistration)} className={`flex-[1.4] px-4 py-3 text-sm ${btnPrimary}`}>Aprovar ✓</button>
              </div>
            )}

            {selectedRegistration.photoUrl && (
              <div className="border-t border-[#f2eff2] pt-5 mt-5">
                <h4 className="font-bold text-[#111828] mb-3 flex items-center gap-2 text-sm">
                  <Camera className="w-5 h-5 text-[#714c62]" /> Foto enviada pelo utilizador
                </h4>
                <img src={selectedRegistration.photoUrl} alt="Foto da atividade" className="w-full h-64 object-cover rounded-2xl mb-2" />
                <p className="text-xs text-gray-400 mb-2">
                  Enviada em {new Date(selectedRegistration.photoUploadedAt).toLocaleDateString('pt-PT', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
                {selectedRegistration.photoLocation ? (
                  <a
                    href={`https://www.openstreetmap.org/?mlat=${selectedRegistration.photoLocation.lat}&mlon=${selectedRegistration.photoLocation.lng}#map=17/${selectedRegistration.photoLocation.lat}/${selectedRegistration.photoLocation.lng}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#714c62] bg-[#f1e9ee] px-3 py-2 rounded-full mb-4 hover:bg-[#e6dae2] transition"
                  >
                    <MapPin className="w-3.5 h-3.5" /> Ver localização no mapa
                  </a>
                ) : (
                  <p className="text-[11px] text-gray-300 mb-4">Sem localização GPS registada</p>
                )}
                {selectedRegistration.photoStatus === 'pending' && (
                  <div className="flex flex-col gap-2.5">
                    <button onClick={() => handleApprovePhoto(selectedRegistration)} disabled={completing === selectedRegistration.id}
                      className={`w-full px-4 py-3 text-sm ${btnPrimary} flex items-center justify-center gap-2`}>
                      <CheckCircle className="w-5 h-5" /> Aprovar foto e concluir atividade
                    </button>
                    <button onClick={() => handleRejectPhoto(selectedRegistration)} disabled={completing === selectedRegistration.id}
                      className={`w-full px-4 py-3 text-sm ${btnDanger} flex items-center justify-center gap-2`}>
                      <XCircle className="w-5 h-5" /> Rejeitar foto
                    </button>
                  </div>
                )}
                {selectedRegistration.photoStatus === 'approved' && (
                  <div className="bg-[#e6f2ea] rounded-2xl p-4 text-[#3d7a52] font-bold text-sm flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" /> Foto aprovada e atividade concluída
                  </div>
                )}
                {selectedRegistration.photoStatus === 'rejected' && (
                  <div className="bg-[#faeaea] rounded-2xl p-4 text-[#8d3b3b] font-bold text-sm flex items-center gap-2">
                    <XCircle className="w-5 h-5" /> Foto rejeitada — o utilizador pode enviar nova foto
                  </div>
                )}
              </div>
            )}

            {selectedRegistration.status === 'approved' && !selectedRegistration.completed && (() => {
              const activityForReg = activities.find(a => a.id === selectedRegistration.activityId);
              const requiresPhoto = activityForReg?.requiresPhoto;
              if (requiresPhoto && !selectedRegistration.photoUrl) {
                return (
                  <div className="w-full px-4 py-3 mt-5 bg-[#f7f3f5] text-gray-500 text-sm font-semibold rounded-2xl flex items-center justify-center gap-2">
                    <Camera className="w-5 h-5" /> O utilizador ainda não enviou foto para validação
                  </div>
                );
              }
              if (!selectedRegistration.photoUrl) {
                return (
                  <button onClick={() => handleCompleteActivity(selectedRegistration)} disabled={completing === selectedRegistration.id}
                    className={`w-full px-4 py-3 mt-5 text-sm ${btnPrimary} flex items-center justify-center gap-2`}>
                    <CheckCircle className="w-5 h-5" />
                    {completing === selectedRegistration.id ? 'A processar...' : 'Marcar como concluída'}
                  </button>
                );
              }
              return null;
            })()}
          </div>
        </div>
      );
    }

    // --- inscrições de uma atividade ---
    if (selectedActivityForRegs) {
      const filteredRegs = filterStatus === 'all' ? activityRegistrations : activityRegistrations.filter(reg => reg.status === filterStatus);
      const pCount = activityRegistrations.filter(r => r.status === 'pending').length;
      const aCount = activityRegistrations.filter(r => r.status === 'approved').length;
      const rCount = activityRegistrations.filter(r => r.status === 'rejected').length;
      const cCount = activityRegistrations.filter(r => r.completed === true).length;

      const filters = [
        { id: 'all', label: `Todas ${activityRegistrations.length}` },
        { id: 'pending', label: `Pendentes ${pCount}` },
        { id: 'approved', label: `Aprovadas ${aCount}` },
        { id: 'rejected', label: `Rejeitadas ${rCount}` }
      ];

      return (
        <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
          <PageHeader eyebrow="Inscrições" title={selectedActivityForRegs.name}
            onBack={() => { setSelectedActivityForRegs(null); setActivityRegistrations([]); setFilterStatus('all'); }} />
          <p className="text-sm text-gray-500 -mt-2 mb-1">{activityRegistrations.length} inscrições no total</p>
          <p className="text-sm text-[#3d7a52] font-bold mb-4 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> {cCount} concluídas</p>

          <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
            {filters.map(f => (
              <button key={f.id} onClick={() => setFilterStatus(f.id)} className={filterStatus === f.id ? chipOn : chipOff}>{f.label}</button>
            ))}
          </div>

          <div className="space-y-3">
            {filteredRegs.length === 0 ? (
              <div className={`${card} p-6 text-center text-gray-400`}>
                <UserCheck className="w-12 h-12 mx-auto mb-2 text-gray-200" />
                <p className="text-sm">Nenhuma inscrição encontrada.</p>
              </div>
            ) : (
              filteredRegs.map((reg) => {
                const isCompleted = reg.completed === true;
                return (
                  <div key={reg.id} className={`${card} p-4 ${isCompleted ? 'opacity-80' : ''}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="w-11 h-11 rounded-full bg-[#f1e9ee] text-[#714c62] font-extrabold text-sm flex items-center justify-center flex-shrink-0">
                        {(reg.userName || 'U').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-[#111828] text-sm break-words">{reg.userName}</p>
                        <p className="text-xs text-gray-500 break-all">{reg.userEmail}</p>
                      </div>
                      {isCompleted ? (
                        <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#714c62] text-white whitespace-nowrap">Concluída</span>
                      ) : (
                        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${statusPill(reg.status)}`}>{statusLabel(reg.status)}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-400 mb-3">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(reg.createdAt).toLocaleDateString('pt-PT')}</span>
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {reg.userPhone}</span>
                      {isCompleted && reg.completedAt && (
                        <span className="flex items-center gap-1 text-[#3d7a52] font-bold"><CheckCircle className="w-3 h-3" /> {new Date(reg.completedAt).toLocaleDateString('pt-PT')}</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setSelectedRegistration(reg)} className={`flex-1 px-4 py-2.5 text-xs ${btnGhost}`}>Detalhes</button>
                      {reg.status === 'approved' && !isCompleted && (
                        selectedActivityForRegs?.requiresPhoto && !reg.photoUrl ? (
                          <div className="flex-1 px-3 py-2.5 bg-[#f7f3f5] text-gray-400 text-xs font-semibold rounded-2xl flex items-center justify-center gap-1.5">
                            <Camera className="w-4 h-4" /> Aguarda foto
                          </div>
                        ) : (
                          <button onClick={() => handleCompleteActivity(reg)} disabled={completing === reg.id}
                            className={`flex-1 px-4 py-2.5 text-xs ${btnPrimary} flex items-center justify-center gap-1.5`}>
                            <CheckCircle className="w-4 h-4" /> {completing === reg.id ? 'A processar…' : 'Concluir'}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      );
    }

    // --- lista de atividades ---
    const myUid = auth.currentUser?.uid;
    const listActivities = restrictToMine
      ? activities.filter((a) => a.responsibleAdminId === myUid && !a.archived)
      : activities;
    const scopedRegs = restrictToMine
      ? allRegistrations.filter((r) => listActivities.some((a) => a.id === r.activityId))
      : allRegistrations;

    return (
      <div className={hideHeader ? '' : 'pt-6 pb-32 px-4 max-w-lg mx-auto'}>
        {!hideHeader && <PageHeader eyebrow="Gestão" title="Inscrições" />}
        <div className="grid grid-cols-3 gap-2.5 mb-5">
          <div className={`${card} p-3.5 text-center`}>
            <p className="text-xl font-extrabold text-[#b45309]">{scopedRegs.filter(r => r.status === 'pending').length}</p>
            <p className="text-[11px] font-semibold text-gray-500">Pendentes</p>
          </div>
          <div className={`${card} p-3.5 text-center`}>
            <p className="text-xl font-extrabold text-[#3d7a52]">{scopedRegs.filter(r => r.status === 'approved').length}</p>
            <p className="text-[11px] font-semibold text-gray-500">Aprovadas</p>
          </div>
          <div className={`${card} p-3.5 text-center`}>
            <p className="text-xl font-extrabold text-[#8d3b3b]">{scopedRegs.filter(r => r.status === 'rejected').length}</p>
            <p className="text-[11px] font-semibold text-gray-500">Rejeitadas</p>
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-4">Toca numa atividade para ver as inscrições</p>

        <div className="space-y-3">
          {listActivities.length === 0 ? (
            <div className={`${card} p-6 text-center text-gray-400`}>
              <Activity className="w-12 h-12 mx-auto mb-2 text-gray-200" />
              <p className="text-sm">{restrictToMine ? 'Não és responsável por nenhuma atividade.' : 'Nenhuma atividade criada ainda.'}</p>
            </div>
          ) : (
            listActivities.map((activity) => {
              const regs = allRegistrations.filter(r => r.activityId === activity.id);
              const pendingRegs = regs.filter(r => r.status === 'pending');
              const completedRegs = regs.filter(r => r.completed === true);
              return (
                <button key={activity.id}
                  onClick={async () => {
                    setSelectedActivityForRegs(activity);
                    const actRegs = await getRegistrationsByActivity(activity.id);
                    setActivityRegistrations(actRegs);
                  }}
                  className={`${card} w-full p-4 flex items-center gap-3.5 text-left hover:shadow-lg transition`}>
                  <img src={activity.image} alt={activity.name} className="w-16 h-16 rounded-2xl object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#111828] text-sm break-words">{activity.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {regs.length} inscrições · {completedRegs.length} concluídas
                    </p>
                    <div className="flex gap-2 mt-1.5">
                      {pendingRegs.length > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#fdf2e3] text-[#b45309]">{pendingRegs.length} pendentes</span>
                      )}
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${activity.availableSlots > 0 ? 'bg-[#f1e9ee] text-[#714c62]' : 'bg-[#faeaea] text-[#8d3b3b]'}`}>
                        {activity.availableSlots} vagas
                      </span>
                    </div>
                  </div>
                  <ChevronLeft className="w-5 h-5 text-[#c3bcc9] rotate-180 flex-shrink-0" />
                </button>
              );
            })
          )}
        </div>
      </div>
    );
  };

  // ---------- UTILIZADORES ----------
  const UsersPage = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [userRegistrations, setUserRegistrations] = useState([]);
    const [userActivityHistory, setUserActivityHistory] = useState([]);
    const [formData, setFormData] = useState({ name: '', points: 0, level: 'Iniciante', activities: 0, trails: 0, isPremium: false, isAdmin: false });
    const [saving, setSaving] = useState(false);
    const [completing, setCompleting] = useState(null);

    useEffect(() => {
      if (editingUser) {
        setFormData({
          name: editingUser.name || '',
          points: editingUser.points || 0,
          level: editingUser.level || 'Iniciante',
          activities: editingUser.activities || 0,
          trails: editingUser.trails || 0,
          isPremium: editingUser.isPremium || false,
          isAdmin: editingUser.isAdmin || false
        });
      }
    }, [editingUser]);

    const filteredUsers = allUsers.filter(user =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const loadUserDetails = async (user) => {
      setSelectedUser(user);
      const regs = await getUserRegistrations(user.id);
      const history = await getUserActivityHistory(user.id);
      setUserRegistrations(regs);
      setUserActivityHistory(history);
    };

    const handleSaveUser = async () => {
      const currentAdminUid = auth.currentUser?.uid;
      const isSelf = editingUser.id === currentAdminUid;
      const demotingAdmin = !!editingUser.isAdmin && !formData.isAdmin;

      // Regra 1: um admin não pode remover o seu próprio acesso de administrador
      if (demotingAdmin && isSelf) {
        alert('❌ Não podes remover o teu próprio acesso de administrador.');
        return;
      }
      // Regra 2: tem de existir sempre pelo menos um administrador
      if (demotingAdmin) {
        const adminCount = allUsers.filter(u => u.isAdmin).length;
        if (adminCount <= 1) {
          alert('❌ Tem de existir sempre pelo menos um administrador. Promove outro utilizador a administrador antes de remover este.');
          return;
        }
      }

      if (!window.confirm('Guardar alterações ao utilizador?')) return;
      setSaving(true);
      try {
        const { isPremium, ...profileData } = formData;
        if (isSelf) profileData.isAdmin = true; // salvaguarda: nunca despromover o próprio
        await updateUser(editingUser.id, profileData);
        if (isPremium !== !!editingUser.isPremium) {
          if (isPremium) await activatePremium(editingUser.id, editingUser.stripeSessionId || null);
          else await deactivatePremium(editingUser.id);
        }
        // Refletir as alterações no ranking público
        try {
          await syncLeaderboardEntry(editingUser.id, { ...editingUser, ...profileData, isPremium });
        } catch (e) { console.error('Sync ranking falhou:', e); }
        alert('✅ Utilizador atualizado com sucesso!');
        setEditingUser(null);
        setSelectedUser(null);
        loadData().catch((e) => console.error('Erro ao atualizar dados:', e));
      } catch (error) {
        console.error('Erro ao atualizar utilizador:', error);
        alert('❌ Erro ao atualizar utilizador!');
      }
      setSaving(false);
    };

    const handleCompleteActivity = async (registration) => {
      const activity = activities.find(a => a.id === registration.activityId);
      if (!activity) { alert('❌ Atividade não encontrada!'); return; }
      if (!window.confirm(`Marcar como concluída?\n\nUtilizador: ${registration.userName}\nAtividade: ${activity.name}\nPontos a receber: +${activity.points}`)) return;
      setCompleting(registration.id);
      try {
        const award = await completeActivity(registration.userId, registration.activityId, activity.points, registration.id);
        alert(`✅ Atividade concluída! ${registration.userName} ganhou +${activity.points} pontos!` + (award ? `\n\n🎯 Cumpriu a meta mensal e recebeu +${award.reward} Eco-pontos extra!` : ''));
        await loadData();
        if (selectedUser && selectedUser.id === registration.userId) {
          const updatedUser = await getUserData(registration.userId);
          setSelectedUser(updatedUser);
          const regs = await getUserRegistrations(registration.userId);
          const history = await getUserActivityHistory(registration.userId);
          setUserRegistrations(regs);
          setUserActivityHistory(history);
        }
      } catch (error) {
        console.error('❌ Erro ao completar atividade:', error);
        alert('❌ Erro ao marcar atividade como concluída!');
      }
      setCompleting(null);
    };

    // --- edição ---
    if (editingUser) {
      const Field = ({ label, children }) => (
        <div>
          <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">{label}</label>
          {children}
        </div>
      );
      const isSelfEdit = editingUser.id === auth.currentUser?.uid;
      const adminCount = allUsers.filter(u => u.isAdmin).length;
      const lockAdminToggle = isSelfEdit || (editingUser.isAdmin && adminCount <= 1);
      return (
        <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
          <PageHeader eyebrow="Utilizadores" title="Editar" onBack={() => setEditingUser(null)} />
          <div className={`${card} p-5 space-y-4`}>
            <Field label="Nome">
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={inputCls} placeholder="Nome do utilizador" />
            </Field>
            <Field label="Email">
              <input type="email" value={editingUser.email} disabled className={`${inputCls} bg-[#f7f3f5] cursor-not-allowed text-gray-500`} />
              <p className="text-xs text-gray-400 mt-1">O email não pode ser alterado</p>
            </Field>
            <Field label="Pontos">
              <input type="number" value={formData.points} onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })} className={inputCls} min="0" />
            </Field>
            <Field label="Nível">
              <select value={formData.level} onChange={(e) => setFormData({ ...formData, level: e.target.value })} className={inputCls}>
                <option value="Iniciante">Iniciante</option>
                <option value="Intermediário">Intermediário</option>
                <option value="Avançado">Avançado</option>
                <option value="Expert">Expert</option>
              </select>
            </Field>
            <Field label="Atividades Completadas">
              <input type="number" value={formData.activities} onChange={(e) => setFormData({ ...formData, activities: parseInt(e.target.value) || 0 })} className={inputCls} min="0" />
            </Field>
            <Field label="Trilhos Completados">
              <input type="number" value={formData.trails} onChange={(e) => setFormData({ ...formData, trails: parseInt(e.target.value) || 0 })} className={inputCls} min="0" />
            </Field>
            <Field label="Administrador">
              <button
                type="button"
                disabled={lockAdminToggle}
                onClick={() => setFormData({ ...formData, isAdmin: !formData.isAdmin })}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl border text-sm font-bold transition ${formData.isAdmin ? 'bg-[#e6eef2] border-[#8fb0c2] text-[#2c5468]' : 'bg-[#f7f3f5] border-[#e8e2e6] text-gray-500'} ${lockAdminToggle ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <span className="flex items-center gap-2"><UserCheck className="w-4 h-4" /> {formData.isAdmin ? 'Administrador' : 'Utilizador normal'}</span>
                {!lockAdminToggle && <span className="text-xs font-semibold underline">{formData.isAdmin ? 'Tornar utilizador' : 'Tornar admin'}</span>}
              </button>
              {isSelfEdit ? (
                <p className="text-xs text-gray-400 mt-1">Não podes alterar o teu próprio estado de administrador.</p>
              ) : (editingUser.isAdmin && adminCount <= 1) ? (
                <p className="text-xs text-gray-400 mt-1">É o único administrador — promove outro antes de o remover.</p>
              ) : (
                <p className="text-xs text-gray-400 mt-1">Administradores têm acesso ao painel de gestão.</p>
              )}
            </Field>
            {!editingUser.isAdmin && (
              <Field label="Premium">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, isPremium: !formData.isPremium })}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl border text-sm font-bold transition ${formData.isPremium ? 'bg-[#fbf3dd] border-[#e8c76a] text-[#8a6d1c]' : 'bg-[#f7f3f5] border-[#e8e2e6] text-gray-500'}`}
                >
                  <span className="flex items-center gap-2"><Crown className="w-4 h-4" /> {formData.isPremium ? 'Premium ativo' : 'Premium inativo'}</span>
                  <span className="text-xs font-semibold underline">{formData.isPremium ? 'Desativar' : 'Ativar'}</span>
                </button>
                <p className="text-xs text-gray-400 mt-1">Ativação manual — útil se o pagamento Stripe foi concluído mas o Premium não ativou na app.</p>
              </Field>
            )}
            <div className="flex gap-2.5 pt-1">
              <button onClick={() => setEditingUser(null)} disabled={saving} className={`flex-1 px-4 py-3 text-sm ${btnGhost}`}>Cancelar</button>
              <button onClick={handleSaveUser} disabled={saving} className={`flex-[1.4] px-4 py-3 text-sm ${btnPrimary}`}>
                {saving ? 'A guardar…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // --- detalhe ---
    if (selectedUser) {
      const StatBox = ({ label, value }) => (
        <div className="bg-[#f7f3f5] rounded-2xl p-3.5 text-center">
          <p className="text-xl font-extrabold text-[#111828]">{value}</p>
          <p className="text-[11px] font-semibold text-gray-500">{label}</p>
        </div>
      );
      return (
        <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
          <PageHeader eyebrow="Utilizadores" title="Perfil"
            onBack={() => { setSelectedUser(null); setUserRegistrations([]); setUserActivityHistory([]); }}
            right={
              <button onClick={() => setEditingUser(selectedUser)} className={`flex items-center gap-1.5 px-4 py-2.5 text-xs ${btnSoft}`}>
                <Edit2 className="w-3.5 h-3.5" /> Editar
              </button>
            } />

          <div className={`${card} p-5 mb-3`}>
            <div className="flex items-center gap-4 mb-4">
              {selectedUser.photoURL ? (
                <img src={selectedUser.photoURL} alt={selectedUser.name} className="w-14 h-14 rounded-full object-cover" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-[#714c62] text-white font-bold text-lg flex items-center justify-center">
                  {selectedUser.name?.charAt(0) || 'U'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-extrabold text-[#111828] break-words">{selectedUser.name}</p>
                <p className="text-xs text-gray-500 break-all">{selectedUser.email}</p>
                <div className="flex gap-1.5 mt-1.5">
                  {selectedUser.isAdmin && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#f1e9ee] text-[#714c62]">Admin</span>}
                  {selectedUser.isPremium && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#fbf3dd] text-[#8a6d1c]">Premium</span>}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <StatBox label="Pontos" value={selectedUser.points || 0} />
              <StatBox label="Nível" value={selectedUser.level || 'Iniciante'} />
              <StatBox label="Atividades" value={selectedUser.activities || 0} />
              <StatBox label="Trilhos" value={selectedUser.trails || 0} />
            </div>
            {selectedUser.createdAt && (
              <p className="text-xs text-gray-400 mt-3 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> Membro desde {new Date(selectedUser.createdAt).toLocaleDateString('pt-PT', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            )}
          </div>

          {/* Inscrições */}
          <div className={`${card} p-5 mb-3`}>
            <h4 className="font-bold text-[#111828] mb-3 flex items-center gap-2 text-sm">
              <UserCheck className="w-5 h-5 text-[#714c62]" /> Inscrições ({userRegistrations.length})
            </h4>
            {userRegistrations.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Nenhuma inscrição registada</p>
            ) : (
              <div className="space-y-2.5">
                {userRegistrations.map((reg) => {
                  const activity = activities.find(a => a.id === reg.activityId);
                  const isCompleted = reg.completed === true;
                  return (
                    <div key={reg.id} className="bg-[#f7f3f5] rounded-2xl p-3.5">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="font-bold text-[#111828] text-sm break-words">{activity?.name || 'Atividade'}</p>
                        {isCompleted ? (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#714c62] text-white whitespace-nowrap">Concluída</span>
                        ) : (
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${statusPill(reg.status)}`}>{statusLabel(reg.status)}</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2.5 text-[11px] text-gray-500">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(reg.createdAt).toLocaleDateString('pt-PT')}</span>
                        {activity && <span className="flex items-center gap-1 font-bold text-[#714c62]"><Star className="w-3 h-3" /> +{activity.points} pts</span>}
                      </div>
                      {reg.status === 'approved' && !isCompleted && activity && (
                        activity.requiresPhoto && !reg.photoUrl ? (
                          <p className="text-[11px] text-gray-400 mt-2 flex items-center gap-1"><Camera className="w-3 h-3" /> Aguarda foto do utilizador</p>
                        ) : (
                          <button onClick={() => handleCompleteActivity(reg)} disabled={completing === reg.id}
                            className={`mt-2.5 px-3.5 py-2 text-[11px] ${btnPrimary} flex items-center gap-1`}>
                            <CheckCircle className="w-3 h-3" /> {completing === reg.id ? 'A processar…' : 'Concluir'}
                          </button>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Histórico de pontos */}
          <div className={`${card} p-5 mb-3`}>
            <h4 className="font-bold text-[#111828] mb-3 flex items-center gap-2 text-sm">
              <Trophy className="w-5 h-5 text-[#b97a1f]" /> Histórico de pontos ({userActivityHistory.length})
            </h4>
            {userActivityHistory.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Nenhuma atividade completada ainda</p>
            ) : (
              <div className="space-y-2">
                {userActivityHistory.map((historyItem) => {
                  const activity = activities.find(a => a.id === historyItem.activityId);
                  return (
                    <div key={historyItem.id} className="flex items-center justify-between gap-3 bg-[#f7f3f5] rounded-2xl p-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-[#111828] text-sm break-words">{activity?.name || 'Atividade'}</p>
                        <p className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
                          <Clock className="w-3 h-3" /> {new Date(historyItem.completedAt).toLocaleDateString('pt-PT', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                      <span className="flex items-center gap-1 bg-[#714c62] text-white px-3 py-1.5 rounded-full font-bold text-xs whitespace-nowrap">
                        <Star className="w-3.5 h-3.5 fill-white" /> +{historyItem.pointsEarned}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {!selectedUser.isAdmin && (
            <button
              onClick={() => {
                if (window.confirm(`Tem certeza que deseja apagar o utilizador ${selectedUser.name}?`)) {
                  handleDeleteUser(selectedUser.id);
                  setSelectedUser(null);
                }
              }}
              className={`w-full px-4 py-3 text-sm ${btnDanger} flex items-center justify-center gap-2`}>
              <Trash2 className="w-5 h-5" /> Eliminar utilizador
            </button>
          )}
        </div>
      );
    }

    // --- lista ---
    return (
      <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
        <PageHeader eyebrow="Comunidade" title="Utilizadores"
          right={<span className="text-xs font-bold text-[#714c62] bg-[#f1e9ee] px-3 py-1.5 rounded-full">{allUsers.length} total</span>} />

        <div className="relative mb-2">
          <Search className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
          <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Procurar por nome ou email…"
            className="w-full pl-12 pr-10 py-3.5 rounded-2xl bg-white shadow-[0_2px_8px_rgba(17,24,40,0.05)] border-0 focus:ring-2 focus:ring-[#714c62] focus:outline-none text-sm" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <p className="text-xs text-gray-400 mb-4 px-1">
          {filteredUsers.length} utilizador{filteredUsers.length !== 1 ? 'es' : ''} encontrado{filteredUsers.length !== 1 ? 's' : ''}
        </p>

        <div className="space-y-3">
          {filteredUsers.length === 0 ? (
            <div className={`${card} p-6 text-center text-gray-400`}>
              <Users className="w-12 h-12 mx-auto mb-2 text-gray-200" />
              <p className="text-sm">Nenhum utilizador encontrado</p>
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div key={user.id} className={`${card} p-4`}>
                <div className="flex items-center gap-3 mb-3">
                  {user.photoURL ? (
                    <img src={user.photoURL} alt={user.name || 'Utilizador'} className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-[#f1e9ee] text-[#714c62] font-extrabold flex items-center justify-center flex-shrink-0">
                      {user.name?.charAt(0) || 'U'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#111828] text-sm break-words">{user.name}</p>
                    <p className="text-xs text-gray-500 break-all">{user.email}</p>
                  </div>
                  {user.isAdmin && (
                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#f1e9ee] text-[#714c62] flex items-center gap-1 whitespace-nowrap">
                      <UserCheck className="w-3 h-3" /> Admin
                    </span>
                  )}
                  {user.isPremium && !user.isAdmin && (
                    <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#fbf3dd] text-[#8a6d1c] flex items-center gap-1 whitespace-nowrap">
                      <Crown className="w-3 h-3" /> Premium
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold px-3 py-1.5 rounded-full bg-[#f1e9ee] text-[#714c62] flex items-center gap-1">
                    <Star className="w-3 h-3" /> {user.points || 0} pts
                  </span>
                  <span className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-[#f7f3f5] text-gray-500">
                    {user.activities || 0} atividades
                  </span>
                  <button onClick={() => loadUserDetails(user)} className="ml-auto text-xs font-extrabold text-[#714c62] px-1">
                    Ver perfil →
                  </button>
                  {!user.isAdmin && (
                    <button onClick={() => handleDeleteUser(user.id)} className="p-1.5 text-[#c3bcc9] hover:text-[#8d3b3b] transition">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  // ---------- NOVIDADES ----------
  const NewsPage = () => (
    <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
      <PageHeader eyebrow="Conteúdos" title="Novidades" onBack={() => setCurrentAdminPage('more')}
        right={<span className="text-xs font-bold text-[#714c62] bg-[#f1e9ee] px-3 py-1.5 rounded-full">{news.length}</span>} />
      <div className="space-y-3">
        {news.map((item) => (
          <div key={item.id} className={`${card} overflow-hidden`}>
            <img src={item.image} alt={item.title} className="w-full h-32 object-cover" />
            <div className="p-4">
              <h3 className="font-bold text-[#111828] mb-1 text-sm">{item.title}</h3>
              <p className="text-xs text-gray-500 mb-3 leading-relaxed">{item.description}</p>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-gray-400">{item.date}</span>
                <button onClick={() => openModal('news', 'edit', item)}
                  className="ml-auto w-9 h-9 rounded-xl bg-[#f7f3f5] flex items-center justify-center hover:bg-[#efe9ec] transition">
                  <Edit2 className="w-4 h-4 text-[#111828]" />
                </button>
                <button onClick={() => handleDeleteNews(item.id)}
                  className="w-9 h-9 rounded-xl bg-[#faeaea] flex items-center justify-center hover:bg-[#f5dcdc] transition">
                  <Trash2 className="w-4 h-4 text-[#8d3b3b]" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Fab onClick={() => openModal('news', 'create')} />
    </div>
  );

  // ---------- OFERTAS ----------
  const OffersPage = () => (
    <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
      <PageHeader eyebrow="Conteúdos" title="Ofertas" onBack={() => setCurrentAdminPage('more')}
        right={<span className="text-xs font-bold text-[#714c62] bg-[#f1e9ee] px-3 py-1.5 rounded-full">{offers.length} ativas</span>} />
      <div className="space-y-3">
        {offers.map((offer) => (
          <div key={offer.id} className={`${card} p-4`}>
            <div className="flex gap-3 mb-3">
              <img src={offer.image} alt={offer.title} className="w-14 h-14 rounded-2xl object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-[#111828] text-sm break-words">{offer.title}</h3>
              </div>
              <span className="text-xs font-extrabold text-[#714c62] bg-[#f1e9ee] px-3 py-1.5 rounded-full h-fit whitespace-nowrap">{offer.points} pts</span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => openModal('offer', 'edit', offer)}
                className={`flex-1 px-4 py-2.5 text-xs ${btnGhost} flex items-center justify-center gap-1.5`}>
                <Edit2 className="w-3.5 h-3.5" /> Editar
              </button>
              <button onClick={() => handleDeleteOffer(offer.id)}
                className="w-10 rounded-2xl bg-[#faeaea] flex items-center justify-center hover:bg-[#f5dcdc] transition">
                <Trash2 className="w-4 h-4 text-[#8d3b3b]" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <Fab onClick={() => openModal('offer', 'create')} />
    </div>
  );

  // ---------- ATIVIDADES ----------
  const ActivitiesPage = () => {
    const [selectedCat, setSelectedCat] = useState(null);
    const [search, setSearch] = useState('');
    // Arquivadas não aparecem aqui — têm a sua própria secção
    const notArchived = activities.filter((a) => !a.archived);
    const byCat = selectedCat ? notArchived.filter(act => act.categoryId === selectedCat) : notArchived;
    const term = search.trim().toLowerCase();
    const filteredAct = term
      ? byCat.filter((a) => (a.name || '').toLowerCase().includes(term)
          || activityCode(a).toLowerCase().includes(term)
          || (a.zone || '').toLowerCase().includes(term))
      : byCat;

    const handleStart = async (activity) => {
      if (!window.confirm(`Começar "${activity.name}"?\n\nAs inscrições fecham e ninguém mais se pode inscrever.`)) return;
      try {
        await startActivity(activity.id, userData);
        await loadData();
        setCurrentAdminPage('active');
      } catch (e) {
        console.error('Erro ao começar a atividade:', e);
        alert('Erro ao começar a atividade: ' + (e?.message || e));
      }
    };

    return (
      <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
        <PageHeader eyebrow="Gestão" title="Atividades"
          right={<span className="text-xs font-bold text-[#714c62] bg-[#f1e9ee] px-3 py-1.5 rounded-full">{notArchived.length}</span>} />

        <div className="relative mb-4">
          <Search className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pesquisar por nome, código ou zona…"
            className="w-full pl-11 pr-10 py-3 rounded-2xl border border-[#e8e2e6] bg-white text-sm focus:ring-2 focus:ring-[#714c62] focus:outline-none"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-[#f7f3f5] flex items-center justify-center text-gray-400">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
          <button onClick={() => setSelectedCat(null)} className={selectedCat === null ? chipOn : chipOff}>Todas</button>
          {categories.map((cat) => (
            <button key={cat.id} onClick={() => setSelectedCat(cat.id)} className={selectedCat === cat.id ? chipOn : chipOff}>
              {cat.name}
            </button>
          ))}
        </div>

        {filteredAct.length === 0 && (
          <div className={`${card} p-8 text-center text-gray-400 mb-4`}>
            <Search className="w-10 h-10 mx-auto mb-2 text-gray-200" />
            <p className="text-sm">Nenhuma atividade encontrada.</p>
          </div>
        )}

        <div className="space-y-3">
          {filteredAct.map((activity) => {
            const category = categories.find(c => c.id === activity.categoryId);
            return (
              <div key={activity.id} className={`${card} overflow-hidden`}>
                <div className="relative h-32">
                  <img src={activity.image} alt={activity.name} className="w-full h-full object-cover" />
                  {category && (
                    <span className="absolute top-2.5 left-2.5 text-[11px] font-bold text-[#111828] bg-white px-2.5 py-1 rounded-full">
                      {category.name}
                    </span>
                  )}
                  <span className="absolute top-2.5 right-2.5 text-[11px] font-bold text-white bg-[#714c62] px-2.5 py-1 rounded-full">
                    +{activity.points} pts
                  </span>
                  {activity.ongoing && (
                    <span className="absolute bottom-2.5 left-2.5 text-[11px] font-bold text-[#3d7a52] bg-[#e6f2ea] px-2.5 py-1 rounded-full">A decorrer</span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-0.5">
                    <h3 className="font-bold text-[#111828] text-sm">{activity.name}</h3>
                    <span className="text-[10px] font-mono font-bold text-gray-500 bg-[#f2eef1] px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">{activityCode(activity)}</span>
                  </div>
                  {activity.zone && <p className="text-[11px] text-[#714c62] font-semibold mb-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> {activity.zone}</p>}
                  <p className="text-xs text-gray-500 mb-2">
                    {activity.distance && activity.distance.trim() !== '' ? `${activity.distance} · ` : ''}{activity.difficulty}
                    {activity.availableSlots !== undefined && (
                      <span className={`font-bold ${activity.availableSlots > 0 ? 'text-[#3d7a52]' : 'text-[#8d3b3b]'}`}> · {activity.availableSlots} vagas</span>
                    )}
                    {activity.requiresPhoto && <span className="text-[#714c62] font-semibold"> · foto obrigatória</span>}
                    {(activity.tasks?.length > 0) && <span className="text-[#714c62] font-semibold"> · {activity.tasks.length} tarefas</span>}
                  </p>
                  {activity.responsibleAdminName && (
                    <p className="text-[11px] text-gray-400 mb-1.5 flex items-center gap-1"><UserCheck className="w-3 h-3" /> Resp.: {activity.responsibleAdminName}</p>
                  )}
                  {activity.ongoing ? (
                    <button onClick={() => { setSelectedActiveActivity(activity); setCurrentAdminPage('active'); }}
                      className="w-full mb-2 px-3 py-2.5 text-xs font-bold rounded-2xl flex items-center justify-center gap-1.5 transition bg-[#111828] text-white hover:bg-[#242c42]">
                      <Activity className="w-3.5 h-3.5" /> A decorrer — ver detalhes
                    </button>
                  ) : (
                    <button onClick={() => handleStart(activity)}
                      className="w-full mb-2 px-3 py-2.5 text-xs font-bold rounded-2xl flex items-center justify-center gap-1.5 transition bg-[#e6f2ea] text-[#3d7a52] hover:bg-[#d8ecdf]">
                      <CheckCircle className="w-3.5 h-3.5" /> Começar atividade
                    </button>
                  )}
                  <div className="flex gap-2">
                    <button onClick={() => openModal('activity', 'edit', activity)}
                      className={`flex-1 px-3 py-2.5 text-xs ${btnGhost} flex items-center justify-center gap-1.5`}>
                      <Edit2 className="w-3.5 h-3.5" /> Editar
                    </button>
                    <button onClick={() => { setCurrentAdminPage('registrations'); }}
                      className={`flex-1 px-3 py-2.5 text-xs ${btnSoft} flex items-center justify-center gap-1.5`}>
                      <Users className="w-3.5 h-3.5" /> Inscrições
                    </button>
                    <button onClick={() => handleDeleteActivity(activity.id)}
                      className="w-10 rounded-2xl bg-[#faeaea] flex items-center justify-center hover:bg-[#f5dcdc] transition">
                      <Trash2 className="w-4 h-4 text-[#8d3b3b]" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <Fab onClick={() => openModal('activity', 'create')} />
      </div>
    );
  };

  // ---------- RECOMPENSAS ----------
  const AdminRewardsPage = () => (
    <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
      <PageHeader eyebrow="Catálogo" title="Recompensas" onBack={() => setCurrentAdminPage('more')}
        right={<span className="text-xs font-bold text-[#714c62] bg-[#f1e9ee] px-3 py-1.5 rounded-full">{rewardCatalog.length} itens</span>} />
      {rewardCatalog.length === 0 ? (
        <div className={`${card} p-8 text-center`}>
          <Award className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Nenhuma recompensa no catálogo.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {rewardCatalog.map((reward) => (
            <div key={reward.id} className={`${card} p-4`}>
              {reward.image ? (
                <img src={reward.image} alt={reward.name} className="w-full h-24 rounded-2xl object-cover mb-2.5" />
              ) : (
                <div className="w-11 h-11 rounded-2xl bg-[#f1e9ee] flex items-center justify-center mb-2.5">
                  <Award className="w-5 h-5 text-[#714c62]" />
                </div>
              )}
              <p className="font-bold text-[#111828] text-sm">{reward.name}</p>
              <p className="text-[11px] text-gray-500 mt-0.5 mb-2.5 line-clamp-2">{reward.description}</p>
              <div className="flex items-center justify-between">
                <button onClick={() => openModal('reward', 'edit', reward)}
                  className="w-8 h-8 rounded-xl bg-[#f7f3f5] flex items-center justify-center hover:bg-[#efe9ec] transition">
                  <Edit2 className="w-3.5 h-3.5 text-[#111828]" />
                </button>
                <button onClick={() => handleDeleteReward(reward.id)}
                  className="w-8 h-8 rounded-xl bg-[#faeaea] flex items-center justify-center hover:bg-[#f5dcdc] transition">
                  <Trash2 className="w-3.5 h-3.5 text-[#8d3b3b]" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Fab onClick={() => openModal('reward', 'create')} />
    </div>
  );

  // ---------- MODAL DE DESAFIO ----------
  const ChallengeModal = ({ challenge, onClose, onSave }) => {
    const [formData, setFormData] = useState({
      name: challenge?.name || '',
      description: challenge?.description || '',
      pointsCost: challenge?.pointsCost || 0,
      image: challenge?.image || ''
    });
    const [tasks, setTasks] = useState(challenge?.tasks || []);
    const [selectedRewards, setSelectedRewards] = useState(challenge?.rewards || []);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(challenge?.image || null);
    const [saving, setSaving] = useState(false);

    const addTask = () => {
      setTasks([...tasks, { id: Date.now().toString(), name: '', description: '', details: '', points: 0, order: tasks.length + 1 }]);
    };
    const removeTask = (taskId) => {
      setTasks(tasks.filter(t => t.id !== taskId).map((t, i) => ({ ...t, order: i + 1 })));
    };
    const updateTask = (taskId, field, value) => {
      setTasks(tasks.map(t => t.id === taskId ? { ...t, [field]: value } : t));
    };
    const toggleReward = (reward) => {
      const exists = selectedRewards.find(r => r.rewardId === reward.id);
      if (exists) setSelectedRewards(selectedRewards.filter(r => r.rewardId !== reward.id));
      else setSelectedRewards([...selectedRewards, { rewardId: reward.id, name: reward.name, image: reward.image }]);
    };
    const handleImageChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        if (!file.type.startsWith('image/')) { alert('Selecione uma imagem!'); return; }
        if (file.size > 10 * 1024 * 1024) { alert('Imagem muito grande! Máximo 10MB.'); return; }
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
      }
    };
    const handleSave = async () => {
      if (!formData.name) { alert('Preencha o nome do desafio!'); return; }
      if (tasks.length === 0) { alert('Adicione pelo menos uma tarefa!'); return; }
      setSaving(true);
      try {
        let finalData = { ...formData, tasks, rewards: selectedRewards };
        if (imageFile) {
          const photoUrl = await uploadActivityPhoto(imageFile);
          finalData.image = photoUrl;
        }
        await onSave(finalData, challenge?.id);
        onClose();
      } catch (error) {
        console.error('Erro ao guardar desafio:', error);
      }
      setSaving(false);
    };

    return (
      <div className="fixed inset-0 bg-[#111828]/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-[28px] w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-extrabold text-[#111828]">{challenge ? 'Editar' : 'Criar'} desafio</h2>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#f7f3f5] flex items-center justify-center text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">Nome</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={inputCls} placeholder="Nome do desafio" />
          </div>
          <div className="mb-4">
            <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">Descrição</label>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className={`${inputCls} h-20`} placeholder="Descrição do desafio" />
          </div>
          <div className="mb-4">
            <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">Custo de inscrição (pontos)</label>
            <input type="number" value={formData.pointsCost} onChange={(e) => setFormData({ ...formData, pointsCost: parseInt(e.target.value) || 0 })}
              className={inputCls} placeholder="50" />
          </div>
          <div className="mb-5">
            <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">Imagem</label>
            <input type="file" accept="image/*" onChange={handleImageChange} className={inputCls} />
            {imagePreview && <img src={imagePreview} alt="Preview" className="w-full h-32 object-cover mt-2 rounded-2xl" />}
          </div>

          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-[#111828] text-sm">Tarefas</h3>
              <button onClick={addTask} className={`px-3.5 py-2 text-xs ${btnSoft} flex items-center gap-1`}>
                <Plus className="w-3.5 h-3.5" /> Adicionar
              </button>
            </div>
            {tasks.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Nenhuma tarefa adicionada.</p>}
            <div className="space-y-3">
              {tasks.map((task, idx) => (
                <div key={task.id} className="bg-[#f7f3f5] rounded-2xl p-3.5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-extrabold text-[#714c62]">Tarefa {idx + 1}</span>
                    <button onClick={() => removeTask(task.id)} className="text-[#c3bcc9] hover:text-[#8d3b3b]">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <input type="text" placeholder="Nome da tarefa" value={task.name} onChange={(e) => updateTask(task.id, 'name', e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl border border-[#e8e2e6] bg-white text-sm mb-2 focus:ring-2 focus:ring-[#714c62] focus:outline-none" />
                  <input type="text" placeholder="Descrição" value={task.description} onChange={(e) => updateTask(task.id, 'description', e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl border border-[#e8e2e6] bg-white text-sm mb-2 focus:ring-2 focus:ring-[#714c62] focus:outline-none" />
                  <input type="text" placeholder="Detalhes (opcional)" value={task.details} onChange={(e) => updateTask(task.id, 'details', e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl border border-[#e8e2e6] bg-white text-sm mb-2 focus:ring-2 focus:ring-[#714c62] focus:outline-none" />
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 font-semibold">Pontos:</label>
                    <input type="number" placeholder="0" value={task.points} onChange={(e) => updateTask(task.id, 'points', parseInt(e.target.value) || 0)}
                      className="w-24 px-3 py-1.5 rounded-xl border border-[#e8e2e6] bg-white text-sm focus:ring-2 focus:ring-[#714c62] focus:outline-none" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h3 className="font-bold text-[#111828] text-sm mb-3">Recompensas (do catálogo)</h3>
            {rewardCatalog.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Nenhuma recompensa no catálogo. Crie primeiro na secção Recompensas.</p>
            ) : (
              <div className="space-y-2">
                {rewardCatalog.map(reward => {
                  const isSelected = selectedRewards.some(r => r.rewardId === reward.id);
                  return (
                    <div key={reward.id} onClick={() => toggleReward(reward)}
                      className={`flex items-center gap-3 p-3 rounded-2xl cursor-pointer transition ${isSelected ? 'bg-[#f1e9ee] ring-2 ring-[#714c62]' : 'bg-[#f7f3f5] hover:bg-[#efe9ec]'}`}>
                      {reward.image && <img src={reward.image} alt={reward.name} className="w-11 h-11 rounded-xl object-cover" />}
                      <p className="flex-1 font-semibold text-sm text-[#111828]">{reward.name}</p>
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isSelected ? 'bg-[#714c62]' : 'border-2 border-[#d8d0d5]'}`}>
                        {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex gap-2.5">
            <button onClick={onClose} className={`flex-1 px-4 py-3 text-sm ${btnGhost}`}>Cancelar</button>
            <button onClick={handleSave} disabled={saving} className={`flex-[1.4] px-4 py-3 text-sm ${btnPrimary}`}>
              {saving ? 'A guardar…' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ---------- PREMIUM (DESAFIOS) ----------
  const AdminPremiumPage = () => {
    if (selectedChallengeForRegs) {
      const challenge = challenges.find(c => c.id === selectedChallengeForRegs);
      const regs = allChallengeRegistrations.filter(r => r.challengeId === selectedChallengeForRegs);

      return (
        <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
          <PageHeader eyebrow="Premium" title={challenge?.name} onBack={() => setSelectedChallengeForRegs(null)} />
          <p className="text-sm text-gray-500 -mt-2 mb-5">{regs.length} inscrições</p>

          {regs.length === 0 ? (
            <div className={`${card} p-8 text-center text-gray-400`}>
              <p className="text-sm">Nenhuma inscrição neste desafio.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {regs.map(reg => {
                const completedCount = reg.taskProgress?.filter(t => t.status === 'completed').length || 0;
                const totalCount = reg.taskProgress?.length || 0;
                const hasPending = reg.taskProgress?.some(t => t.photoStatus === 'pending');

                return (
                  <div key={reg.id} className={`${card} p-5`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="min-w-0">
                        <p className="font-bold text-[#111828] text-sm">{reg.userName}</p>
                        <p className="text-[11px] text-gray-400">Inscrito em {new Date(reg.enrolledAt).toLocaleDateString('pt-PT')}</p>
                      </div>
                      <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${reg.status === 'completed' ? 'bg-[#e6f2ea] text-[#3d7a52]' : 'bg-[#f1e9ee] text-[#714c62]'}`}>
                        {reg.status === 'completed' ? 'Concluído' : 'Em progresso'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="flex-1 h-2 rounded-full bg-[#f1e9ee]">
                        <div className="h-2 rounded-full bg-[#714c62]" style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }} />
                      </div>
                      <span className="text-[11px] font-bold text-[#714c62]">{completedCount}/{totalCount}</span>
                    </div>

                    {hasPending && (
                      <p className="text-[11px] text-[#b45309] font-bold mb-2.5 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Tem fotos pendentes de aprovação
                      </p>
                    )}

                    <div className="space-y-2">
                      {(reg.taskProgress || []).map((tp, idx) => {
                        const taskInfo = challenge?.tasks?.find(t => t.id === tp.taskId);
                        return (
                          <div key={tp.taskId} className="bg-[#f7f3f5] rounded-2xl p-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold text-[#111828]">{idx + 1}. {taskInfo?.name || 'Tarefa'}</span>
                              <span className={`text-[11px] font-bold ${tp.status === 'completed' ? 'text-[#3d7a52]' : tp.status === 'available' ? 'text-[#714c62]' : 'text-gray-400'}`}>
                                {tp.status === 'completed' ? 'Concluída' : tp.status === 'available' ? 'Ativa' : 'Bloqueada'}
                              </span>
                            </div>
                            {tp.photoStatus === 'pending' && tp.photoUrl && (
                              <div className="mt-2.5">
                                <img src={tp.photoUrl} alt="Foto" className="w-full h-32 object-cover rounded-xl mb-2.5" />
                                <div className="flex gap-2">
                                  <button onClick={() => handleRejectChallengePhoto(reg.id, tp.taskId)}
                                    className={`flex-1 py-2 text-xs ${btnDanger} flex items-center justify-center gap-1`}>
                                    <XCircle className="w-3.5 h-3.5" /> Rejeitar
                                  </button>
                                  <button onClick={() => handleApproveChallengePhoto(reg.id, tp.taskId, taskInfo?.points || 0)}
                                    className={`flex-[1.4] py-2 text-xs ${btnPrimary} flex items-center justify-center gap-1`}>
                                    <CheckCircle className="w-3.5 h-3.5" /> Aprovar
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
        <PageHeader eyebrow="Premium" title="Desafios" onBack={() => setCurrentAdminPage('more')}
          right={<span className="text-xs font-bold text-[#8a6d1c] bg-[#fbf3dd] px-3 py-1.5 rounded-full flex items-center gap-1"><Crown className="w-3.5 h-3.5" /> {challenges.length}</span>} />

        {challenges.length === 0 ? (
          <div className={`${card} p-8 text-center`}>
            <Target className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">Nenhum desafio criado.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {challenges.map(challenge => {
              const regCount = allChallengeRegistrations.filter(r => r.challengeId === challenge.id).length;
              const pendingPhotos = allChallengeRegistrations
                .filter(r => r.challengeId === challenge.id)
                .reduce((acc, r) => acc + (r.taskProgress?.filter(t => t.photoStatus === 'pending').length || 0), 0);

              return (
                <div key={challenge.id} className={`${card} overflow-hidden`}>
                  {challenge.image && <img src={challenge.image} alt={challenge.name} className="w-full h-32 object-cover" />}
                  <div className="p-4">
                    <h3 className="font-bold text-[#111828] text-sm mb-0.5">{challenge.name}</h3>
                    <p className="text-xs text-gray-500 mb-2">{challenge.description}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mb-3">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#f7f3f5] text-gray-500">{challenge.tasks?.length || 0} tarefas</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#f7f3f5] text-gray-500">{challenge.pointsCost} pts inscrição</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#f1e9ee] text-[#714c62]">{regCount} inscritos</span>
                      {pendingPhotos > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#fdf2e3] text-[#b45309]">{pendingPhotos} fotos pendentes</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setSelectedChallengeForRegs(challenge.id)}
                        className={`flex-1 px-3 py-2.5 text-xs ${btnSoft}`}>Ver inscrições</button>
                      <button onClick={() => { setEditingChallenge(challenge); setShowChallengeModal(true); }}
                        className="w-10 rounded-2xl bg-[#f7f3f5] flex items-center justify-center hover:bg-[#efe9ec] transition">
                        <Edit2 className="w-4 h-4 text-[#111828]" />
                      </button>
                      <button onClick={() => handleDeleteChallenge(challenge.id)}
                        className="w-10 rounded-2xl bg-[#faeaea] flex items-center justify-center hover:bg-[#f5dcdc] transition">
                        <Trash2 className="w-4 h-4 text-[#8d3b3b]" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Fab onClick={() => { setEditingChallenge(null); setShowChallengeModal(true); }} />

        {showChallengeModal && (
          <ChallengeModal
            challenge={editingChallenge}
            onClose={() => { setShowChallengeModal(false); setEditingChallenge(null); }}
            onSave={handleSaveChallenge}
          />
        )}
      </div>
    );
  };

  const RedemptionsPage = () => {
    const [vouchers, setVouchers] = useState({});
    const [busy, setBusy] = useState(null);
    const pending = (redemptions || []).filter((r) => r.status !== 'sent');
    const done = (redemptions || []).filter((r) => r.status === 'sent');

    const markSent = async (r) => {
      setBusy(r.id);
      try {
        await updateRedemption(r.id, { status: 'sent', voucher: vouchers[r.id] || r.voucher || '', sentAt: new Date().toISOString() });
        await loadData();
      } catch (e) {
        console.error('Erro ao atualizar resgate:', e);
        alert('Erro ao atualizar o resgate.');
      } finally {
        setBusy(null);
      }
    };

    const mailtoHref = (r) => {
      const subject = `A tua recompensa GreenStep: ${r.offerTitle || ''}`;
      const body = `Olá ${r.userName || ''},\n\nObrigado por resgatares "${r.offerTitle || ''}" na GreenStep.\n\nO teu voucher / código: ${vouchers[r.id] || '____________'}\n\nInstruções de utilização: (preencher)\n\nObrigado por seres um turista sustentável!\nEquipa GreenStep`;
      return `mailto:${r.userEmail || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    return (
      <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
        <PageHeader eyebrow="Recompensas" title="Resgates" onBack={() => setCurrentAdminPage('more')}
          right={pending.length > 0 && <span className="text-xs font-bold text-[#8d3b3b] bg-[#faeaea] px-3 py-1.5 rounded-full">{pending.length} pendentes</span>} />

        {pending.length === 0 ? (
          <div className={`${card} p-8 text-center text-gray-400 mb-5`}>
            <CheckCircle className="w-10 h-10 mx-auto mb-2 text-gray-200" />
            <p className="text-sm">Nenhum resgate pendente.</p>
          </div>
        ) : (
          <div className="space-y-3 mb-6">
            {pending.map((r) => (
              <div key={r.id} className={`${card} p-5`}>
                <div className="flex items-start gap-3 mb-3">
                  <span className="w-11 h-11 rounded-full bg-[#f1e9ee] text-[#714c62] font-extrabold text-sm flex items-center justify-center flex-shrink-0">
                    {(r.userName || 'U').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[#111828] text-sm">{r.userName}</p>
                    <p className="text-xs text-gray-500 break-all">{r.userEmail}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">{r.createdAt ? new Date(r.createdAt).toLocaleString('pt-PT') : ''}</p>
                  </div>
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#fdf2e3] text-[#b45309] whitespace-nowrap">Pendente</span>
                </div>
                <div className="bg-[#f7f3f5] rounded-2xl p-3 mb-3">
                  <p className="text-sm font-bold text-[#111828]">{r.offerTitle}</p>
                  <p className="text-xs text-[#714c62] font-semibold flex items-center gap-1"><Leaf className="w-3 h-3" /> {r.pointsCost} Eco-pontos</p>
                </div>
                <label className="block text-[11px] font-bold text-[#9b8b94] uppercase mb-1">Voucher / código (opcional)</label>
                <input value={vouchers[r.id] || ''} onChange={(e) => setVouchers({ ...vouchers, [r.id]: e.target.value })}
                  className={`${inputCls} mb-3`} placeholder="Ex: ECO-2026-XYZ" />
                <div className="flex gap-2.5">
                  <a href={mailtoHref(r)} className={`flex-1 px-4 py-2.5 text-xs ${btnSoft} flex items-center justify-center gap-1.5`}>
                    <Mail className="w-3.5 h-3.5" /> Abrir email
                  </a>
                  <button onClick={() => markSent(r)} disabled={busy === r.id} className={`flex-[1.3] px-4 py-2.5 text-xs ${btnPrimary} flex items-center justify-center gap-1.5`}>
                    <CheckCircle className="w-3.5 h-3.5" /> {busy === r.id ? '…' : 'Marcar enviado'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {done.length > 0 && (
          <>
            <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#9b8b94] mb-3">Enviados ({done.length})</p>
            <div className="space-y-2">
              {done.map((r) => (
                <div key={r.id} className={`${card} p-4 opacity-70`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-bold text-[#111828] text-sm">{r.userName}</p>
                      <p className="text-[11px] text-gray-400 break-all">{r.offerTitle}{r.voucher ? ` · ${r.voucher}` : ''}</p>
                    </div>
                    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#e6f2ea] text-[#3d7a52] whitespace-nowrap">Enviado</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // ---------- CONQUISTAS ----------
  const AchievementsAdminPage = () => {
    const [busy, setBusy] = useState(false);
    const list = achievements || [];
    const usingDefaults = list.length > 0 && String(list[0].id).startsWith('default-');

    const handleImportDefaults = async () => {
      if (!window.confirm('Criar as 9 conquistas predefinidas na base de dados?\n\nDepois podes editá-las e apagá-las livremente.')) return;
      setBusy(true);
      try {
        const { added } = await seedAchievements(DEFAULT_ACHIEVEMENTS);
        await loadData();
        alert(`✅ ${added} conquistas criadas!`);
      } catch (e) {
        console.error('Erro ao criar conquistas:', e);
        alert('❌ Erro: ' + (e?.message || e));
      } finally { setBusy(false); }
    };

    const handleDelete = async (a) => {
      if (!window.confirm(`Apagar a conquista "${a.name}"?`)) return;
      try {
        await deleteAchievement(a.id);
        await loadData();
      } catch (e) {
        console.error('Erro ao apagar conquista:', e);
        alert('❌ Erro ao apagar: ' + (e?.message || e));
      }
    };

    const handleToggleActive = async (a) => {
      try {
        await updateAchievement(a.id, { active: a.active === false });
        await loadData();
      } catch (e) {
        console.error('Erro ao alterar conquista:', e);
        alert('❌ Erro: ' + (e?.message || e));
      }
    };

    return (
      <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
        <PageHeader eyebrow="Gamificação" title="Conquistas" onBack={() => setCurrentAdminPage('more')}
          right={<span className="text-xs font-bold text-[#714c62] bg-[#f1e9ee] px-3 py-1.5 rounded-full">{list.length}</span>} />

        {usingDefaults && (
          <div className="bg-[#fbf3dd] rounded-2xl p-4 mb-4 flex gap-2 items-start">
            <AlertCircle className="w-4 h-4 text-[#8a6d1c] mt-0.5 flex-shrink-0" />
            <div className="text-[11px] text-[#8a6d1c] leading-relaxed">
              <p className="font-bold mb-1">Estas são as conquistas predefinidas (ainda não estão na base de dados).</p>
              <p className="mb-2">Para as poderes editar ou apagar, importa-as primeiro.</p>
              <button onClick={handleImportDefaults} disabled={busy}
                className="bg-[#8a6d1c] text-white px-3 py-2 rounded-xl font-bold text-[11px] disabled:opacity-50">
                {busy ? 'A criar…' : 'Importar conquistas predefinidas'}
              </button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {list.map((a) => (
            <div key={a.id} className={`${card} p-4 ${a.active === false ? 'opacity-60' : ''}`}>
              <div className="flex items-start gap-3">
                <span className="text-3xl flex-shrink-0">{a.emoji || '🏆'}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#111828] text-sm">{a.name}</p>
                  <p className="text-xs text-gray-500">{a.description}</p>
                  <p className="text-[11px] text-[#714c62] font-semibold mt-1">
                    {a.metric === 'premium' ? 'Ser membro Premium' : `${metricLabel(a.metric)} ≥ ${a.threshold}`}
                  </p>
                </div>
                {a.active === false && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#f7f3f5] text-gray-500">Oculta</span>}
              </div>
              {!usingDefaults && (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => openModal('achievement', 'edit', a)}
                    className={`flex-1 px-3 py-2.5 text-xs ${btnGhost} flex items-center justify-center gap-1.5`}>
                    <Edit2 className="w-3.5 h-3.5" /> Editar
                  </button>
                  <button onClick={() => handleToggleActive(a)}
                    className={`flex-1 px-3 py-2.5 text-xs ${btnSoft} flex items-center justify-center gap-1.5`}>
                    {a.active === false ? <><CheckCircle className="w-3.5 h-3.5" /> Mostrar</> : <><XCircle className="w-3.5 h-3.5" /> Ocultar</>}
                  </button>
                  <button onClick={() => handleDelete(a)}
                    className="w-10 rounded-2xl bg-[#faeaea] flex items-center justify-center hover:bg-[#f5dcdc] transition">
                    <Trash2 className="w-4 h-4 text-[#8d3b3b]" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        {!usingDefaults && <Fab onClick={() => openModal('achievement', 'create')} />}
      </div>
    );
  };

  // ---------- ATIVIDADE A DECORRER: DETALHE ----------
  const ActiveActivityDetail = ({ activity, onBack }) => {
    const [busy, setBusy] = useState(false);
    const [endDate, setEndDate] = useState(activity.plannedEndAt ? activity.plannedEndAt.slice(0, 16) : '');

    const regs = (allRegistrations || []).filter((r) => r.activityId === activity.id);
    const approved = regs.filter((r) => r.status === 'approved' || r.completed);
    const finished = approved.filter((r) => r.completed === true);
    const unfinished = approved.filter((r) => r.completed !== true);
    const fmt = (d) => (d ? new Date(d).toLocaleString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

    const handleSaveEndDate = async () => {
      setBusy(true);
      try {
        await setActivityEndDate(activity.id, endDate ? new Date(endDate).toISOString() : null);
        await loadData();
        alert('✅ Data de fim guardada!');
      } catch (e) {
        console.error('Erro ao guardar data de fim:', e);
        alert('❌ Erro: ' + (e?.message || e));
      } finally { setBusy(false); }
    };

    const handleEnd = async () => {
      if (!window.confirm(`Terminar "${activity.name}"?\n\n${finished.length} participante(s) concluíram e ${unfinished.length} não concluíram.\n\nA atividade passa para as Arquivadas e deixa de aparecer na lista de atividades.`)) return;
      setBusy(true);
      try {
        await endActivity(activity.id);
        await loadData();
        onBack();
        alert('✅ Atividade terminada e arquivada.');
      } catch (e) {
        console.error('Erro ao terminar atividade:', e);
        alert('❌ Erro: ' + (e?.message || e));
      } finally { setBusy(false); }
    };

    const Row = ({ r, done }) => (
      <div className="flex items-center gap-3 bg-[#f7f3f5] rounded-2xl p-3">
        <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-extrabold ${done ? 'bg-[#3d7a52]' : 'bg-[#c3bcc9]'}`}>
          {(r.userName || 'U').charAt(0).toUpperCase()}
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-[#111828] text-sm truncate">{r.userName}</p>
          <p className="text-[11px] text-gray-500 break-all">{r.userEmail}</p>
        </div>
        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${done ? 'bg-[#e6f2ea] text-[#3d7a52]' : 'bg-[#fdf2e3] text-[#b45309]'}`}>
          {done ? 'Concluiu' : 'Por concluir'}
        </span>
      </div>
    );

    return (
      <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
        <PageHeader eyebrow={activity.archived ? 'Arquivada' : 'A decorrer'} title={activity.name} onBack={onBack} />

        <div className={`${card} p-5 mb-3`}>
          <div className="space-y-2 text-sm">
            <p className="flex items-center gap-2 text-gray-600">Código: <span className="font-mono font-bold text-[#111828] bg-[#f2eef1] px-2 py-0.5 rounded-full">{activityCode(activity)}</span></p>
            {activity.zone && <p className="flex items-center gap-2 text-[#111828]"><MapPin className="w-4 h-4 text-[#714c62]" /> {activity.zone}</p>}
            <p className="flex items-center gap-2 text-gray-600"><UserCheck className="w-4 h-4 text-[#714c62]" /> Responsável: <span className="font-bold text-[#111828]">{activity.responsibleAdminName || 'Não atribuído'}</span></p>
            <p className="flex items-center gap-2 text-gray-600"><Clock className="w-4 h-4 text-[#714c62]" /> Início: <span className="font-bold text-[#111828]">{fmt(activity.startedAt)}</span></p>
            {activity.plannedEndAt && <p className="flex items-center gap-2 text-gray-600"><Clock className="w-4 h-4 text-[#714c62]" /> Fim previsto: <span className="font-bold text-[#111828]">{fmt(activity.plannedEndAt)}</span></p>}
            {activity.endedAt && <p className="flex items-center gap-2 text-gray-600"><CheckCircle className="w-4 h-4 text-[#3d7a52]" /> Terminada: <span className="font-bold text-[#111828]">{fmt(activity.endedAt)}</span></p>}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2.5 mb-3">
          <div className={`${card} p-3 text-center`}>
            <p className="text-xl font-extrabold text-[#111828]">{approved.length}</p>
            <p className="text-[11px] text-gray-500 font-semibold">Participantes</p>
          </div>
          <div className={`${card} p-3 text-center`}>
            <p className="text-xl font-extrabold text-[#3d7a52]">{finished.length}</p>
            <p className="text-[11px] text-gray-500 font-semibold">Concluíram</p>
          </div>
          <div className={`${card} p-3 text-center`}>
            <p className="text-xl font-extrabold text-[#b45309]">{unfinished.length}</p>
            <p className="text-[11px] text-gray-500 font-semibold">Não concluíram</p>
          </div>
        </div>

        {!activity.archived && (
          <div className={`${card} p-5 mb-3`}>
            <label className="block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5">Data e hora de fim</label>
            <input type="datetime-local" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputCls} />
            <button onClick={handleSaveEndDate} disabled={busy} className={`w-full mt-2.5 px-4 py-2.5 text-xs ${btnSoft} flex items-center justify-center gap-1.5`}>
              <Clock className="w-3.5 h-3.5" /> Guardar data de fim
            </button>
          </div>
        )}

        <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#9b8b94] mb-2">Participantes ({approved.length})</p>
        {approved.length === 0 ? (
          <div className={`${card} p-6 text-center text-gray-400 mb-4`}>
            <Users className="w-10 h-10 mx-auto mb-2 text-gray-200" />
            <p className="text-sm">Ninguém inscrito nesta atividade.</p>
          </div>
        ) : (
          <div className="space-y-2 mb-4">
            {finished.map((r) => <Row key={r.id} r={r} done />)}
            {unfinished.map((r) => <Row key={r.id} r={r} done={false} />)}
          </div>
        )}

        {!activity.archived && (
          <button onClick={handleEnd} disabled={busy}
            className="w-full px-4 py-3.5 text-sm font-bold rounded-2xl bg-[#8d3b3b] text-white shadow-[0_5px_14px_rgba(141,59,59,0.3)] hover:bg-[#7a3232] transition disabled:opacity-50 flex items-center justify-center gap-2">
            <XCircle className="w-4 h-4" /> {busy ? 'A processar…' : 'Terminar e arquivar atividade'}
          </button>
        )}
      </div>
    );
  };

  // ---------- ATIVIDADES ATIVAS ----------
  const ActiveActivitiesPage = () => {
    if (selectedActiveActivity) {
      const fresh = activities.find((a) => a.id === selectedActiveActivity.id) || selectedActiveActivity;
      return <ActiveActivityDetail activity={fresh} onBack={() => setSelectedActiveActivity(null)} />;
    }
    const ongoing = activities.filter((a) => a.ongoing && !a.archived);
    return (
      <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
        <PageHeader eyebrow="Gestão" title="Atividades ativas" onBack={() => setCurrentAdminPage('more')}
          right={<span className="text-xs font-bold text-[#3d7a52] bg-[#e6f2ea] px-3 py-1.5 rounded-full">{ongoing.length}</span>} />
        {ongoing.length === 0 ? (
          <div className={`${card} p-8 text-center text-gray-400`}>
            <Activity className="w-10 h-10 mx-auto mb-2 text-gray-200" />
            <p className="text-sm">Nenhuma atividade a decorrer.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {ongoing.map((a) => <ActivityRowCard key={a.id} a={a} onOpen={() => setSelectedActiveActivity(a)} />)}
          </div>
        )}
      </div>
    );
  };

  // Cartão reutilizável para listas de atividades
  const ActivityRowCard = ({ a, onOpen, muted }) => {
    const regs = (allRegistrations || []).filter((r) => r.activityId === a.id && (r.status === 'approved' || r.completed));
    const done = regs.filter((r) => r.completed === true).length;
    return (
      <button onClick={onOpen} className={`${card} p-4 w-full text-left flex items-center gap-3 hover:shadow-lg transition ${muted ? 'opacity-75' : ''}`}>
        {a.image && <img src={a.image} alt={a.name} className="w-14 h-14 rounded-2xl object-cover flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-[#111828] text-sm truncate">{a.name}</p>
          <span className="text-[10px] font-mono font-bold text-gray-500">{activityCode(a)}</span>
          {a.zone && <p className="text-[11px] text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {a.zone}</p>}
          <p className="text-[11px] text-[#714c62] font-semibold">{regs.length} participantes · {done} concluíram</p>
        </div>
        <span className="text-xs font-extrabold text-[#714c62] whitespace-nowrap">Ver →</span>
      </button>
    );
  };

  // ---------- MINHA RESPONSABILIDADE (inscrições + atividades) ----------
  const MyActivitiesPage = () => {
    // Detalhes ocupam a página toda
    if (selectedActivityForRegs || selectedRegistration) {
      return <RegistrationsPage restrictToMine />;
    }
    if (selectedActiveActivity) {
      const fresh = activities.find((a) => a.id === selectedActiveActivity.id) || selectedActiveActivity;
      return <ActiveActivityDetail activity={fresh} onBack={() => setSelectedActiveActivity(null)} />;
    }

    const myId = auth.currentUser?.uid;
    const mine = activities.filter((a) => a.responsibleAdminId === myId);
    const ongoing = mine.filter((a) => a.ongoing && !a.archived);
    const upcoming = mine.filter((a) => !a.ongoing && !a.archived);
    const archived = mine.filter((a) => a.archived);
    const myPending = (allRegistrations || []).filter(
      (r) => r.status === 'pending' && mine.some((a) => a.id === r.activityId)
    ).length;

    const Section = ({ title, list, muted }) => list.length > 0 && (
      <>
        <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#9b8b94] mb-2 mt-4">{title} ({list.length})</p>
        <div className="space-y-3">
          {list.map((a) => <ActivityRowCard key={a.id} a={a} muted={muted} onOpen={() => setSelectedActiveActivity(a)} />)}
        </div>
      </>
    );

    return (
      <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
        <PageHeader eyebrow="Gestão" title="Minha responsabilidade"
          right={<span className="text-xs font-bold text-[#714c62] bg-[#f1e9ee] px-3 py-1.5 rounded-full">{mine.length}</span>} />

        {/* Escolha de vista */}
        <div className="grid grid-cols-2 gap-2.5 mb-5">
          <button onClick={() => setMyTab('registrations')}
            className={`px-4 py-3 rounded-2xl font-bold text-sm flex flex-col items-center gap-0.5 transition ${myTab === 'registrations' ? 'bg-[#111828] text-white shadow-[0_6px_16px_rgba(17,24,40,0.25)]' : 'bg-white text-gray-500 shadow-[0_2px_8px_rgba(17,24,40,0.05)]'}`}>
            <span className="flex items-center gap-1.5"><UserCheck className="w-4 h-4" /> Inscrições</span>
            <span className={`text-[10px] font-semibold ${myTab === 'registrations' ? 'text-[#b3a5ad]' : 'text-gray-400'}`}>
              {myPending > 0 ? `${myPending} por aprovar` : 'nenhuma pendente'}
            </span>
          </button>
          <button onClick={() => setMyTab('activities')}
            className={`px-4 py-3 rounded-2xl font-bold text-sm flex flex-col items-center gap-0.5 transition ${myTab === 'activities' ? 'bg-[#111828] text-white shadow-[0_6px_16px_rgba(17,24,40,0.25)]' : 'bg-white text-gray-500 shadow-[0_2px_8px_rgba(17,24,40,0.05)]'}`}>
            <span className="flex items-center gap-1.5"><Activity className="w-4 h-4" /> Atividades</span>
            <span className={`text-[10px] font-semibold ${myTab === 'activities' ? 'text-[#b3a5ad]' : 'text-gray-400'}`}>
              {ongoing.length > 0 ? `${ongoing.length} a decorrer` : `${mine.length} no total`}
            </span>
          </button>
        </div>

        {myTab === 'registrations' ? (
          <RegistrationsPage restrictToMine hideHeader />
        ) : mine.length === 0 ? (
          <div className={`${card} p-8 text-center text-gray-400`}>
            <UserCheck className="w-10 h-10 mx-auto mb-2 text-gray-200" />
            <p className="text-sm">Não és responsável por nenhuma atividade.</p>
            <p className="text-xs text-gray-300 mt-1">Define o admin responsável ao criar ou editar uma atividade.</p>
          </div>
        ) : (
          <>
            <Section title="A decorrer" list={ongoing} />
            <Section title="Por começar" list={upcoming} />
            <Section title="Arquivadas" list={archived} muted />
          </>
        )}
      </div>
    );
  };

  // ---------- ARQUIVADAS ----------
  const ArchivedActivitiesPage = () => {
    if (selectedActiveActivity) {
      const fresh = activities.find((a) => a.id === selectedActiveActivity.id) || selectedActiveActivity;
      return <ActiveActivityDetail activity={fresh} onBack={() => setSelectedActiveActivity(null)} />;
    }
    const archived = activities.filter((a) => a.archived);
    const handleUnarchive = async (a) => {
      if (!window.confirm(`Repor "${a.name}" na lista de atividades?`)) return;
      try {
        await unarchiveActivity(a.id);
        await loadData();
      } catch (e) {
        console.error('Erro ao repor atividade:', e);
        alert('❌ Erro: ' + (e?.message || e));
      }
    };
    return (
      <div className="pt-6 pb-32 px-4 max-w-lg mx-auto">
        <PageHeader eyebrow="Histórico" title="Arquivadas" onBack={() => setCurrentAdminPage('more')}
          right={<span className="text-xs font-bold text-gray-500 bg-[#f2eef1] px-3 py-1.5 rounded-full">{archived.length}</span>} />
        {archived.length === 0 ? (
          <div className={`${card} p-8 text-center text-gray-400`}>
            <ClipboardList className="w-10 h-10 mx-auto mb-2 text-gray-200" />
            <p className="text-sm">Nenhuma atividade arquivada.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {archived.map((a) => (
              <div key={a.id}>
                <ActivityRowCard a={a} muted onOpen={() => setSelectedActiveActivity(a)} />
                <button onClick={() => handleUnarchive(a)} className="text-[11px] font-bold text-[#714c62] mt-1 ml-1 underline">
                  Repor na lista de atividades
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderAdminPage = () => {
    switch (currentAdminPage) {
      case 'dashboard': return <DashboardPage />;
      case 'registrations': return <RegistrationsPage />;
      case 'users': return <UsersPage />;
      case 'news': return <NewsPage />;
      case 'offers': return <OffersPage />;
      case 'activities': return <ActivitiesPage />;
      case 'premium': return <AdminPremiumPage />;
      case 'rewards': return <AdminRewardsPage />;
      case 'redemptions': return <RedemptionsPage />;
      case 'achievements': return <AchievementsAdminPage />;
      case 'active': return <ActiveActivitiesPage />;
      case 'mine': return <MyActivitiesPage />;
      case 'archived': return <ArchivedActivitiesPage />;
      case 'more': return <MorePage />;
      default: return <DashboardPage />;
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f3f5]" style={{ fontFamily: 'inherit' }}>
      {renderAdminPage()}
      <Footer onNavigate={handleFooterNavigate} isAdmin={userData?.isAdmin === true} />
      <AdminNav />
      {showModal && <AdminModal type={modalType} mode={modalMode} item={editingItem} closeModal={closeModal} loadData={loadData} categories={categories} admins={(allUsers || []).filter((u) => u.isAdmin)} />}
    </div>
  );
};

// ============================================
// ADMIN MODAL (criar/editar notícia, oferta, atividade, recompensa)
// ============================================

const AdminModal = ({ type, mode, item, closeModal, loadData, categories, admins = [] }) => {
  const [formData, setFormData] = useState(
    item ? {
      ...item,
      hasDistance: !!(item.distance && item.distance.trim() !== ''),
      tasks: (item.tasks || []).map((t) => ({ ...t, routeText: t.routeText !== undefined ? t.routeText : routeToText(t.route) })),
    } : {}
  );
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(item?.image || null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const addTask = () => setFormData({ ...formData, tasks: [...(formData.tasks || []), { id: Date.now().toString(), order: (formData.tasks?.length || 0) + 1, name: '', description: '', routeText: '' }] });
  const removeTask = (taskId) => setFormData({ ...formData, tasks: (formData.tasks || []).filter((t) => t.id !== taskId) });
  const updateTaskField = (taskId, field, value) => setFormData({ ...formData, tasks: (formData.tasks || []).map((t) => (t.id === taskId ? { ...t, [field]: value } : t)) });

  const inputCls = 'w-full px-4 py-3 rounded-2xl border border-[#e8e2e6] bg-white focus:ring-2 focus:ring-[#714c62] focus:outline-none';
  const labelCls = 'block text-xs font-bold text-[#9b8b94] uppercase tracking-wide mb-1.5';

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) { alert('⚠️ Por favor, selecione apenas ficheiros de imagem!'); return; }
      if (file.size > 10 * 1024 * 1024) { alert('⚠️ A imagem é muito grande! Máximo 10MB.'); return; }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let finalData = { ...formData };
      if (imageFile) {
        setUploadingImage(true);
        const photoUrl = await uploadActivityPhoto(imageFile);
        finalData.image = photoUrl;
        setUploadingImage(false);
      }
      if (type === 'news') {
        if (mode === 'create') await addNews(finalData); else await updateNews(item.id, finalData);
      } else if (type === 'offer') {
        if (mode === 'create') await addOffer(finalData); else await updateOffer(item.id, finalData);
      } else if (type === 'activity') {
        const cleanTasks = (finalData.tasks || []).map((t, i) => ({
          id: t.id || `${Date.now()}-${i}`,
          order: i + 1,
          name: t.name || '',
          description: t.description || '',
          routeText: t.routeText !== undefined ? t.routeText : routeToText(t.route),
          route: t.routeText !== undefined ? parseRoute(t.routeText) : (t.route || []),
        }));
        const activityData = {
          ...finalData,
          tasks: cleanTasks,
          lat: (finalData.lat !== undefined && finalData.lat !== '') ? parseFloat(finalData.lat) : null,
          lng: (finalData.lng !== undefined && finalData.lng !== '') ? parseFloat(finalData.lng) : null,
          availableSlots: finalData.availableSlots !== undefined ? finalData.availableSlots : 20,
        };
        if (mode === 'create') await addActivity(activityData); else await updateActivity(item.id, activityData);
      } else if (type === 'reward') {
        if (mode === 'create') await addReward(finalData); else await updateReward(item.id, finalData);
      } else if (type === 'achievement') {
        const achData = {
          emoji: finalData.emoji || '🏆',
          name: finalData.name || '',
          description: finalData.description || '',
          metric: finalData.metric || 'activities',
          threshold: parseInt(finalData.threshold) || 0,
          order: parseInt(finalData.order) || 0,
          active: finalData.active !== false,
        };
        if (!achData.name) { alert('⚠️ Indique o nome da conquista!'); setSaving(false); return; }
        if (mode === 'create') await addAchievement(achData); else await updateAchievement(item.id, achData);
      }
      alert('✅ Guardado com sucesso!');
      closeModal();
      // Atualizar os dados em segundo plano — não bloqueia o fecho do modal
      loadData().catch((e) => console.error('Erro ao atualizar dados:', e));
    } catch (error) {
      console.error('Erro ao guardar:', error);
      alert('❌ Erro ao guardar!\n\n' + (error?.code ? `${error.code}\n` : '') + (error?.message || error));
    }
    setSaving(false);
  };

  const renderFormFields = () => {
    if (type === 'news') {
      return (
        <>
          <div className="mb-4">
            <label className={labelCls}>Título</label>
            <input type="text" value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className={inputCls} placeholder="Título da notícia" />
          </div>
          <div className="mb-4">
            <label className={labelCls}>Descrição</label>
            <textarea value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className={`${inputCls} h-24`} placeholder="Descrição da notícia" />
          </div>
          <div className="mb-4">
            <label className={labelCls}>Imagem</label>
            <input type="file" accept="image/*" onChange={handleImageChange} className={inputCls} />
            <p className="text-xs text-gray-400 mt-1">JPG, PNG ou WEBP (máx 10MB)</p>
            {imagePreview && <img src={imagePreview} alt="Preview" className="w-full h-40 object-cover mt-2 rounded-2xl" />}
          </div>
          <div className="mb-4">
            <label className={labelCls}>Data</label>
            <input type="text" value={formData.date || new Date().toLocaleDateString('pt-PT')}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className={inputCls} placeholder="DD/MM/YYYY" />
          </div>
        </>
      );
    } else if (type === 'offer') {
      return (
        <>
          <div className="mb-4">
            <label className={labelCls}>Título</label>
            <input type="text" value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className={inputCls} placeholder="Título da oferta" />
          </div>
          <div className="mb-4">
            <label className={labelCls}>Pontos necessários</label>
            <input type="number" value={formData.points || ''} onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })}
              className={inputCls} placeholder="100" />
          </div>
          <div className="mb-4">
            <label className={labelCls}>Manual de instruções (mostrado ao resgatar)</label>
            <textarea value={formData.instructions || ''} onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              className={`${inputCls} h-24`} placeholder="Como usar o voucher, validade, onde apresentar, etc." />
            <p className="text-xs text-gray-400 mt-1">Se ficar vazio, é mostrado um texto genérico.</p>
          </div>
          <div className="mb-4">
            <label className={labelCls}>Imagem</label>
            <input type="file" accept="image/*" onChange={handleImageChange} className={inputCls} />
            <p className="text-xs text-gray-400 mt-1">JPG, PNG ou WEBP (máx 10MB)</p>
            {imagePreview && <img src={imagePreview} alt="Preview" className="w-full h-40 object-cover mt-2 rounded-2xl" />}
          </div>
          <div className="mb-4">
            <label className={labelCls}>Parceiro (opcional)</label>
            <input type="text" value={formData.partnerName || ''} onChange={(e) => setFormData({ ...formData, partnerName: e.target.value })}
              className={inputCls} placeholder="Ex: EcoLoja Verde" />
            <select value={formData.partnerType || ''} onChange={(e) => setFormData({ ...formData, partnerType: e.target.value })} className={`${inputCls} mt-2`}>
              <option value="">Tipo de parceiro…</option>
              <option value="Empresa">Empresa</option>
              <option value="Município">Município</option>
              <option value="Organização">Organização ambiental</option>
            </select>
          </div>
        </>
      );
    } else if (type === 'activity') {
      return (
        <>
          <div className="mb-4">
            <label className={labelCls}>Nome</label>
            <input type="text" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={inputCls} placeholder="Nome da atividade" />
          </div>
          <div className="mb-4">
            <label className={labelCls}>Categoria</label>
            <select value={formData.categoryId || ''} onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })} className={inputCls}>
              <option value="">Selecione uma categoria</option>
              {categories.map((cat) => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
            </select>
          </div>
          <div className="mb-4">
            <label className={labelCls}>Zona</label>
            <input type="text" value={formData.zone || ''} onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
              className={inputCls} placeholder="Ex: Serra de Sintra" />
            <p className="text-xs text-gray-400 mt-1">Aparece na lista de atividades, por baixo do nome.</p>
          </div>
          <div className="mb-4">
            <label className={labelCls}>Admin responsável</label>
            <select
              value={formData.responsibleAdminId || ''}
              onChange={(e) => {
                const adm = admins.find((a) => a.id === e.target.value);
                setFormData({ ...formData, responsibleAdminId: e.target.value || null, responsibleAdminName: adm?.name || null });
              }}
              className={inputCls}
            >
              <option value="">Sem responsável atribuído</option>
              {admins.map((a) => (<option key={a.id} value={a.id}>{a.name || a.email}</option>))}
            </select>
            <p className="text-xs text-gray-400 mt-1">A atividade aparece em "Minha responsabilidade" desse admin.</p>
          </div>
          <div className="mb-4">
            <label className={labelCls}>Coordenadas exatas (opcional)</label>
            <div className="flex gap-2">
              <input type="number" step="any" value={formData.lat ?? ''} onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                className={inputCls} placeholder="Latitude (ex: 38.7997)" />
              <input type="number" step="any" value={formData.lng ?? ''} onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                className={inputCls} placeholder="Longitude (ex: -9.3906)" />
            </div>
            <p className="text-xs text-gray-400 mt-1">Mostradas nos detalhes, com botão para abrir no mapa.</p>
          </div>
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input type="checkbox" checked={formData.hasDistance || false}
                onChange={(e) => setFormData({ ...formData, hasDistance: e.target.checked, distance: e.target.checked ? (formData.distance || '') : '' })}
                className="w-4 h-4 accent-[#714c62]" />
              <span className="text-sm font-semibold text-[#111828]">Tem distância?</span>
            </label>
            {formData.hasDistance && (
              <input type="text" value={formData.distance || ''} onChange={(e) => setFormData({ ...formData, distance: e.target.value })}
                className={inputCls} placeholder="Ex: 5 km" />
            )}
          </div>
          <div className="mb-4">
            <label className={labelCls}>Dificuldade</label>
            <select value={formData.difficulty || ''} onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })} className={inputCls}>
              <option value="">Selecione</option>
              <option value="Fácil">Fácil</option>
              <option value="Médio">Médio</option>
              <option value="Difícil">Difícil</option>
            </select>
          </div>
          <div className="mb-4">
            <label className={labelCls}>Pontos</label>
            <input type="number" value={formData.points || ''} onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })}
              className={inputCls} placeholder="50" />
          </div>
          <div className="mb-4">
            <label className={labelCls}>Vagas disponíveis</label>
            <input type="number" value={formData.availableSlots !== undefined ? formData.availableSlots : 20}
              onChange={(e) => setFormData({ ...formData, availableSlots: parseInt(e.target.value) || 0 })}
              className={inputCls} placeholder="20" min="0" />
            <p className="text-xs text-gray-400 mt-1">Valor padrão: 20 vagas</p>
          </div>
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.requiresPhoto || false}
                onChange={(e) => setFormData({ ...formData, requiresPhoto: e.target.checked })}
                className="w-4 h-4 accent-[#714c62]" />
              <span className="text-sm font-semibold text-[#111828]">Requer foto para conclusão</span>
            </label>
            <p className="text-xs text-gray-400 mt-1 ml-6">Se marcado, o utilizador deve enviar uma foto que o admin aprovará antes de concluir a atividade</p>
          </div>
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.certified || false}
                onChange={(e) => setFormData({ ...formData, certified: e.target.checked })}
                className="w-4 h-4 accent-[#3d7a52]" />
              <span className="text-sm font-semibold text-[#111828]">Trilho / atividade certificada</span>
            </label>
            <p className="text-xs text-gray-400 mt-1 ml-6">Mostra um selo de certificação ecológica.</p>
          </div>
          <div className="mb-4">
            <label className={labelCls}>Parceiro (opcional)</label>
            <input type="text" value={formData.partnerName || ''} onChange={(e) => setFormData({ ...formData, partnerName: e.target.value })}
              className={inputCls} placeholder="Ex: Câmara Municipal de Sintra" />
            <select value={formData.partnerType || ''} onChange={(e) => setFormData({ ...formData, partnerType: e.target.value })} className={`${inputCls} mt-2`}>
              <option value="">Tipo de parceiro…</option>
              <option value="Empresa">Empresa</option>
              <option value="Município">Município</option>
              <option value="Organização">Organização ambiental</option>
            </select>
          </div>
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className={labelCls} style={{ margin: 0 }}>Tarefas da atividade</label>
              <button type="button" onClick={addTask} className="px-3 py-1.5 text-xs bg-[#f1e9ee] text-[#714c62] font-bold rounded-xl flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> Adicionar
              </button>
            </div>
            {(formData.tasks || []).length === 0 && <p className="text-xs text-gray-400 py-2">Sem tarefas. As tarefas aparecem ao utilizador quando a atividade estiver a decorrer.</p>}
            <div className="space-y-3">
              {(formData.tasks || []).map((task, idx) => (
                <div key={task.id} className="bg-[#f7f3f5] rounded-2xl p-3.5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-extrabold text-[#714c62]">Tarefa {idx + 1}</span>
                    <button type="button" onClick={() => removeTask(task.id)} className="text-[#c3bcc9] hover:text-[#8d3b3b]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <input type="text" placeholder="Nome da tarefa" value={task.name || ''} onChange={(e) => updateTaskField(task.id, 'name', e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl border border-[#e8e2e6] bg-white text-sm mb-2 focus:ring-2 focus:ring-[#714c62] focus:outline-none" />
                  <input type="text" placeholder="Descrição" value={task.description || ''} onChange={(e) => updateTaskField(task.id, 'description', e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl border border-[#e8e2e6] bg-white text-sm mb-2 focus:ring-2 focus:ring-[#714c62] focus:outline-none" />
                  <label className="block text-[11px] font-bold text-[#9b8b94] mb-1">Rota no mapa (uma coordenada por linha: lat, lng)</label>
                  <textarea placeholder={"38.7997, -9.3906\n38.8010, -9.3890"} value={task.routeText || ''} onChange={(e) => updateTaskField(task.id, 'routeText', e.target.value)}
                    className="w-full px-3.5 py-2 rounded-xl border border-[#e8e2e6] bg-white text-sm h-20 font-mono focus:ring-2 focus:ring-[#714c62] focus:outline-none" />
                  <p className="text-[10px] text-gray-400 mt-1">2+ pontos desenham o trilho; 1 ponto marca o local. Vazio = tarefa sem mapa.</p>
                </div>
              ))}
            </div>
          </div>
          <div className="mb-4">
            <label className={labelCls}>Imagem</label>
            <input type="file" accept="image/*" onChange={handleImageChange} className={inputCls} />
            <p className="text-xs text-gray-400 mt-1">JPG, PNG ou WEBP (máx 10MB)</p>
            {imagePreview && <img src={imagePreview} alt="Preview" className="w-full h-40 object-cover mt-2 rounded-2xl" />}
          </div>
        </>
      );
    } else if (type === 'reward') {
      return (
        <>
          <div className="mb-4">
            <label className={labelCls}>Nome</label>
            <input type="text" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={inputCls} placeholder="Nome da recompensa" />
          </div>
          <div className="mb-4">
            <label className={labelCls}>Descrição</label>
            <textarea value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className={`${inputCls} h-24`} placeholder="Descrição da recompensa" />
          </div>
          <div className="mb-4">
            <label className={labelCls}>Tipo</label>
            <select value={formData.rewardType || 'produto'} onChange={(e) => setFormData({ ...formData, rewardType: e.target.value })} className={inputCls}>
              <option value="produto">Produto</option>
              <option value="experiencia">Experiência exclusiva</option>
            </select>
          </div>
          {/* fim recompensa */}
          <div className="mb-4">
            <label className={labelCls}>Imagem</label>
            <input type="file" accept="image/*" onChange={handleImageChange} className={inputCls} />
            <p className="text-xs text-gray-400 mt-1">JPG, PNG ou WEBP (máx 10MB)</p>
            {imagePreview && <img src={imagePreview} alt="Preview" className="w-full h-40 object-cover mt-2 rounded-2xl" />}
          </div>
        </>
      );
    } else if (type === 'achievement') {
      const isPremiumMetric = (formData.metric || 'activities') === 'premium';
      return (
        <>
          <div className="mb-4">
            <label className={labelCls}>Ícone (emoji)</label>
            <input type="text" value={formData.emoji || ''} onChange={(e) => setFormData({ ...formData, emoji: e.target.value })}
              className={`${inputCls} text-2xl text-center`} placeholder="🏆" maxLength={4} />
            <p className="text-xs text-gray-400 mt-1">Um emoji que represente a conquista.</p>
          </div>
          <div className="mb-4">
            <label className={labelCls}>Nome</label>
            <input type="text" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={inputCls} placeholder="Ex: Explorador" />
          </div>
          <div className="mb-4">
            <label className={labelCls}>Descrição</label>
            <input type="text" value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className={inputCls} placeholder="Ex: Completa 5 atividades" />
          </div>
          <div className="mb-4">
            <label className={labelCls}>Critério para desbloquear</label>
            <select value={formData.metric || 'activities'} onChange={(e) => setFormData({ ...formData, metric: e.target.value })} className={inputCls}>
              {ACHIEVEMENT_METRICS.map((m) => (<option key={m.id} value={m.id}>{m.label}</option>))}
            </select>
          </div>
          {!isPremiumMetric && (
            <div className="mb-4">
              <label className={labelCls}>Valor mínimo</label>
              <input type="number" min="0" value={formData.threshold ?? ''} onChange={(e) => setFormData({ ...formData, threshold: e.target.value })}
                className={inputCls} placeholder="5" />
              <p className="text-xs text-gray-400 mt-1">Desbloqueia quando o utilizador atinge este valor ou mais.</p>
            </div>
          )}
          <div className="mb-4">
            <label className={labelCls}>Ordem</label>
            <input type="number" min="0" value={formData.order ?? ''} onChange={(e) => setFormData({ ...formData, order: e.target.value })}
              className={inputCls} placeholder="1" />
            <p className="text-xs text-gray-400 mt-1">Define a posição na lista (menor aparece primeiro).</p>
          </div>
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.active !== false}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4 accent-[#714c62]" />
              <span className="text-sm font-semibold text-[#111828]">Visível para os utilizadores</span>
            </label>
          </div>
        </>
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-[#111828]/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[28px] max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-extrabold text-[#111828]">
            {mode === 'create' ? 'Criar' : 'Editar'} {
              type === 'news' ? 'notícia' :
                type === 'offer' ? 'oferta' :
                  type === 'activity' ? 'atividade' :
                    type === 'reward' ? 'recompensa' :
                      type === 'achievement' ? 'conquista' : ''
            }
          </h2>
          <button onClick={closeModal} className="w-9 h-9 rounded-full bg-[#f7f3f5] flex items-center justify-center text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {renderFormFields()}

        <div className="flex gap-2.5 mt-6">
          <button onClick={closeModal}
            className="flex-1 px-4 py-3 text-sm bg-[#f7f3f5] text-[#111828] font-bold rounded-2xl hover:bg-[#efe9ec] transition">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving || uploadingImage}
            className="flex-[1.4] px-4 py-3 text-sm bg-[#714c62] text-white font-bold rounded-2xl shadow-[0_5px_14px_rgba(113,76,98,0.35)] hover:bg-[#5c3d50] transition disabled:opacity-50">
            {uploadingImage ? 'A enviar imagem…' : saving ? 'A guardar…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GreenStepApp;
